// src/common/time.ts
var MS_PER_DAY = 864e5;

// src/calendar/calendar-day.ts
var DEFAULT_BUSINESS_TIME_ZONE = "Asia/Beirut";
function toCalendarDay(date, timeZone = DEFAULT_BUSINESS_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat("en-US", { day: "numeric", month: "numeric", timeZone, year: "numeric" }).formatToParts(new Date(date)).reduce((acc, part) => ({ ...acc, [part.type]: Number(part.value) }), {});
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
}
function addMonthsUtc(date, months) {
  const source = new Date(date);
  const target = new Date(source);
  target.setUTCDate(1);
  target.setUTCMonth(target.getUTCMonth() + months);
  const lastDayOfTarget = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(source.getUTCDate(), lastDayOfTarget));
  return target;
}
function addDays(date, days) {
  return new Date(new Date(date).getTime() + days * MS_PER_DAY);
}
function resolveBusinessTimeZone() {
  return DEFAULT_BUSINESS_TIME_ZONE;
}

// src/capacity/capacity.interface.ts
var VOLUME_UNITS = { L: "L", M3: "m3" };
var WEIGHT_UNITS = { KG: "kg", LB: "lb" };

// src/constants/documents.ts
var VEHICLE_DOCUMENT_MAX_FILE_SIZE = 10 * 1024 * 1024;
var VEHICLE_DOCUMENT_ACCEPTED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"];
var VEHICLE_DOCUMENT_ACCEPTED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];
var VEHICLE_DOCUMENT_MAX_FILES = 10;
var VEHICLE_IMAGE_MAX_FILE_SIZE = 5 * 1024 * 1024;
var VEHICLE_IMAGE_ACCEPTED_MIME_TYPES = ["image/jpeg", "image/png"];
var VEHICLE_IMAGE_ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png"];
var VEHICLE_IMAGE_MAX_FILES = 5;
var VEHICLE_DISPOSAL_MAX_DOCUMENTS = 1;
var VEHICLE_DISPOSAL_MAX_PHOTOS = 4;
var VEHICLE_EXPENSE_MAX_DOCUMENTS = 3;
var DOCUMENT_TEMPLATE_MAX_FILE_SIZE = 5 * 1024 * 1024;
var DOCUMENT_TEMPLATE_MIME_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
var DOCUMENT_TEMPLATE_ACCEPTED_MIME_TYPES = [DOCUMENT_TEMPLATE_MIME_TYPE];
var DOCUMENT_TEMPLATE_ACCEPTED_EXTENSIONS = [".docx"];
var DOCUMENT_TEMPLATE_MAX_UNCOMPRESSED_SIZE = 50 * 1024 * 1024;
var DOCUMENT_TEMPLATE_MAX_PLACEHOLDERS = 200;
var DOCUMENT_FIELD_VALUE_MAX_LENGTH = 500;
var GENERATED_DOCUMENT_MIME_TYPE = "application/pdf";
var DOCUMENT_FIELD_EMPTY_VALUE = "-";
var DOCUMENT_FIELD_FILL_IN_VALUE = ".....";
var SUPPLIER_DOCUMENT_ACCEPTED_EXTENSIONS = VEHICLE_DOCUMENT_ACCEPTED_EXTENSIONS;
var SUPPLIER_DOCUMENT_MAX_FILE_SIZE = VEHICLE_DOCUMENT_MAX_FILE_SIZE;
var SUPPLIER_DOCUMENT_MAX_FILES = 10;
var ISSUE_PHOTO_ACCEPTED_MIME_TYPES = VEHICLE_DOCUMENT_ACCEPTED_MIME_TYPES;
var ISSUE_PHOTO_ACCEPTED_EXTENSIONS = VEHICLE_DOCUMENT_ACCEPTED_EXTENSIONS;
var ISSUE_PHOTO_MAX_FILE_SIZE = VEHICLE_DOCUMENT_MAX_FILE_SIZE;
var ISSUE_PHOTO_MAX_FILES = 10;
var WORK_ORDER_DOCUMENT_ACCEPTED_MIME_TYPES = VEHICLE_DOCUMENT_ACCEPTED_MIME_TYPES;
var WORK_ORDER_DOCUMENT_ACCEPTED_EXTENSIONS = VEHICLE_DOCUMENT_ACCEPTED_EXTENSIONS;
var WORK_ORDER_DOCUMENT_MAX_FILE_SIZE = VEHICLE_DOCUMENT_MAX_FILE_SIZE;
var WORK_ORDER_DOCUMENT_MAX_FILES = 20;
var CONTRACT_QUOTATION_ACCEPTED_MIME_TYPES = ["application/pdf"];
var CONTRACT_QUOTATION_ACCEPTED_EXTENSIONS = [".pdf"];
var CONTRACT_QUOTATION_MAX_FILE_SIZE = VEHICLE_DOCUMENT_MAX_FILE_SIZE;
var RELIEF_SIGNED_NOTE_ACCEPTED_MIME_TYPES = CONTRACT_QUOTATION_ACCEPTED_MIME_TYPES;
var RELIEF_SIGNED_NOTE_ACCEPTED_EXTENSIONS = CONTRACT_QUOTATION_ACCEPTED_EXTENSIONS;
var RELIEF_SIGNED_NOTE_MAX_FILE_SIZE = CONTRACT_QUOTATION_MAX_FILE_SIZE;
var CONTRACT_VAT_CERTIFICATE_ACCEPTED_MIME_TYPES = VEHICLE_DOCUMENT_ACCEPTED_MIME_TYPES;
var CONTRACT_VAT_CERTIFICATE_ACCEPTED_EXTENSIONS = VEHICLE_DOCUMENT_ACCEPTED_EXTENSIONS;
var CONTRACT_VAT_CERTIFICATE_MAX_FILE_SIZE = VEHICLE_DOCUMENT_MAX_FILE_SIZE;
var WORK_ORDER_DAMAGE_PHOTO_ACCEPTED_MIME_TYPES = ["image/jpeg", "image/png"];
var WORK_ORDER_DAMAGE_PHOTO_ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png"];
var WORK_ORDER_DAMAGE_PHOTO_MAX_FILE_SIZE = ISSUE_PHOTO_MAX_FILE_SIZE;
var WORK_ORDER_DAMAGE_PHOTO_MAX_FILES = 10;

// src/constants/password.ts
var PASSWORD_MIN_LENGTH = 8;
var PASSWORD_MAX_LENGTH = 16;
var PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{8,16}$/;
var PASSWORD_LOWERCASE_REGEX = /[a-z]/;
var PASSWORD_UPPERCASE_REGEX = /[A-Z]/;
var PASSWORD_NUMBER_REGEX = /\d/;
var PASSWORD_SYMBOL_REGEX = /[^a-zA-Z\d]/;

// src/constants/time-of-day.ts
var TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// src/contract/agreement-dates.ts
function leaseEndDate(start, termMonths) {
  const monthIndex = start.getUTCMonth() + termMonths;
  const lastDayOfEndMonth = new Date(Date.UTC(start.getUTCFullYear(), monthIndex + 1, 0)).getUTCDate();
  const sameDay = Date.UTC(start.getUTCFullYear(), monthIndex, Math.min(start.getUTCDate(), lastDayOfEndMonth));
  return new Date(sameDay - 24 * 60 * 60 * 1e3);
}
function leaseTermMonths(start, lastDay) {
  const next = new Date(lastDay.getTime() + 24 * 60 * 60 * 1e3);
  const months = (next.getUTCFullYear() - start.getUTCFullYear()) * 12 + (next.getUTCMonth() - start.getUTCMonth());
  const lastDayOfNextMonth = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
  const partialMonth = next.getUTCDate() < Math.min(start.getUTCDate(), lastDayOfNextMonth);
  return Math.max(0, partialMonth ? months - 1 : months);
}
function agreementTermMonths(agreement, line) {
  return agreement.termMonths ?? line?.termMonths ?? null;
}

// src/contract/agreement-terms.ts
function agreementCurrentTerms(agreement, line) {
  const termMonths = agreementTermMonths(agreement, line);
  const endDate = agreement.endDate ?? (agreement.handoverDate !== null && termMonths !== null ? leaseEndDate(agreement.handoverDate, termMonths) : null);
  return {
    endDate,
    endDateSince: agreement.endDateSince,
    extraKmPrice: agreement.extraKmPrice,
    extraKmPriceSince: agreement.extraKmPriceSince,
    modified: agreement.termsModifiedAt !== null,
    monthlyGross: agreement.monthlyGross,
    monthlyNet: agreement.monthlyNet,
    monthlyPriceSince: agreement.monthlyPriceSince,
    signedMonthlyNet: agreementSignedMonthlyNet(agreement),
    termMonths: agreement.handoverDate !== null && endDate !== null ? leaseTermMonths(agreement.handoverDate, endDate) : termMonths
  };
}
function agreementSignedMonthlyNet(agreement) {
  return agreement.originalMonthlyNet ?? agreement.monthlyNet;
}

// src/contract/agreement.interface.ts
var AGREEMENT_HANDOVER_STATE = {
  DOWNLOADED: "DOWNLOADED",
  NOT_UPLOADED: "NOT_UPLOADED",
  UPLOADED: "UPLOADED"
};
function handoverDocumentFileName(agreementNumber) {
  return `${agreementNumber}-handover.pdf`;
}
function agreementAnnexFileName(agreementNumber) {
  return `${agreementNumber}-annex.pdf`;
}
function formatAgreementNumber(contractNumber, lineIndex, vehicleIndex) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${contractNumber}-${pad(lineIndex)}-${pad(vehicleIndex)}`;
}

// src/contract/contract-totals.ts
var roundToCents = (value) => Math.round(value * 100) / 100;
var CURRENCY_MINOR_UNITS = {
  EUR: 2,
  GBP: 2,
  LBP: 0,
  USD: 2
};
function roundToMinorUnit(value, currency) {
  const decimals = CURRENCY_MINOR_UNITS[currency];
  const text = String(value);
  if (text.includes("e")) {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }
  return Number(`${Math.round(Number(`${text}e${decimals}`))}e-${decimals}`);
}
function vatPerVehicle(net, vatRatePercent, vatExempt) {
  return vatExempt ? 0 : roundToCents(net * vatRatePercent / 100);
}
function computeContractTotals(lines, vatRatePercent, vatExempt) {
  const totals = { monthlyGross: 0, monthlyNet: 0, monthlyVat: 0, vehicleCount: 0 };
  for (const { invoicedMonthlyNet, quantity } of lines) {
    totals.vehicleCount += quantity;
    if (invoicedMonthlyNet === null) {
      continue;
    }
    const vat = vatPerVehicle(invoicedMonthlyNet, vatRatePercent, vatExempt);
    totals.monthlyNet += invoicedMonthlyNet * quantity;
    totals.monthlyVat += vat * quantity;
  }
  totals.monthlyNet = roundToCents(totals.monthlyNet);
  totals.monthlyVat = roundToCents(totals.monthlyVat);
  totals.monthlyGross = roundToCents(totals.monthlyNet + totals.monthlyVat);
  return totals;
}
function computeContractValue(lines, vatRatePercent, vatExempt) {
  let value = 0;
  for (const { invoicedMonthlyNet, quantity, termMonths } of lines) {
    if (invoicedMonthlyNet === null || termMonths === null) {
      continue;
    }
    const gross = invoicedMonthlyNet + vatPerVehicle(invoicedMonthlyNet, vatRatePercent, vatExempt);
    value += quantity * gross * termMonths;
  }
  return roundToCents(value);
}

// src/contract/contract.interface.ts
var CONTRACT_STATUS = {
  DRAFT: "DRAFT",
  READY_TO_SEND: "READY_TO_SEND",
  SENT_TO_CUSTOMER: "SENT_TO_CUSTOMER",
  EXPIRED: "EXPIRED",
  SIGNED: "SIGNED",
  ACTIVE: "ACTIVE",
  HOLDOVER: "HOLDOVER",
  ENDING: "ENDING",
  CLOSED: "CLOSED",
  // Set by the termination stories (SWOOP-383); shown and filterable from SWOOP-345. No document holds
  // it yet, so no migration.
  TERMINATED: "TERMINATED",
  ARCHIVED: "ARCHIVED"
};
var CONTRACT_KIND = { MASTER: "MASTER", ADDENDUM: "ADDENDUM" };
var LEASE_MODE = { OPERATING_LEASE: "OPERATING_LEASE", LEASE_TO_OWN: "LEASE_TO_OWN" };
var MILEAGE_ALLOWANCE_MODE = { PER_VEHICLE: "PER_VEHICLE", POOLED: "POOLED" };
var AGREEMENT_STATUS = {
  PENDING: "PENDING",
  ASSIGNED: "ASSIGNED",
  ON_LEASE: "ON_LEASE",
  EARLY_TERMINATED: "EARLY_TERMINATED",
  SUBSTITUTED: "SUBSTITUTED"
};
var CONTRACT_DEFAULTS = {
  depositMonths: 1,
  gracePeriodDays: 5,
  kind: CONTRACT_KIND.MASTER,
  leaseMode: LEASE_MODE.OPERATING_LEASE,
  mileageAllowanceMode: MILEAGE_ALLOWANCE_MODE.PER_VEHICLE,
  vatExempt: false
};
var CONTRACT_LIMITS = {
  /** Deposit, in months of rent (SWOOP-351). */
  depositMonthsMax: 6,
  /** Decimal places an amount may carry (SWOOP-349). */
  amountDecimals: 2,
  archiveReasonMax: 500,
  exemptionReferenceMax: 100,
  /** A recorded quotation's blob path and file name (SWOOP-394). */
  quotationBlobPathMax: 300,
  quotationFileNameMax: 255
};
var CONTRACT_DOCUMENT_STATUS = {
  FAILED: "FAILED",
  GENERATED: "GENERATED",
  NO_TEMPLATE: "NO_TEMPLATE"
};
var CONTRACT_SIGNED_VIA = { MAGIC_LINK: "MAGIC_LINK", MANUAL: "MANUAL" };

// src/contract/agreement-drafts.ts
function buildAgreementDrafts(contract) {
  return contract.lines.flatMap((line, lineOffset) => {
    const monthlyNet = line.invoicedMonthlyNet ?? 0;
    const monthlyGross = roundToCents(
      monthlyNet + vatPerVehicle(monthlyNet, contract.vatRatePercent, contract.vatExempt)
    );
    return Array.from(
      { length: line.quantity },
      (_unused, vehicleOffset) => ({
        // SWOOP-417: the annex print/sign fields are all null until the annex is printed / signed.
        annexPrintedAt: null,
        annexPrintedBy: null,
        annualMileage: line.annualMileage,
        // SWOOP-366/398: the assignment and handover fields are all null until a vehicle is assigned / handed over.
        assignedAt: null,
        assignedBy: null,
        contractId: contract.id,
        contractNumber: contract.number,
        customerId: contract.customerId,
        driverId: null,
        // SWOOP-414: the modify fields are all null until the first terms modify, and the current end date then falls
        // back to `leaseEndDate(handover, term)` (see R2). The extra-km price is the line's, copied as signed.
        endDate: null,
        endDateSince: null,
        extraKmPrice: line.extraKmPrice,
        extraKmPriceSince: null,
        handoverDate: null,
        handoverDocumentDownloadedAt: null,
        handoverDocumentDownloadedBy: null,
        handoverDraftDate: null,
        handoverDraftDriverId: null,
        handoverDraftOdometer: null,
        handoverDraftTime: null,
        handoverOdometer: null,
        handoverRecordedAt: null,
        handoverRecordedBy: null,
        handoverTime: null,
        lineIndex: lineOffset + 1,
        makeId: line.makeId,
        modelId: line.modelId,
        monthlyGross,
        monthlyNet,
        monthlyPriceSince: null,
        number: formatAgreementNumber(contract.number, lineOffset + 1, vehicleOffset + 1),
        originalEndDate: null,
        originalExtraKmPrice: null,
        originalMonthlyGross: null,
        originalMonthlyNet: null,
        signedAnnexDocumentId: null,
        signedAnnexUploadedAt: null,
        signedAnnexUploadedBy: null,
        signedHandoverDocumentId: null,
        signedHandoverUploadedAt: null,
        signedHandoverUploadedBy: null,
        status: AGREEMENT_STATUS.PENDING,
        termMonths: line.termMonths,
        termsModifiedAt: null,
        termsModifiedBy: null,
        territoryId: contract.territoryId,
        vehicleId: null,
        vehicleIndex: vehicleOffset + 1
      })
    );
  });
}

// src/contract/relief.interface.ts
var RELIEF_STATUS = {
  PREPARED: "PREPARED",
  OUT: "OUT",
  RETURNED: "RETURNED",
  CANCELLED: "CANCELLED"
};
var RELIEF_CHARGE_TYPE = {
  FREE: "FREE",
  PER_DAY: "PER_DAY",
  PER_MONTH: "PER_MONTH"
};
var RELIEF_SELECTABLE_CHARGE_TYPES = [RELIEF_CHARGE_TYPE.FREE, RELIEF_CHARGE_TYPE.PER_DAY];
var RELIEF_INVOICE_STATUS = {
  TO_BE_RAISED: "TO_BE_RAISED"
};

// src/contract/relief-charge.ts
var RELIEF_DAYS_PER_MONTH = 30;
var MS_PER_DAY2 = 24 * 60 * 60 * 1e3;
function reliefDays(fromIsoDate, toIsoDate) {
  const [fromYear, fromMonth, fromDay] = fromIsoDate.split("-").map(Number);
  const [toYear, toMonth, toDay] = toIsoDate.split("-").map(Number);
  const from = Date.UTC(fromYear, fromMonth - 1, fromDay);
  const to = Date.UTC(toYear, toMonth - 1, toDay);
  return Math.round((to - from) / MS_PER_DAY2);
}
var RELIEF_DAILY_RATE = {
  [RELIEF_CHARGE_TYPE.FREE]: () => 0,
  [RELIEF_CHARGE_TYPE.PER_DAY]: (amount) => amount,
  [RELIEF_CHARGE_TYPE.PER_MONTH]: (amount) => amount / RELIEF_DAYS_PER_MONTH
};
function baseCharge(input, baseDays) {
  const periods = input.chargePeriods !== void 0 && input.chargePeriods.length > 0 ? input.chargePeriods : [{ chargeAmount: input.chargeAmount, chargeType: input.chargeType, startsOn: input.handoverDate }];
  return periods.reduce((total, period, index) => {
    const next = periods[index + 1];
    const from = Math.max(0, reliefDays(input.handoverDate, period.startsOn));
    const to = next === void 0 ? baseDays : Math.min(baseDays, reliefDays(input.handoverDate, next.startsOn));
    const days = Math.max(0, to - from);
    return total + RELIEF_DAILY_RATE[period.chargeType](period.chargeAmount) * days;
  }, 0);
}
function computeReliefCharge(input) {
  const agreedDays = reliefDays(input.handoverDate, input.agreedReturnDate);
  const daysKept = Math.max(1, reliefDays(input.handoverDate, input.returnDate));
  const overrunDays = Math.max(0, reliefDays(input.agreedReturnDate, input.returnDate));
  const baseDays = daysKept - overrunDays;
  const net = roundToCents(baseCharge(input, baseDays) + input.overrunRatePerDay * overrunDays);
  const vat = vatPerVehicle(net, input.vatRatePercent, input.vatExempt);
  const gross = roundToCents(net + vat);
  return { agreedDays, baseDays, daysKept, gross, net, overrunDays, vat };
}
function earliestReliefChargeStart(relief) {
  return relief.chargePeriods[relief.chargePeriods.length - 1]?.startsOn ?? relief.handoverDate;
}
function isSelectableReliefChargeType(chargeType) {
  return RELIEF_SELECTABLE_CHARGE_TYPES.some((selectable) => selectable === chargeType);
}
function reliefInvoiceStatus(gross) {
  return gross > 0 ? RELIEF_INVOICE_STATUS.TO_BE_RAISED : null;
}
function liveOverrunDays(relief, todayIsoDate) {
  return relief.status === RELIEF_STATUS.OUT ? Math.max(0, reliefDays(relief.agreedReturnDate, todayIsoDate)) : relief.overrunDays ?? 0;
}

// src/contract/relief-actions.ts
var RELIEF_ACTION = {
  ASSIGN: "ASSIGN",
  EDIT_CHARGE: "EDIT_CHARGE",
  RETURN: "RETURN",
  CANCEL: "CANCEL"
};
function isReliefOpen(status) {
  return status === RELIEF_STATUS.PREPARED || status === RELIEF_STATUS.OUT;
}
var ACTIONS_BY_STATUS = {
  [RELIEF_STATUS.PREPARED]: [RELIEF_ACTION.CANCEL],
  [RELIEF_STATUS.OUT]: [RELIEF_ACTION.EDIT_CHARGE, RELIEF_ACTION.RETURN],
  [RELIEF_STATUS.RETURNED]: [],
  [RELIEF_STATUS.CANCELLED]: []
};
function nextReliefActions(relief, contractLeasing) {
  if (relief === null) {
    return contractLeasing ? [RELIEF_ACTION.ASSIGN] : [];
  }
  const actions = ACTIONS_BY_STATUS[relief.status];
  if (contractLeasing) {
    return actions;
  }
  return actions.filter((action) => action === RELIEF_ACTION.RETURN || action === RELIEF_ACTION.CANCEL);
}

// src/customer/customer.interface.ts
var CUSTOMER_TYPE = {
  COMPANY: "COMPANY",
  INDIVIDUAL: "INDIVIDUAL"
};
var CUSTOMER_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE"
};
var LEASING_STATUS = {
  CUSTOMER: "CUSTOMER",
  DEBTOR: "DEBTOR",
  PROSPECT: "PROSPECT"
};
function resolveLeasingStatus(customer) {
  return customer.leasingStatus ?? LEASING_STATUS.PROSPECT;
}

// src/contract/contract-actions.ts
var CONTRACT_ARCHIVABLE_STATUSES = [
  CONTRACT_STATUS.DRAFT,
  CONTRACT_STATUS.READY_TO_SEND,
  CONTRACT_STATUS.SENT_TO_CUSTOMER,
  CONTRACT_STATUS.EXPIRED
];
var CONTRACT_DELETABLE_STATUSES = [
  CONTRACT_STATUS.DRAFT,
  CONTRACT_STATUS.READY_TO_SEND
];
var CONTRACT_EDITABLE_STATUSES = [
  CONTRACT_STATUS.DRAFT,
  CONTRACT_STATUS.READY_TO_SEND
];
var CONTRACT_SIGNED_COPY_STATUSES = [
  CONTRACT_STATUS.READY_TO_SEND,
  CONTRACT_STATUS.SENT_TO_CUSTOMER
];
var CONTRACT_SIGNED_STATUSES = [
  CONTRACT_STATUS.SIGNED,
  CONTRACT_STATUS.ACTIVE,
  CONTRACT_STATUS.HOLDOVER,
  CONTRACT_STATUS.ENDING,
  CONTRACT_STATUS.CLOSED,
  CONTRACT_STATUS.TERMINATED
];
var CONTRACT_LEASING_STATUSES = [
  CONTRACT_STATUS.SIGNED,
  CONTRACT_STATUS.ACTIVE,
  CONTRACT_STATUS.HOLDOVER,
  CONTRACT_STATUS.ENDING
];
var CONTRACT_ADDENDUM_SETTLED_STATUSES = [
  CONTRACT_STATUS.SIGNED,
  CONTRACT_STATUS.ACTIVE,
  CONTRACT_STATUS.HOLDOVER,
  CONTRACT_STATUS.ENDING,
  CONTRACT_STATUS.CLOSED,
  CONTRACT_STATUS.TERMINATED,
  CONTRACT_STATUS.ARCHIVED
];
var CONTRACT_ADDENDUM_MASTER_STATUSES = [
  CONTRACT_STATUS.SIGNED,
  CONTRACT_STATUS.ACTIVE
];
function canArchiveContract(status) {
  return CONTRACT_ARCHIVABLE_STATUSES.includes(status);
}
function canDuplicateContract(contract) {
  return contract.kind === CONTRACT_KIND.MASTER && contract.status !== CONTRACT_STATUS.ARCHIVED;
}
function canBeAddendumMaster(contract) {
  return contract.kind === CONTRACT_KIND.MASTER && CONTRACT_ADDENDUM_MASTER_STATUSES.includes(contract.status);
}
function canDeleteContract(contract) {
  return CONTRACT_DELETABLE_STATUSES.includes(contract.status) && contract.sentAt === null;
}
function canEditContract(contract) {
  return CONTRACT_EDITABLE_STATUSES.includes(contract.status) && contract.sentAt === null;
}
function isContractSigned(status) {
  return CONTRACT_SIGNED_STATUSES.includes(status);
}
function isLeasingContract(status) {
  return CONTRACT_LEASING_STATUSES.includes(status);
}
function canRestoreContract(status) {
  return status === CONTRACT_STATUS.ARCHIVED;
}
function restoreTargetStatus(statusBeforeArchive) {
  return statusBeforeArchive ?? CONTRACT_STATUS.DRAFT;
}
function canRegenerateContractDocument(contract) {
  return contract.status === CONTRACT_STATUS.READY_TO_SEND && contract.sentAt === null;
}
function contractLinesHaveExtraKmPrice(contract) {
  return contract.lines.every((line) => line.extraKmPrice !== null);
}
function canSendForSignature(contract) {
  return canRegenerateContractDocument(contract) && contract.document?.status === CONTRACT_DOCUMENT_STATUS.GENERATED && contractLinesHaveExtraKmPrice(contract);
}
function canUploadSignedCopy(contract) {
  if (!CONTRACT_SIGNED_COPY_STATUSES.includes(contract.status)) {
    return false;
  }
  return contract.status !== CONTRACT_STATUS.READY_TO_SEND || contractLinesHaveExtraKmPrice(contract);
}

// src/contract/customer-leasing-status.ts
function nextLeasingStatus(current, contractStatuses) {
  if (contractStatuses.some(isLeasingContract)) {
    return LEASING_STATUS.DEBTOR;
  }
  if (current === LEASING_STATUS.DEBTOR || contractStatuses.some(isContractSigned)) {
    return LEASING_STATUS.CUSTOMER;
  }
  return current;
}

// src/contract/contract-defaults.ts
var DEFAULT_INVOICING_CYCLE = "Monthly in advance";
var DEFAULT_PAYMENT_DUE = "Net 30";
function pickDefaultOption(options, preferred) {
  return options.includes(preferred) ? preferred : options[0] ?? null;
}
function formatContractNumber(year, sequence) {
  const yy = String(year % 100).padStart(2, "0");
  return `MA-${yy}-${String(sequence).padStart(4, "0")}`;
}
function formatAddendumNumber(masterNumber, index) {
  return `${masterNumber}-A${index}`;
}
function masterNumberOfAddendum(addendumNumber) {
  return addendumNumber.replace(/-A\d+$/, "");
}
function addUtcMonths(start, months) {
  const year = start.getUTCFullYear();
  const month = start.getUTCMonth();
  const day = start.getUTCDate();
  const lastDayOfTargetMonth = new Date(Date.UTC(year, month + months + 1, 0)).getUTCDate();
  return new Date(
    Date.UTC(
      year,
      month + months,
      Math.min(day, lastDayOfTargetMonth),
      start.getUTCHours(),
      start.getUTCMinutes(),
      start.getUTCSeconds(),
      start.getUTCMilliseconds()
    )
  );
}
function computeContractEndDate(startDate, lines) {
  const terms = lines.map((line) => line.termMonths).filter((term) => term !== null);
  if (startDate === null || terms.length === 0) {
    return null;
  }
  return addUtcMonths(startDate, Math.max(...terms));
}

// src/contract/contract-extension.ts
var CONTRACT_EXTENSION_STATUS = { SIGNED: "SIGNED", WAITING: "WAITING" };
function extensionMonthsNeeded(contractEnd, latestEnd) {
  if (latestEnd.getTime() <= contractEnd.getTime()) {
    return 0;
  }
  let months = 1;
  while (addUtcMonths(contractEnd, months).getTime() < latestEnd.getTime()) {
    months += 1;
  }
  return months;
}
function extendedContractEnd(contractEnd, latestEnd) {
  return addUtcMonths(contractEnd, extensionMonthsNeeded(contractEnd, latestEnd));
}
function formatExtensionNumber(contractNumber, index) {
  return `${contractNumber}-EXT${String(index).padStart(2, "0")}`;
}
function contractExtensionFileName(extensionNumber) {
  return `${extensionNumber}.pdf`;
}

// src/leasing-settings/leasing-settings.interface.ts
var DISTANCE_UNIT = { km: "km", mi: "mi" };
var EXTRA_FEE_BASIS = {
  PER_MILLE_OF_CONTRACT_VALUE: "PER_MILLE_OF_CONTRACT_VALUE",
  PERCENT_OF_CONTRACT_VALUE: "PERCENT_OF_CONTRACT_VALUE",
  FIXED_PER_CONTRACT: "FIXED_PER_CONTRACT"
};
var LEASING_SETTINGS_DEFAULTS = {
  terms: [24, 36, 48, 60, 72],
  annualMileages: [15e3, 2e4, 3e4, 4e4, 5e4],
  quoteValidityDays: 7,
  distanceUnit: DISTANCE_UNIT.km,
  costOfFundsPercent: 9,
  marginPercent: 25,
  adminFeeMonthly: 22,
  leaseToOwnPercent: 1,
  invoicingCycles: ["Monthly in advance", "Quarterly in advance"],
  paymentDueOptions: ["Cash", "Net 30", "Net 45", "Net 60"],
  lateInterestPercent: 9,
  rentIncreaseAfterEndPercent: 10,
  extraFees: [
    {
      code: "STAMP_DUTY",
      name: "Stamp duty",
      amount: 4,
      basis: EXTRA_FEE_BASIS.PER_MILLE_OF_CONTRACT_VALUE,
      mandatory: true
    }
  ]
};
var LEASING_SETTINGS_LIMITS = {
  maxOptions: 20,
  termMonthsMax: 240,
  annualMileageMax: 1e6,
  quoteValidityDays: { min: 1, max: 180 },
  /** Upper bound of the three percentage rates; every pricing parameter's minimum is 0. */
  percentMax: 100,
  /** Decimal places a pricing parameter, a payment-terms rate or a fee amount may carry. */
  pricingDecimals: 2,
  /** Longest invoicing cycle or payment due label, after trimming. */
  optionLabelMax: 40,
  /** Longest extra fee name, after trimming. */
  feeNameMax: 60,
  /** Most extra fees a territory may hold. */
  maxFees: 20,
  /** Upper bound of a per-mille fee amount. */
  perMilleMax: 1e3
};
var LEASING_ADMIN_FEE_MAX_BY_CURRENCY = {
  EUR: 1e4,
  GBP: 1e4,
  LBP: 1e9,
  USD: 1e4
};
var EXTRA_FEE_AMOUNT_MAX_ANY = Math.max(
  LEASING_SETTINGS_LIMITS.perMilleMax,
  LEASING_SETTINGS_LIMITS.percentMax,
  ...Object.values(LEASING_ADMIN_FEE_MAX_BY_CURRENCY)
);
var EXTRA_FEE_MAX_BY_BASIS = {
  [EXTRA_FEE_BASIS.PER_MILLE_OF_CONTRACT_VALUE]: () => LEASING_SETTINGS_LIMITS.perMilleMax,
  [EXTRA_FEE_BASIS.PERCENT_OF_CONTRACT_VALUE]: () => LEASING_SETTINGS_LIMITS.percentMax,
  // Same technical guard as the admin fee (SWOOP-340): a fixed fee is money in the territory's currency.
  [EXTRA_FEE_BASIS.FIXED_PER_CONTRACT]: (currency) => LEASING_ADMIN_FEE_MAX_BY_CURRENCY[currency]
};
function extraFeeAmountMax(basis, currency) {
  return EXTRA_FEE_MAX_BY_BASIS[basis](currency);
}

// src/contract/contract-fees.ts
var EXTRA_FEE_FORMULA = {
  [EXTRA_FEE_BASIS.FIXED_PER_CONTRACT]: (_contractValue, rate) => rate,
  [EXTRA_FEE_BASIS.PERCENT_OF_CONTRACT_VALUE]: (contractValue, rate) => contractValue * rate / 100,
  [EXTRA_FEE_BASIS.PER_MILLE_OF_CONTRACT_VALUE]: (contractValue, rate) => contractValue * rate / 1e3
};
function computeExtraFeeAmount(basis, rate, contractValue) {
  return roundToCents(EXTRA_FEE_FORMULA[basis](contractValue, rate));
}
function isApplied(mandatory, applied, index) {
  return mandatory || applied?.[index] === true;
}
function buildContractExtraFees(territoryFees, applied, contractValue) {
  return territoryFees.map(
    (fee, index) => withAmount(
      {
        amount: 0,
        applied: isApplied(fee.mandatory, applied, index),
        basis: fee.basis,
        code: fee.code,
        mandatory: fee.mandatory,
        name: fee.name,
        rate: fee.amount
      },
      contractValue
    )
  );
}
function recomputeContractExtraFees(stored, applied, contractValue) {
  return stored.map(
    (fee, index) => withAmount(
      { ...fee, applied: applied === void 0 ? fee.applied : isApplied(fee.mandatory, applied, index) },
      contractValue
    )
  );
}
function withAmount(fee, contractValue) {
  return { ...fee, amount: fee.applied ? computeExtraFeeAmount(fee.basis, fee.rate, contractValue) : 0 };
}

// src/invoicing/enums.ts
var BILLING_SOURCE = {
  LEASING: "LEASING",
  MANUAL: "MANUAL",
  RECHARGE: "RECHARGE",
  RENTAL: "RENTAL",
  TRANSPORT: "TRANSPORT",
  WORKSHOP: "WORKSHOP"
};
var CHARGE_SOURCE_TYPE = {
  ADMIN_FEE: "ADMIN_FEE",
  CONTRACT_FEE: "CONTRACT_FEE",
  DAMAGE_RECHARGE: "DAMAGE_RECHARGE",
  FINE_RECHARGE: "FINE_RECHARGE",
  LEASE_RENT: "LEASE_RENT",
  MANUAL_ADJUSTMENT: "MANUAL_ADJUSTMENT"
};
var CHARGE_STATE = {
  CANCELLED: "CANCELLED",
  INVOICED: "INVOICED",
  READY: "READY",
  RESERVED: "RESERVED"
};
var LEGAL_STATUS = {
  DRAFT: "DRAFT",
  ISSUED: "ISSUED",
  VOID: "VOID"
};
var INVOICE_TYPE = {
  CREDIT_NOTE: "CREDIT_NOTE",
  INVOICE: "INVOICE"
};
var DOCUMENT_CATEGORY = {
  ADJUSTMENT: "ADJUSTMENT",
  DAMAGE: "DAMAGE",
  GENERAL: "GENERAL",
  LEASE: "LEASE"
};
var DOCUMENT_CODE = {
  AJ: "AJ",
  BC: "BC",
  CR: "CR",
  DM: "DM"
};
var PAYMENT_STATUS = {
  PAID: "PAID",
  PARTIAL: "PARTIAL",
  UNKNOWN: "UNKNOWN",
  UNPAID: "UNPAID"
};
var INVOICE_DELIVERY_STATUS = {
  DELIVERED: "DELIVERED",
  FAILED: "FAILED",
  NOT_QUEUED: "NOT_QUEUED",
  QUEUED: "QUEUED"
};
var ERP_SYNC_STATUS = {
  FAILED: "FAILED",
  NOT_CONFIGURED: "NOT_CONFIGURED",
  PENDING: "PENDING",
  REVIEW: "REVIEW",
  SYNCED: "SYNCED"
};
var SOURCE_PLATFORM = {
  DYNAMICS_365: "DYNAMICS_365",
  HETIVO: "HETIVO",
  IMPORT: "IMPORT",
  ODOO: "ODOO"
};
var RUN_MODE = {
  MONTHLY: "MONTHLY",
  SOLO: "SOLO",
  SYNC: "SYNC"
};
var RUN_STATUS = {
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  QUEUED: "QUEUED",
  RUNNING: "RUNNING"
};
var GROUP_STATUS = {
  AUTO_ISSUED: "AUTO_ISSUED",
  DRAFTED: "DRAFTED",
  FAILED: "FAILED",
  ISSUED: "ISSUED",
  PENDING: "PENDING",
  REVIEW: "REVIEW"
};
var JOB_TYPE = {
  ACCOUNTING_SWEEP: "ACCOUNTING_SWEEP",
  DELIVERY: "DELIVERY",
  ISSUE_BATCH_SWEEP: "ISSUE_BATCH_SWEEP",
  PAYMENT_PULL: "PAYMENT_PULL",
  REMINDER: "REMINDER",
  RUN_DISPATCH: "RUN_DISPATCH",
  SCHEDULED_EXPORT: "SCHEDULED_EXPORT",
  SOURCE_SYNC: "SOURCE_SYNC"
};
var JOB_STATUS = {
  FAILED: "FAILED",
  PARTIAL: "PARTIAL",
  RUNNING: "RUNNING",
  SUCCEEDED: "SUCCEEDED"
};
var ISSUE_BATCH_STATUS = {
  COMPLETED: "COMPLETED",
  COMPLETED_WITH_FAILURES: "COMPLETED_WITH_FAILURES",
  FAILED: "FAILED",
  QUEUED: "QUEUED",
  RUNNING: "RUNNING",
  STOPPED: "STOPPED"
};
var ISSUE_BATCH_ITEM_STATUS = {
  FAILED: "FAILED",
  ISSUED: "ISSUED",
  PENDING: "PENDING",
  SKIPPED: "SKIPPED"
};
var ISSUE_BATCH_SOURCE = {
  MANUAL: "MANUAL",
  RUN: "RUN"
};
var LINE_KIND = {
  CHARGE: "CHARGE",
  CREDIT: "CREDIT",
  SUMMARY: "SUMMARY"
};
var CREDIT_REASON = {
  DAMAGE_DISPUTE: "DAMAGE_DISPUTE",
  DUPLICATE_CHARGE: "DUPLICATE_CHARGE",
  EARLY_TERMINATION: "EARLY_TERMINATION",
  FINE_CANCELLED: "FINE_CANCELLED",
  GOODWILL: "GOODWILL",
  OTHER: "OTHER",
  PRICE_ERROR: "PRICE_ERROR",
  VEHICLE_SUBSTITUTION: "VEHICLE_SUBSTITUTION"
};
var INTEGRATION_MODE = {
  ERP_ISSUES: "ERP_ISSUES",
  ERP_PUSH: "ERP_PUSH",
  FILE_EXPORT: "FILE_EXPORT"
};
var AUTOMATION_MODE = {
  ASSISTED: "ASSISTED",
  AUTOMATIC: "AUTOMATIC",
  MANUAL: "MANUAL"
};
var ACCOUNTING_TARGET = {
  DEFAULT: "DEFAULT"
};
var ACCOUNTING_MAPPING_TYPE = {
  CURRENCY: "CURRENCY",
  CUSTOMER: "CUSTOMER",
  LOCATION: "LOCATION",
  PAYMENT_METHOD: "PAYMENT_METHOD",
  SOURCE_TYPE: "SOURCE_TYPE",
  TAX_RATE: "TAX_RATE"
};
var ACCOUNTING_SYNC_OPERATION = {
  CREDIT: "CREDIT",
  ENSURE_CUSTOMER: "ENSURE_CUSTOMER",
  ISSUE: "ISSUE",
  PAYMENT: "PAYMENT",
  POST: "POST",
  VOID: "VOID"
};
var ACCOUNTING_SYNC_JOB_STATUS = {
  DEAD: "DEAD",
  FAILED: "FAILED",
  IN_PROGRESS: "IN_PROGRESS",
  PENDING: "PENDING",
  SUCCEEDED: "SUCCEEDED"
};
var SERIES_RESET_POLICY = {
  NEVER: "NEVER",
  YEARLY: "YEARLY"
};
var IDEMPOTENCY_STATE = {
  COMPLETED: "COMPLETED",
  IN_PROGRESS: "IN_PROGRESS"
};
var BASE_CURRENCY_FX_POLICY = {
  OPTIONAL: "OPTIONAL",
  REQUIRED: "REQUIRED"
};
var PAYMENT_STATUS_SOURCE = {
  ERP: "ERP",
  MANUAL: "MANUAL",
  PAYMENT_PROVIDER: "PAYMENT_PROVIDER"
};
var INVOICE_REFERENCE_KIND = {
  AGREEMENT: "AGREEMENT",
  CONTRACT: "CONTRACT",
  DISPUTE: "DISPUTE",
  EXPENSE: "EXPENSE",
  PO: "PO",
  /** The request reference of a contract fee re-bill, e.g. `SD/2026/000002` (SWOOP-416). */
  REQUEST: "REQUEST",
  TICKET: "TICKET",
  WORK_ORDER: "WORK_ORDER"
};
var EXTERNAL_ISSUE_STATUS = {
  CONFIRMED: "CONFIRMED",
  REJECTED: "REJECTED",
  REQUESTED: "REQUESTED"
};
var GROUPING_KEY = {
  AGREEMENT: "AGREEMENT",
  CONTRACT: "CONTRACT",
  EVENT: "EVENT",
  INVOICING_CYCLE: "INVOICING_CYCLE",
  LOCATION: "LOCATION",
  PO: "PO"
};
var EXPORT_CADENCE = {
  DAILY: "DAILY",
  MONTHLY: "MONTHLY",
  WEEKLY: "WEEKLY"
};
var EXPORT_FORMAT = {
  CSV: "CSV",
  XLSX: "XLSX"
};
var EXPORT_DELIVERY = {
  ATTACHMENT: "ATTACHMENT",
  LINK: "LINK"
};

// src/contract/contract-fee-codes.ts
var CONTRACT_FEE_CODE = {
  NOTARISATION: "NOTARISATION",
  OTHER: "OTHER",
  STAMP_DUTY: "STAMP_DUTY"
};
var CONTRACT_FEE_CODES = Object.values(CONTRACT_FEE_CODE);
var CONTRACT_FEE_CODE_REGISTRY = {
  [CONTRACT_FEE_CODE.NOTARISATION]: {
    documentCategory: DOCUMENT_CATEGORY.ADJUSTMENT,
    rebillable: false,
    referenceCode: "NT"
  },
  [CONTRACT_FEE_CODE.OTHER]: { documentCategory: null, rebillable: false, referenceCode: null },
  [CONTRACT_FEE_CODE.STAMP_DUTY]: {
    documentCategory: DOCUMENT_CATEGORY.ADJUSTMENT,
    rebillable: true,
    referenceCode: "SD"
  }
};
var REBILLABLE_FEE_CODES = CONTRACT_FEE_CODES.filter(
  (code) => CONTRACT_FEE_CODE_REGISTRY[code].rebillable
);
function feeRebillSourceRef(contractId, feeCode, reference) {
  return `${contractId}:${feeCode}:${reference}`;
}
function billedInvoiceOfSourceRef(items, sourceRef) {
  const item = items.find((candidate) => candidate.sourceRef === sourceRef && candidate.status === "KNOWN");
  return item?.charges.find((charge) => charge.invoice !== null)?.invoice ?? null;
}
var FEE_REBILL_SOURCE_REF_PATTERN = /^([0-9a-f]{24}):([A-Z_]+):([A-Z]{2}\/\d{4}\/\d{6})$/;
function parseFeeRebillSourceRef(sourceRef) {
  const match = FEE_REBILL_SOURCE_REF_PATTERN.exec(sourceRef);
  if (match === null) {
    return null;
  }
  const [, contractId, code, reference] = match;
  const feeCode = CONTRACT_FEE_CODES.find((candidate) => candidate === code);
  return feeCode === void 0 ? null : { contractId, feeCode, reference };
}

// src/contract/contract-fee-rebill.ts
var FEE_REBILL_BASIS_KIND = { AMOUNT: "AMOUNT", PERCENT: "PERCENT" };
var FEE_REBILL_STATUS = { RAISED: "RAISED", TO_BE_RAISED: "TO_BE_RAISED" };
var FEE_REBILL_COUNTED_STATUSES = [
  FEE_REBILL_STATUS.TO_BE_RAISED,
  FEE_REBILL_STATUS.RAISED
];
var FEE_REBILL_DEFAULT_STATUS = FEE_REBILL_STATUS.TO_BE_RAISED;
function feeRebillCountsTowardDue(rebill) {
  return FEE_REBILL_COUNTED_STATUSES.includes(rebill.status ?? FEE_REBILL_DEFAULT_STATUS);
}
function hasAtMostTwoDecimals(value) {
  const cents = value * 100;
  return Math.abs(cents - Math.round(cents)) < 1e-6;
}
function feeRebillRemaining(input) {
  const { currency, due, feeCode, rebills } = input;
  const rebilled = rebills.reduce(
    (sum, rebill) => rebill.feeCode === feeCode && feeRebillCountsTowardDue(rebill) ? sum + rebill.amount : sum,
    0
  );
  return Math.max(0, roundToMinorUnit(due - rebilled, currency));
}
function computeFeeRebillAmount(input) {
  const { basis, currency, due, remaining } = input;
  const { kind, value } = basis;
  if (!Number.isFinite(value)) {
    return { error: "BASIS_OUT_OF_RANGE", ok: false };
  }
  let exact;
  if (kind === FEE_REBILL_BASIS_KIND.PERCENT) {
    if (value < 0.01 || value > 100 || !hasAtMostTwoDecimals(value)) {
      return { error: "BASIS_OUT_OF_RANGE", ok: false };
    }
    exact = due * value / 100;
  } else {
    if (value <= 0) {
      return { error: "BASIS_OUT_OF_RANGE", ok: false };
    }
    exact = value;
  }
  const amount = roundToMinorUnit(exact, currency);
  if (amount <= 0) {
    return { error: "ZERO_AMOUNT", ok: false };
  }
  return amount > remaining ? { error: "EXCEEDS_REMAINING", ok: false } : { amount, ok: true };
}

// src/contract/contract-addenda.ts
function isAddendumInForce(status) {
  return CONTRACT_LEASING_STATUSES.includes(status);
}
function computeVehicleCoverage(master, addenda) {
  const addendaVehicleCount = addenda.filter((addendum) => isAddendumInForce(addendum.status)).reduce((sum, addendum) => sum + addendum.vehicleCount, 0);
  return {
    addendaVehicleCount,
    masterVehicleCount: master.vehicleCount,
    vehicleCount: master.vehicleCount + addendaVehicleCount
  };
}

// src/contract/purchase-order.interface.ts
var PURCHASE_ORDER_STATUS = {
  VALID: "VALID",
  EXPIRED: "EXPIRED",
  STARTS_LATER: "STARTS_LATER"
};
var PURCHASE_ORDER_SOURCE = {
  /** The agreement's own PO. */
  AGREEMENT: "AGREEMENT",
  /** The PO of the contract the agreement is on (its master, or its addendum). */
  CONTRACT: "CONTRACT",
  /** The master's PO, for an agreement on an addendum whose addendum has none valid on the date. */
  MASTER: "MASTER"
};
var PURCHASE_ORDER_WRITE_GATE = {
  /** Signed and still running: POs may be added, edited and deleted. */
  EDITABLE: "EDITABLE",
  /** Closed, Terminated, or archived after signature: POs stay on file but are frozen. */
  CLOSED: "CLOSED",
  /** Never signed (not yet, Expired, or archived before signature): no PO can be recorded. */
  NOT_SIGNED: "NOT_SIGNED"
};
var PURCHASE_ORDER_CARD_MODE = {
  /** Signed and still running: list plus actions (for callers holding `contract-po:manage`). */
  EDITABLE: "EDITABLE",
  /** Closed, Terminated, or archived after signature: list only. */
  READ_ONLY: "READ_ONLY",
  /** Not signed yet (SWOOP-364): "Available after signature", no list, Add PO disabled. */
  LOCKED: "LOCKED",
  /** Never signed and no longer signable (Expired, archived before signature): no card. */
  HIDDEN: "HIDDEN"
};
var PURCHASE_ORDER_NUMBER_MAX_LENGTH = 64;
var PURCHASE_ORDER_EXPIRY_WARNING_DAYS = 30;
var CONTRACT_PO_MANAGE_PERMISSION = "contract-po:manage";

// src/contract/purchase-order-rules.ts
var DAY_MS = 24 * 60 * 60 * 1e3;
function utcDay(value) {
  const date = typeof value === "string" ? /* @__PURE__ */ new Date(`${value.slice(0, 10)}T00:00:00.000Z`) : value;
  return Math.floor(date.getTime() / DAY_MS);
}
function poPeriodsOverlap(a, b) {
  const aTo = a.validTo === null ? Number.POSITIVE_INFINITY : utcDay(a.validTo);
  const bTo = b.validTo === null ? Number.POSITIVE_INFINITY : utcDay(b.validTo);
  return utcDay(a.validFrom) <= bTo && utcDay(b.validFrom) <= aTo;
}
function isPurchaseOrderPeriodOrdered(period) {
  return period.validTo === null || utcDay(period.validTo) >= utcDay(period.validFrom);
}
function isPurchaseOrderValidOn(po, date) {
  const day = utcDay(date);
  return utcDay(po.validFrom) <= day && (po.validTo === null || day <= utcDay(po.validTo));
}
function classifyPurchaseOrder(po, date) {
  if (isPurchaseOrderValidOn(po, date)) {
    return PURCHASE_ORDER_STATUS.VALID;
  }
  return utcDay(po.validFrom) > utcDay(date) ? PURCHASE_ORDER_STATUS.STARTS_LATER : PURCHASE_ORDER_STATUS.EXPIRED;
}
function isPurchaseOrderExpiringSoon(po, date) {
  if (po.validTo === null || !isPurchaseOrderValidOn(po, date)) {
    return false;
  }
  return utcDay(po.validTo) - utcDay(date) <= PURCHASE_ORDER_EXPIRY_WARNING_DAYS;
}
function validOn(pos, date) {
  return pos.find((po) => isPurchaseOrderValidOn(po, date));
}
function resolveApplicablePurchaseOrder(input) {
  const masterPos = input.masterPos ?? [];
  const levels = [
    [input.agreementPos, PURCHASE_ORDER_SOURCE.AGREEMENT],
    [input.contractPos, PURCHASE_ORDER_SOURCE.CONTRACT],
    [masterPos, PURCHASE_ORDER_SOURCE.MASTER]
  ];
  for (const [pos, source] of levels) {
    const found = validOn(pos, input.date);
    if (found) {
      return { lapsed: false, po: found, source };
    }
  }
  const lapsed = [...input.agreementPos, ...input.contractPos, ...masterPos].some(
    (po) => classifyPurchaseOrder(po, input.date) === PURCHASE_ORDER_STATUS.EXPIRED
  );
  return { lapsed, po: null, source: null };
}
var CLOSED_STATUSES = [CONTRACT_STATUS.CLOSED, CONTRACT_STATUS.TERMINATED];
function purchaseOrderWriteGate(contract) {
  if (contract.status === CONTRACT_STATUS.ARCHIVED) {
    const before = contract.statusBeforeArchive ?? null;
    return before !== null && CONTRACT_SIGNED_STATUSES.includes(before) ? PURCHASE_ORDER_WRITE_GATE.CLOSED : PURCHASE_ORDER_WRITE_GATE.NOT_SIGNED;
  }
  if (CLOSED_STATUSES.includes(contract.status)) {
    return PURCHASE_ORDER_WRITE_GATE.CLOSED;
  }
  return CONTRACT_SIGNED_STATUSES.includes(contract.status) ? PURCHASE_ORDER_WRITE_GATE.EDITABLE : PURCHASE_ORDER_WRITE_GATE.NOT_SIGNED;
}
function purchaseOrderCardMode(contract) {
  switch (purchaseOrderWriteGate(contract)) {
    case PURCHASE_ORDER_WRITE_GATE.EDITABLE:
      return PURCHASE_ORDER_CARD_MODE.EDITABLE;
    case PURCHASE_ORDER_WRITE_GATE.CLOSED:
      return PURCHASE_ORDER_CARD_MODE.READ_ONLY;
    case PURCHASE_ORDER_WRITE_GATE.NOT_SIGNED:
      return contract.status === CONTRACT_STATUS.ARCHIVED || contract.status === CONTRACT_STATUS.EXPIRED ? PURCHASE_ORDER_CARD_MODE.HIDDEN : PURCHASE_ORDER_CARD_MODE.LOCKED;
  }
}

// src/delivery.ts
var DELIVERY_STATUS = {
  delivered: "delivered",
  failed: "failed",
  in_transit: "in_transit",
  pending: "pending"
};

// src/documents/document-owner.ts
var DOCUMENT_OWNER_TYPE = {
  /**
   * An agreement (SWOOP-398) — its signed handover document (the customer-signed Lease Delivery Note).
   * Written, listed and downloaded ONLY through the agreement's own routes
   * (`/contracts/:id/agreements/:agreementId/handover-document/*`), which check the contract's territory
   * scope; the generic `/documents` routes refuse this owner, as they refuse a contract.
   */
  AGREEMENT: "agreement",
  /**
   * A leasing contract (SWOOP-394) — its quotation PDFs, one per contract version. Written, listed and
   * downloaded ONLY through the contract's own routes (`/contracts/:id/quotations`), which check the
   * contract's territory scope; the generic `/documents` routes refuse this owner.
   */
  CONTRACT: "contract",
  /**
   * A reported fault on a vehicle (SWOOP-255) — its photos. Its own owner type rather than a
   * vehicle-owned document, so the photos stay readable after the vehicle is archived and can be
   * copied to a work order (SWOOP-305) by re-recording them.
   */
  ISSUE: "issue",
  /** A garage / workshop record in `suppliers` (SWOOP-245) — its contract, price list and licences. */
  SUPPLIER: "supplier",
  /**
   * The tenant itself. Used by documents that belong to the organisation rather than to any one
   * record — today the DOCX generation templates, whose id is the tenant's own.
   */
  TENANT: "tenant",
  /**
   * A territory (SWOOP-395) — its contract templates (`DOCUMENT_TEMPLATE`), at most one of them in use. Written,
   * listed and downloaded ONLY through `/contract-templates`, which checks the territory scope and the
   * leasing-settings grants; the generic `/documents` routes refuse this owner.
   */
  TERRITORY: "territory",
  VEHICLE: "vehicle",
  /**
   * A work order's own files (SWOOP-254) — its quotes, reports, photos and the photos copied from the
   * issue it was opened from. Its own owner type (not vehicle-owned) so the files follow the work order's
   * scope, permissions and Maintenance module, and a copied issue photo stays readable from either side.
   */
  WORK_ORDER: "work_order"
};
var DOCUMENT_OWNER_TYPES = Object.values(DOCUMENT_OWNER_TYPE);

// src/documents/document-type.ts
var DOCUMENT_TYPE = {
  /**
   * An agreement's customer-signed agreement annex (SWOOP-417), owner `agreement`. Recorded ONLY through
   * `/contracts/:id/agreements/:agreementId/annex`; refused by the generic document write / delete paths.
   */
  AGREEMENT_SIGNED_ANNEX: "AGREEMENT_SIGNED_ANNEX",
  /**
   * An agreement's customer-signed Lease Delivery Note (SWOOP-398), owner `agreement`. Recorded ONLY through
   * `/contracts/:id/agreements/:agreementId/handover-document`; refused by the generic document write / delete paths.
   */
  AGREEMENT_SIGNED_HANDOVER: "AGREEMENT_SIGNED_HANDOVER",
  /**
   * A contract's quotation PDF (SWOOP-394), owner `contract`, carrying the `contractVersion` it quotes.
   * Recorded ONLY through `/contracts/:id/quotations`; refused by the generic document write / delete paths.
   */
  CONTRACT_QUOTATION: "CONTRACT_QUOTATION",
  /**
   * A contract's signed copy (SWOOP-361), owner `contract`: the uploaded hand-signed PDF, or the copy rendered
   * once the customer accepts by magic link. Written ONLY through `/contracts/:id/master-contract/*`.
   */
  CONTRACT_SIGNED_COPY: "CONTRACT_SIGNED_COPY",
  /**
   * A contract's signed extension addendum (SWOOP-415), owner `contract`: the customer-signed PDF of an extension
   * addendum. Written ONLY through `/contracts/:id/extensions/:extensionId/signed-copy`; refused by the generic
   * document write / delete paths.
   */
  CONTRACT_SIGNED_EXTENSION: "CONTRACT_SIGNED_EXTENSION",
  /**
   * A contract's VAT exemption certificate (SWOOP-352), owner `contract`: the OPTIONAL supporting file a
   * VAT-exempt contract may carry. One per contract. Written and deleted ONLY through
   * `/contracts/:id/vat-certificate`, which refuses it unless the contract is `vatExempt` and not archived;
   * refused by the generic document write / delete paths.
   */
  CONTRACT_VAT_CERTIFICATE: "CONTRACT_VAT_CERTIFICATE",
  /** A DOCX generation template owned by the tenant (SWOOP-268). */
  DOCUMENT_TEMPLATE: "DOCUMENT_TEMPLATE",
  /** A PDF rendered from a template and its hand-typed field values (SWOOP-269). */
  GENERATED_DOCUMENT: "GENERATED_DOCUMENT",
  /** A photo (or scanned notice) attached to a reported issue (SWOOP-255). */
  ISSUE_PHOTO: "ISSUE_PHOTO",
  /**
   * A relief's hand-signed delivery note (SWOOP-399), owner `contract`: the customer-signed PDF uploaded
   * at handover. One live per relief, replaceable until the handover is recorded (the previous one
   * soft-deleted). Written ONLY through `/contracts/:id/reliefs/:reliefId/signed-note`; refused by the
   * generic document write / delete paths.
   */
  RELIEF_SIGNED_NOTE: "RELIEF_SIGNED_NOTE",
  /** The paperwork a garage supplier carries (SWOOP-245). */
  SUPPLIER_CONTRACT: "SUPPLIER_CONTRACT",
  SUPPLIER_INSURANCE: "SUPPLIER_INSURANCE",
  SUPPLIER_OTHER: "SUPPLIER_OTHER",
  SUPPLIER_PRICE_LIST: "SUPPLIER_PRICE_LIST",
  SUPPLIER_TRADE_LICENCE: "SUPPLIER_TRADE_LICENCE",
  VEHICLE_EXPENSE: "VEHICLE_EXPENSE",
  VEHICLE_INSURANCE: "VEHICLE_INSURANCE",
  VEHICLE_OTHERS: "VEHICLE_OTHERS",
  VEHICLE_PHOTOS: "VEHICLE_PHOTOS",
  VEHICLE_REGISTRATION: "VEHICLE_REGISTRATION",
  /**
   * A photo of a single damage on a `DAMAGE` work order's chart (SWOOP-335). Owner `work_order`, carrying a
   * `damageId`. Recorded ONLY through the damage endpoints (in the damage's transaction); excluded from the
   * Documents tab, the drawer list and the 20-document budget (doc Q28). The generic `POST /documents` and
   * `DELETE /documents/:id` refuse it, so a damage can never be left with no photo.
   */
  WORK_ORDER_DAMAGE_PHOTO: "WORK_ORDER_DAMAGE_PHOTO",
  /** A file attached to a work order (SWOOP-254): a quote, a report, a photo, or a copied issue photo. */
  WORK_ORDER_DOCUMENT: "WORK_ORDER_DOCUMENT"
};
var DOCUMENT_TYPES = Object.values(DOCUMENT_TYPE);
var SERVER_MANAGED_DOCUMENT_TYPES = [
  DOCUMENT_TYPE.DOCUMENT_TEMPLATE,
  DOCUMENT_TYPE.GENERATED_DOCUMENT
];
var CASCADING_DELETE_DOCUMENT_TYPES = [DOCUMENT_TYPE.DOCUMENT_TEMPLATE];
var OWNER_ENDPOINT_MANAGED_DOCUMENT_TYPES = [
  // Written and replaced only through the agreement annex routes, which check the contract's scope (SWOOP-417).
  DOCUMENT_TYPE.AGREEMENT_SIGNED_ANNEX,
  // Written and replaced only through the agreement handover routes, which check the contract's scope (SWOOP-398).
  DOCUMENT_TYPE.AGREEMENT_SIGNED_HANDOVER,
  // Written and replaced only through `/contracts/:id/quotations`, which checks the contract's scope (SWOOP-394).
  DOCUMENT_TYPE.CONTRACT_QUOTATION,
  // Written only through `/contracts/:id/master-contract/*`, which checks the contract's scope (SWOOP-361).
  DOCUMENT_TYPE.CONTRACT_SIGNED_COPY,
  // Written only through `/contracts/:id/extensions/:extensionId/signed-copy`, which checks the contract's
  // scope (SWOOP-415).
  DOCUMENT_TYPE.CONTRACT_SIGNED_EXTENSION,
  // Written and deleted only through `/contracts/:id/vat-certificate`, which checks the contract's scope and
  // refuses it unless the contract is VAT exempt and not archived (SWOOP-352).
  DOCUMENT_TYPE.CONTRACT_VAT_CERTIFICATE,
  // Written and replaced only through `/contracts/:id/reliefs/:reliefId/signed-note`, which checks the
  // contract's scope (SWOOP-399).
  DOCUMENT_TYPE.RELIEF_SIGNED_NOTE,
  DOCUMENT_TYPE.WORK_ORDER_DAMAGE_PHOTO
];
var DOCUMENT_TYPES_BY_OWNER = {
  // An agreement owns its signed handover document (SWOOP-398) and its signed agreement annex (SWOOP-417).
  agreement: [DOCUMENT_TYPE.AGREEMENT_SIGNED_HANDOVER, DOCUMENT_TYPE.AGREEMENT_SIGNED_ANNEX],
  // A contract owns its quotations (SWOOP-394), the documents generated from its territory's template, its
  // signed copy (SWOOP-395/361), its signed extension addenda (SWOOP-415), its optional VAT exemption
  // certificate (SWOOP-352) and its reliefs' signed delivery notes (SWOOP-399).
  contract: [
    DOCUMENT_TYPE.CONTRACT_QUOTATION,
    DOCUMENT_TYPE.GENERATED_DOCUMENT,
    DOCUMENT_TYPE.CONTRACT_SIGNED_COPY,
    DOCUMENT_TYPE.CONTRACT_SIGNED_EXTENSION,
    DOCUMENT_TYPE.CONTRACT_VAT_CERTIFICATE,
    DOCUMENT_TYPE.RELIEF_SIGNED_NOTE
  ],
  issue: [DOCUMENT_TYPE.ISSUE_PHOTO],
  // Array order is the Document-type picker's option order (SWOOP-245 design): Contract, Price
  // list, Insurance, Trade licence, Other — not alphabetical, same as `vehicle` below.
  supplier: [
    DOCUMENT_TYPE.SUPPLIER_CONTRACT,
    DOCUMENT_TYPE.SUPPLIER_PRICE_LIST,
    DOCUMENT_TYPE.SUPPLIER_INSURANCE,
    DOCUMENT_TYPE.SUPPLIER_TRADE_LICENCE,
    DOCUMENT_TYPE.SUPPLIER_OTHER
  ],
  tenant: [DOCUMENT_TYPE.DOCUMENT_TEMPLATE, DOCUMENT_TYPE.GENERATED_DOCUMENT],
  // A territory owns its contract templates (SWOOP-395).
  territory: [DOCUMENT_TYPE.DOCUMENT_TEMPLATE],
  vehicle: [
    DOCUMENT_TYPE.VEHICLE_REGISTRATION,
    DOCUMENT_TYPE.VEHICLE_INSURANCE,
    DOCUMENT_TYPE.VEHICLE_PHOTOS,
    DOCUMENT_TYPE.VEHICLE_EXPENSE,
    DOCUMENT_TYPE.VEHICLE_OTHERS
  ],
  // A work order owns the flat WORK_ORDER_DOCUMENT files (SWOOP-254: quotes, reports, photos and copied
  // issue photos, distinguished by sourceIssueId) plus the per-damage WORK_ORDER_DAMAGE_PHOTO photos
  // (SWOOP-335, tied to an embedded damage by damageId, written only through the damage endpoints).
  work_order: [DOCUMENT_TYPE.WORK_ORDER_DOCUMENT, DOCUMENT_TYPE.WORK_ORDER_DAMAGE_PHOTO]
};

// src/documents/storage-folder.ts
var STORAGE_FOLDER = {
  /**
   * Signed handover PDFs uploaded against an agreement (SWOOP-398), nested per agreement
   * (`agreements/{agreementId}/{uuid}`). Keys are minted only by the agreement handover SAS route, which
   * checks the contract's scope, so the folder is deliberately NOT in {@link UPLOADABLE_STORAGE_FOLDERS}.
   */
  AGREEMENTS: "agreements",
  /**
   * Quotation PDFs uploaded against a contract (SWOOP-394), nested per contract
   * (`contracts/{contractId}/{uuid}`). Keys are minted only by `POST /contracts/:id/quotations/sas`, which
   * checks the contract's scope, so the folder is deliberately NOT in {@link UPLOADABLE_STORAGE_FOLDERS}.
   */
  CONTRACTS: "contracts",
  DOCUMENT_TEMPLATES: "document-templates",
  /** Photos attached to a reported issue (SWOOP-255). */
  ISSUES: "issues",
  /**
   * PDFs rendered by the generation step (SWOOP-269). Written by the SERVER, never through an upload
   * SAS — the bytes are produced inside the function, so nothing client-side ever needs a key under
   * this folder. Kept OUT of {@link UPLOADABLE_STORAGE_FOLDERS}, so the upload-SAS endpoint refuses
   * it rather than relying on no client ever asking.
   */
  GENERATED_DOCUMENTS: "generated-documents",
  /** Files carried by a magic link — sent with it, or returned by the recipient (SWOOP-271). */
  MAGIC_LINKS: "magic-links",
  /** Paperwork uploaded against a garage supplier (SWOOP-245). */
  SUPPLIERS: "suppliers",
  VEHICLES: "vehicles",
  /** Files uploaded against a work order (SWOOP-254): quotes, reports and photos. */
  WORK_ORDERS: "work-orders"
};
var STORAGE_FOLDERS = Object.values(STORAGE_FOLDER);
var UPLOADABLE_STORAGE_FOLDERS = [
  STORAGE_FOLDER.DOCUMENT_TEMPLATES,
  // The Report issue drawer uploads its photos straight to blob storage via a SAS key (SWOOP-255).
  STORAGE_FOLDER.ISSUES,
  // The garage Documents block uploads straight to blob storage via a SAS key (SWOOP-245).
  STORAGE_FOLDER.SUPPLIERS,
  STORAGE_FOLDER.VEHICLES,
  // The work order Documents tab uploads straight to blob storage via a SAS key (SWOOP-254).
  STORAGE_FOLDER.WORK_ORDERS
];

// src/documents/document-template-error.ts
var DOCUMENT_TEMPLATE_ERROR_CODE = {
  /** The bytes carry the zip magic but cannot be unzipped or decoded — a truncated/damaged package. */
  CORRUPT: "BadRequest.DocumentTemplateCorrupt",
  /** A live template with the same name already exists for this tenant. */
  DUPLICATE_NAME: "Conflict.DuplicateDocumentTemplateName",
  /** The uploaded file is not a DOCX (bad zip magic, or no `word/document.xml`). */
  NOT_DOCX: "BadRequest.DocumentTemplateNotDocx",
  /**
   * The DOCX parsed, but no placeholder was found in it. A template with nothing to fill cannot serve
   * the generation step, so it is refused at upload rather than failing later with no explanation.
   */
  NO_PLACEHOLDERS: "BadRequest.DocumentTemplateNoPlaceholders",
  /**
   * A repeating-section marker (`{{#name}}` … `{{/name}}`, SWOOP-395) is unbalanced, or does not open and close
   * in the rows of one table. Refused at upload, so a template that could not render its section is never kept.
   */
  SECTION_INVALID: "BadRequest.DocumentTemplateSectionInvalid",
  /** The uploaded blob exceeds `DOCUMENT_TEMPLATE_MAX_FILE_SIZE`. */
  TOO_LARGE: "BadRequest.DocumentTemplateTooLarge",
  /**
   * A submitted field key is not one of this template's detected placeholders (SWOOP-269). Refused
   * rather than ignored: a caller filling `{customer.name}` on a template that has no such field has
   * either the wrong template or a stale form, and silently rendering the value away would produce a
   * document that looks filled and is not.
   */
  UNKNOWN_PLACEHOLDER: "BadRequest.DocumentFieldUnknownPlaceholder",
  /** A submitted field value is not a string, or exceeds `DOCUMENT_FIELD_VALUE_MAX_LENGTH`. */
  VALUE_INVALID: "BadRequest.DocumentFieldValueInvalid",
  /**
   * The DOCX carries active content — macros, an embedded object, a remote template reference, a DDE
   * field, an inline DTD, an unsafe entry name — or inflates far beyond its upload size. ONE code for
   * every such rejection on purpose: which check tripped is logged server-side but never returned, so
   * the endpoint cannot be used as an oracle to tune a payload past the scanner.
   */
  UNSAFE: "BadRequest.DocumentTemplateUnsafe"
};

// src/driver/driver.interface.ts
var DRIVER_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive"
};
var DRIVER_SOURCE = {
  EXISTING_USER: "existing-user",
  NEW_PERSON: "new-person"
};

// src/email/index.ts
var EMAIL_STATUS = {
  Error: "error",
  Pending: "pending",
  Sent: "sent"
};

// src/energy-source/energy-source.interface.ts
var ENERGY_SOURCE_CATEGORY = {
  ALTERNATIVE: "ALTERNATIVE",
  COMBUSTION: "COMBUSTION",
  ELECTRIC: "ELECTRIC",
  HYBRID: "HYBRID",
  OTHER: "OTHER"
};
var PRICE_UNIT = {
  PER_KG: "PER_KG",
  PER_KWH: "PER_KWH",
  PER_LITER: "PER_LITER"
};
var PRICE_COMPONENT = {
  BATTERY: "battery",
  FUEL: "fuel",
  SINGLE: "single"
};

// src/energy-source/energy-source.constants.ts
var ENERGY_SOURCE_IDS = {
  GAS: "e5e5e5e5e5e5e5e5e5e5e501",
  DSL: "e5e5e5e5e5e5e5e5e5e5e502",
  LPG: "e5e5e5e5e5e5e5e5e5e5e503",
  CNG: "e5e5e5e5e5e5e5e5e5e5e504",
  LNG: "e5e5e5e5e5e5e5e5e5e5e505",
  ETH: "e5e5e5e5e5e5e5e5e5e5e506",
  BIO: "e5e5e5e5e5e5e5e5e5e5e507",
  BEV: "e5e5e5e5e5e5e5e5e5e5e508",
  HEV: "e5e5e5e5e5e5e5e5e5e5e509",
  PHEV: "e5e5e5e5e5e5e5e5e5e5e50a",
  FCEV: "e5e5e5e5e5e5e5e5e5e5e50b",
  HYD: "e5e5e5e5e5e5e5e5e5e5e50c",
  SOL: "e5e5e5e5e5e5e5e5e5e5e50d",
  OTHER: "e5e5e5e5e5e5e5e5e5e5e50e"
};
var ENERGY_SOURCES = [
  {
    category: ENERGY_SOURCE_CATEGORY.COMBUSTION,
    id: ENERGY_SOURCE_IDS.GAS,
    description: "Refined petroleum fuel for spark-ignition engines",
    label: { ar: "\u0628\u0646\u0632\u064A\u0646", en: "Gasoline / Petrol", fr: "Essence" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_LITER }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.COMBUSTION,
    id: ENERGY_SOURCE_IDS.DSL,
    description: "Petroleum distillate fuel for compression-ignition engines",
    label: { ar: "\u062F\u064A\u0632\u0644", en: "Diesel", fr: "Diesel" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_LITER }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.COMBUSTION,
    id: ENERGY_SOURCE_IDS.LPG,
    description: "Liquefied petroleum gas (propane/butane blend)",
    label: { ar: "\u063A\u0627\u0632 \u0627\u0644\u0628\u062A\u0631\u0648\u0644 \u0627\u0644\u0645\u0633\u0627\u0644", en: "LPG", fr: "GPL" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_LITER }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.COMBUSTION,
    id: ENERGY_SOURCE_IDS.CNG,
    description: "Compressed natural gas",
    label: { ar: "\u063A\u0627\u0632 \u0637\u0628\u064A\u0639\u064A \u0645\u0636\u063A\u0648\u0637", en: "CNG", fr: "GNC" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_KG }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.COMBUSTION,
    id: ENERGY_SOURCE_IDS.LNG,
    description: "Liquefied natural gas",
    label: { ar: "\u063A\u0627\u0632 \u0637\u0628\u064A\u0639\u064A \u0645\u0633\u0627\u0644", en: "LNG", fr: "GNL" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_KG }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.ALTERNATIVE,
    id: ENERGY_SOURCE_IDS.ETH,
    description: "Ethanol-based biofuel (e.g. E85)",
    label: { ar: "\u0625\u064A\u062B\u0627\u0646\u0648\u0644", en: "Ethanol", fr: "\xC9thanol" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_LITER }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.ALTERNATIVE,
    id: ENERGY_SOURCE_IDS.BIO,
    description: "Renewable diesel from vegetable oils or animal fats",
    label: { ar: "\u062F\u064A\u0632\u0644 \u062D\u064A\u0648\u064A", en: "Biodiesel", fr: "Biodiesel" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_LITER }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.ELECTRIC,
    id: ENERGY_SOURCE_IDS.BEV,
    description: "Fully battery-powered electric drivetrain",
    label: { ar: "\u0643\u0647\u0631\u0628\u0627\u0626\u064A \u0628\u0627\u0644\u0628\u0637\u0627\u0631\u064A\u0629", en: "Battery Electric", fr: "\xC9lectrique \xE0 batterie" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_KWH }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.HYBRID,
    id: ENERGY_SOURCE_IDS.HEV,
    description: "Combined combustion engine and electric motor (non-plug-in)",
    label: { ar: "\u0647\u062C\u064A\u0646 \u0643\u0647\u0631\u0628\u0627\u0626\u064A", en: "Hybrid Electric", fr: "Hybride \xE9lectrique" },
    priceComponents: [
      { component: PRICE_COMPONENT.FUEL, unit: PRICE_UNIT.PER_LITER },
      { component: PRICE_COMPONENT.BATTERY, unit: PRICE_UNIT.PER_KWH }
    ]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.HYBRID,
    id: ENERGY_SOURCE_IDS.PHEV,
    description: "Hybrid with an externally rechargeable battery",
    label: { ar: "\u0647\u062C\u064A\u0646 \u0642\u0627\u0628\u0644 \u0644\u0644\u0634\u062D\u0646", en: "Plug-in Hybrid", fr: "Hybride rechargeable" },
    priceComponents: [
      { component: PRICE_COMPONENT.FUEL, unit: PRICE_UNIT.PER_LITER },
      { component: PRICE_COMPONENT.BATTERY, unit: PRICE_UNIT.PER_KWH }
    ]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.ELECTRIC,
    id: ENERGY_SOURCE_IDS.FCEV,
    description: "Hydrogen fuel-cell electric drivetrain",
    label: { ar: "\u062E\u0644\u064A\u0629 \u0648\u0642\u0648\u062F \u0647\u064A\u062F\u0631\u0648\u062C\u064A\u0646\u064A\u0629", en: "Hydrogen Fuel Cell", fr: "Pile \xE0 combustible \xE0 hydrog\xE8ne" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_KG }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.ALTERNATIVE,
    id: ENERGY_SOURCE_IDS.HYD,
    description: "Hydrogen burned in an internal-combustion engine",
    label: { ar: "\u0627\u062D\u062A\u0631\u0627\u0642 \u0627\u0644\u0647\u064A\u062F\u0631\u0648\u062C\u064A\u0646", en: "Hydrogen Combustion", fr: "Combustion d'hydrog\xE8ne" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_KG }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.ALTERNATIVE,
    id: ENERGY_SOURCE_IDS.SOL,
    description: "Solar-powered propulsion or charging",
    label: { ar: "\u0637\u0627\u0642\u0629 \u0634\u0645\u0633\u064A\u0629", en: "Solar", fr: "Solaire" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_KWH }]
  },
  {
    category: ENERGY_SOURCE_CATEGORY.OTHER,
    id: ENERGY_SOURCE_IDS.OTHER,
    description: "Unspecified or custom energy source",
    label: { ar: "\u0623\u062E\u0631\u0649", en: "Other", fr: "Autre" },
    priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_LITER }]
  }
];

// src/expense/expense.interface.ts
var EXPENSE_LOGGED_VIA = {
  MOBILE: "mobile",
  WEB: "web"
};

// src/expense-category/expense-category.interface.ts
var EXPENSE_CATEGORY_FAMILY = {
  ADMINISTRATIVE: "administrative",
  OPERATIONAL: "operational"
};
var EXPENSE_CATEGORY_ORIGIN = {
  CUSTOM: "custom",
  PREDEFINED: "predefined"
};
var EXPENSE_CATEGORY_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive"
};
var EXPENSE_CATEGORY_DEFAULTS = [
  // ── Administrative ──────────────────────────────────────────────────────
  { code: "INSURANCE", family: EXPENSE_CATEGORY_FAMILY.ADMINISTRATIVE, label: "Insurance" },
  { code: "ROAD_TAX", family: EXPENSE_CATEGORY_FAMILY.ADMINISTRATIVE, label: "Road Tax" },
  { code: "REGISTRATION_PLATE", family: EXPENSE_CATEGORY_FAMILY.ADMINISTRATIVE, label: "Registration Plate" },
  { code: "FINE", family: EXPENSE_CATEGORY_FAMILY.ADMINISTRATIVE, label: "Fine" },
  { code: "CUSTOMS_CLEARANCE", family: EXPENSE_CATEGORY_FAMILY.ADMINISTRATIVE, label: "Customs Clearance" },
  // ── Operational ─────────────────────────────────────────────────────────
  { code: "FUEL_ENERGY", family: EXPENSE_CATEGORY_FAMILY.OPERATIONAL, label: "Fuel / Energy" },
  { code: "TOLL", family: EXPENSE_CATEGORY_FAMILY.OPERATIONAL, label: "Toll" },
  { code: "PARKING", family: EXPENSE_CATEGORY_FAMILY.OPERATIONAL, label: "Parking" },
  { code: "CAR_WASH", family: EXPENSE_CATEGORY_FAMILY.OPERATIONAL, label: "Car Wash" },
  { code: "MAINTENANCE", family: EXPENSE_CATEGORY_FAMILY.OPERATIONAL, label: "Maintenance" },
  { code: "REPAIR", family: EXPENSE_CATEGORY_FAMILY.OPERATIONAL, label: "Repair" },
  { code: "DRIVER_ALLOWANCE", family: EXPENSE_CATEGORY_FAMILY.OPERATIONAL, label: "Driver Allowance" }
];

// src/global.configuration.ts
var HetivoGlobalConfiguration = {
  auth: {
    internalEmailDomains: ["@hetivo.com", "@hetivo.internal", "@felixsportelli.onmicrosoft.com"]
  }
};

// src/inspection/inspection.interface.ts
var INSPECTION_STATUS = { COMPLETED: "completed", DUE: "due", IN_PROGRESS: "in_progress" };
var INSPECTION_STATUSES = Object.values(INSPECTION_STATUS);
var INSPECTION_SOURCE = { ON_DEMAND: "on_demand", SCHEDULED: "scheduled" };

// src/inspection/inspection-list.interface.ts
var INSPECTION_SEARCH_MAX_LENGTH = 100;

// src/inspection-template/inspection-template.interface.ts
var INSPECTION_ITEM_KIND = { PASS_FAIL: "pass_fail", SCORE: "score" };
var INSPECTION_SCHEDULE = {
  ON_DEMAND: "on_demand",
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  EVERY_3_MONTHS: "every_3_months",
  EVERY_6_MONTHS: "every_6_months",
  YEARLY: "yearly"
};
var INSPECTION_TEMPLATE_LIMITS = {
  labelMax: 200,
  maxItemsPerSection: 100,
  maxScoreMax: 100,
  maxScoreMin: 1,
  maxSections: 30,
  nameMax: 120
};

// src/vehicle/vehicle.interface.ts
var VEHICLE_STATUS = {
  ACTIVE: "ACTIVE",
  /**
   * Retired from the fleet (SWOOP-183) — sold, written off, declared a total loss or returned to its
   * lessor. Set ONLY by `POST /vehicles/:id/archive`, never by a PATCH: it is deliberately absent from
   * `assertValidStatusTransition`, like UNDER_MAINTENANCE, so the blocking rules (no open allocation,
   * not under maintenance) cannot be bypassed by editing the Identity form.
   *
   * An archived vehicle is NOT deleted: `deletedAt` stays null so the whole profile keeps reading
   * through the normal scoped paths (seven tabs, documents, photos, change log) and Restore is an
   * ordinary update. This deviates from the `hetivo-persistence` skill's `deletedAt` archive pattern,
   * which is written for master-list entities that have no browsable profile — see the SWOOP-183 PR.
   */
  ARCHIVED: "ARCHIVED",
  DRAFT: "DRAFT",
  INACTIVE: "INACTIVE",
  /**
   * Lent out as a relief vehicle against a waiting leasing agreement (SWOOP-399) — the vehicle is with
   * a customer while its ordered vehicle is late. System-owned: set ONLY by
   * `VehicleService.markOnRelief` and cleared ONLY by `VehicleService.releaseFromRelief`, each a
   * status-guarded compare-and-set, never by a PATCH. Like ARCHIVED and UNDER_MAINTENANCE it is
   * deliberately absent from the allowed `assertValidStatusTransition` moves, so the blocking rules
   * (no archive/disposal, no allocation, no work order while lent) cannot be bypassed by editing the
   * Identity form; unlike ARCHIVED, a move INTO it is refused after the no-op rule, so an ON_RELIEF
   * vehicle stays editable and its Identity save (which echoes the unchanged status) still passes.
   */
  ON_RELIEF: "ON_RELIEF",
  // A vehicle temporarily out of operation (e.g. in the workshop). Backs the SWOOP-100 "In Service"
  // tab / KPI card and the status quick-filter option.
  UNDER_MAINTENANCE: "UNDER_MAINTENANCE"
};
var VEHICLE_ALLOCATION_STATUS = {
  /** The vehicle HAS a currently-open allocation. */
  ALLOCATED: "allocated",
  /** The vehicle has NO currently-open allocation. */
  AVAILABLE: "available"
};
var VEHICLE_TRANSMISSION = {
  AUTOMATIC: "AUTOMATIC",
  CVT: "CVT",
  MANUAL: "MANUAL"
};
var VEHICLE_ACQUISITION_TYPE = {
  BUYBACK: "BUYBACK",
  LEASE: "LEASE",
  LOAN_CREDIT_PURCHASE: "LOAN_CREDIT_PURCHASE",
  PURCHASE: "PURCHASE",
  SUBLEASE: "SUBLEASE"
};
var VEHICLE_END_OF_LEASE_OPTION = {
  EXTEND_CONTRACT: "EXTEND_CONTRACT",
  PURCHASE_OPTION: "PURCHASE_OPTION",
  RETURN_TO_LESSOR: "RETURN_TO_LESSOR"
};
var VEHICLE_RETIREMENT_REASON = {
  RENTAL_RETURN: "RENTAL_RETURN",
  SOLD: "SOLD",
  TOTAL_LOSS: "TOTAL_LOSS",
  WRITE_OFF: "WRITE_OFF"
};
var VEHICLE_ENERGY_LEVEL = {
  EMPTY: 0,
  FULL: 100,
  HALF: 50,
  QUARTER: 25,
  THREE_QUARTERS: 75
};

// src/inspection/inspection-schedule.ts
var INTERVAL_STEPS = {
  [INSPECTION_SCHEDULE.WEEKLY]: { days: 7 },
  [INSPECTION_SCHEDULE.MONTHLY]: { months: 1 },
  [INSPECTION_SCHEDULE.EVERY_3_MONTHS]: { months: 3 },
  [INSPECTION_SCHEDULE.EVERY_6_MONTHS]: { months: 6 },
  [INSPECTION_SCHEDULE.YEARLY]: { months: 12 }
};
function fallsDueOn(schedule, lastCompletedOn, today) {
  if (schedule === INSPECTION_SCHEDULE.ON_DEMAND) {
    return null;
  }
  if (schedule === INSPECTION_SCHEDULE.DAILY) {
    return lastCompletedOn?.getTime() === today.getTime() ? null : today;
  }
  if (lastCompletedOn == null) {
    return today;
  }
  const step = INTERVAL_STEPS[schedule];
  return "days" in step ? addDays(lastCompletedOn, step.days) : addMonthsUtc(lastCompletedOn, step.months);
}
function isDueBy(dueOn, today) {
  return dueOn !== null && dueOn.getTime() <= today.getTime();
}
function isInspectionFleetVehicle(vehicle) {
  return vehicle.deletedAt == null && vehicle.status !== VEHICLE_STATUS.DRAFT && vehicle.status !== VEHICLE_STATUS.ARCHIVED;
}

// src/insurance-package/insurance-package.interface.ts
var INSURANCE_PACKAGE_STATUS = { active: "active", retired: "retired" };
var INSURANCE_PACKAGE_LIMITS = {
  coverMax: 500,
  nameMax: 60,
  rateDecimals: 2
};

// src/invoicing/document-category-registry.ts
var DOCUMENT_CATEGORY_REGISTRY = {
  [DOCUMENT_CATEGORY.ADJUSTMENT]: { code: DOCUMENT_CODE.AJ },
  [DOCUMENT_CATEGORY.DAMAGE]: { code: DOCUMENT_CODE.DM },
  [DOCUMENT_CATEGORY.GENERAL]: { code: DOCUMENT_CODE.BC },
  [DOCUMENT_CATEGORY.LEASE]: { code: DOCUMENT_CODE.BC }
};
function documentCodeFor(category, invoiceType) {
  return invoiceType === INVOICE_TYPE.CREDIT_NOTE ? DOCUMENT_CODE.CR : DOCUMENT_CATEGORY_REGISTRY[category].code;
}

// src/territory/territory.interface.ts
var DEFAULT_TERRITORY_VAT_RATE = 11;
var TERRITORY_CURRENCY = {
  EUR: "EUR",
  GBP: "GBP",
  LBP: "LBP",
  USD: "USD"
};
var TERRITORY_LANGUAGE = {
  EN: "en",
  FR: "fr"
};
var TERRITORY_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive"
};
var TERRITORY_ZONE_TYPE = {
  COUNTRY: "country"
};

// src/invoicing/tenant-billing-settings.interface.ts
var DOCUMENT_LANGUAGE = {
  AR: "ar",
  EN: "en",
  FR: "fr"
};
var BILLING_EXCHANGE_RATE_SOURCE_MAX_LENGTH = 200;
var BILLING_EXCHANGE_RATES_ADDED_PER_SAVE_MAX = 50;
var BILLING_EXCHANGE_RATE_CURRENCIES = Object.values(TERRITORY_CURRENCY);

// src/invoicing/invoice-overdue.ts
var NOT_OVERDUE = { daysOverdue: null, overdue: false };
function invoiceOverdue(invoice, now, timeZone) {
  if (invoice.legalStatus !== LEGAL_STATUS.ISSUED || invoice.dueDate === null || invoice.amountOutstanding === null || !(Number(invoice.amountOutstanding) > 0)) {
    return NOT_OVERDUE;
  }
  const lastGraceDay = toCalendarDay(invoice.dueDate, timeZone).getTime() + invoice.gracePeriodDays * MS_PER_DAY;
  const daysOverdue = Math.round((toCalendarDay(now, timeZone).getTime() - lastGraceDay) / MS_PER_DAY);
  return daysOverdue > 0 ? { daysOverdue, overdue: true } : NOT_OVERDUE;
}

// src/invoicing/invoice-number.ts
var INVOICE_NUMBER_MAX_SEQUENCE = 999999;
var INVOICE_NUMBER_PATTERN = /^([A-Z]{2})\/(\d{4})\/(\d{6})$/;
var NUMBER_CODE_PATTERN = /^[A-Z]{2}$/;
function isDocumentCode(value) {
  return Object.values(DOCUMENT_CODE).includes(value);
}
function formatInvoiceNumber({ code, sequence, year }) {
  if (!NUMBER_CODE_PATTERN.test(code)) {
    throw new RangeError(`Invoice number code must be two upper-case letters, got ${code}`);
  }
  if (!Number.isInteger(sequence) || sequence < 1 || sequence > INVOICE_NUMBER_MAX_SEQUENCE) {
    throw new RangeError(
      `Invoice sequence must be an integer from 1 to ${INVOICE_NUMBER_MAX_SEQUENCE}, got ${sequence}`
    );
  }
  if (!Number.isInteger(year) || year < 1e3 || year > 9999) {
    throw new RangeError(`Invoice year must be a four-digit integer, got ${year}`);
  }
  return `${code}/${year}/${String(sequence).padStart(6, "0")}`;
}
function parseDocumentNumber(value) {
  const match = INVOICE_NUMBER_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const [, code, year, sequence] = match;
  return { code, sequence: Number(sequence), year: Number(year) };
}
function parseInvoiceNumber(value) {
  const parts = parseDocumentNumber(value);
  if (parts === null || !isDocumentCode(parts.code)) {
    return null;
  }
  return { code: parts.code, sequence: parts.sequence, year: parts.year };
}
function normalizeInvoiceNumber(value) {
  return value.replace(/\s+/g, "").toUpperCase();
}
function invoiceCounterKey({
  code,
  territoryId,
  year
}) {
  return `invoice:${territoryId}:${code}:${year}`;
}

// src/invoicing/money.ts
var DECIMAL_STRING = /^-?\d+(\.\d+)?$/;
function isDecimalString(value) {
  return typeof value === "string" && DECIMAL_STRING.test(value);
}

// src/invitation/invitation.interface.ts
var INVITATION_EXPIRY_DAYS = 3;
var INVITATION_EXPIRY_DAYS_IN_MS = INVITATION_EXPIRY_DAYS * MS_PER_DAY;
var INVITATION_CHANNEL = {
  EMAIL: "email",
  SMS: "sms",
  WHATSAPP: "whatsapp"
};
var INVITATION_STATUS = {
  ACCEPTED: "accepted",
  CANCELLED: "cancelled",
  CREATED: "created",
  EXPIRED: "expired",
  PENDING: "pending"
};
var INVITATION_STATUS_TRANSITIONS = {
  [INVITATION_STATUS.ACCEPTED]: [],
  [INVITATION_STATUS.CANCELLED]: [INVITATION_STATUS.PENDING],
  [INVITATION_STATUS.CREATED]: [INVITATION_STATUS.CANCELLED, INVITATION_STATUS.EXPIRED, INVITATION_STATUS.PENDING],
  [INVITATION_STATUS.EXPIRED]: [INVITATION_STATUS.PENDING],
  [INVITATION_STATUS.PENDING]: [INVITATION_STATUS.ACCEPTED, INVITATION_STATUS.CANCELLED, INVITATION_STATUS.EXPIRED]
};

// src/issue/issue.interface.ts
var ISSUE_TYPE = {
  FAULT: "FAULT",
  BREAKDOWN: "BREAKDOWN",
  RECALL: "RECALL",
  DAMAGE: "DAMAGE"
};
var ISSUE_TYPES = Object.values(ISSUE_TYPE);
var ISSUE_STATUS = {
  OPEN: "OPEN",
  IN_PROGRESS: "IN_PROGRESS",
  RESOLVED: "RESOLVED"
};
var ISSUE_STATUSES = Object.values(ISSUE_STATUS);
var ISSUE_TIMELINE_KIND = {
  REPORTED: "REPORTED",
  CREATED_WITH_WORK_ORDER: "CREATED_WITH_WORK_ORDER",
  STATUS_CHANGED: "STATUS_CHANGED",
  COMMENT: "COMMENT",
  WORK_ORDER_OPENED: "WORK_ORDER_OPENED",
  RESOLVED_BY_WORK_ORDER: "RESOLVED_BY_WORK_ORDER"
};
var ISSUE_TIMELINE_KINDS = Object.values(ISSUE_TIMELINE_KIND);

// src/work-order/work-order.interface.ts
var WORK_ORDER_STATUS = {
  AT_GARAGE: "AT_GARAGE",
  CANCELLED: "CANCELLED",
  CLOSED: "CLOSED",
  DONE: "DONE",
  OPEN: "OPEN",
  RECEIVED: "RECEIVED"
};
var WORK_ORDER_OPEN_STATUSES = [
  WORK_ORDER_STATUS.OPEN,
  WORK_ORDER_STATUS.RECEIVED,
  WORK_ORDER_STATUS.AT_GARAGE,
  WORK_ORDER_STATUS.DONE
];
function isWorkOrderOpen(status) {
  return WORK_ORDER_OPEN_STATUSES.includes(status);
}
var WORK_ORDER_TEXT_MAX = 1e3;
var WORK_ORDER_SOURCE = {
  REMINDER: "reminder"
};
var WORK_ORDER_TYPE = {
  NOT_SCHEDULED: "NOT_SCHEDULED",
  SCHEDULED: "SCHEDULED"
};
var WORK_ORDER_NOT_SCHEDULED_REASON = {
  CORRECTIVE: "CORRECTIVE",
  DAMAGE: "DAMAGE",
  PREVENTIVE: "PREVENTIVE"
};
var WORK_ORDER_ITEM_REASON = {
  CORRECTIVE: "CORRECTIVE",
  DAMAGE: "DAMAGE",
  PREVENTIVE: "PREVENTIVE"
};
var WORK_ORDER_ITEM_SOURCE = {
  ADDITIONAL_POINT: "additional_point",
  INTERVAL: "interval"
};
var WORK_ORDER_TIMELINE_EVENT = {
  CANCELLED: "CANCELLED",
  CLOSED: "CLOSED",
  /** A document was deleted from the work order (SWOOP-335, doc Q15). Appended without a status change. */
  DOCUMENT_DELETED: "DOCUMENT_DELETED",
  /** A document was uploaded to the work order (SWOOP-254). Appended without a status change. */
  DOCUMENT_UPLOADED: "DOCUMENT_UPLOADED",
  MARKED_DONE: "MARKED_DONE",
  OPENED: "OPENED",
  RECEIVED: "RECEIVED",
  SENT_TO_GARAGE: "SENT_TO_GARAGE"
};
var DAMAGE_RESPONSIBILITY = {
  CUSTOMER: "CUSTOMER",
  OUR_DRIVER: "OUR_DRIVER",
  THIRD_PARTY: "THIRD_PARTY",
  UNKNOWN: "UNKNOWN"
};
var DAMAGE_NATURE = {
  BODY: "BODY",
  MECHANICAL: "MECHANICAL"
};
var VEHICLE_SECTION = {
  AIR_CONDITIONING: "air_conditioning",
  BODY: "body",
  BRAKES: "brakes",
  ELECTRICAL: "electrical",
  ENGINE: "engine",
  EXHAUST: "exhaust",
  INTERIOR: "interior",
  OTHER: "other",
  SUSPENSION_STEERING: "suspension_steering",
  TRANSMISSION: "transmission",
  TYRES_WHEELS: "tyres_wheels"
};
var VEHICLE_SECTIONS = Object.values(VEHICLE_SECTION);
function resolveVehicleStatusAtOpening(workOrder) {
  return workOrder.vehicleStatusAtOpening ?? VEHICLE_STATUS.ACTIVE;
}
function deriveWorkOrderTimeline(workOrder) {
  const stored = workOrder.timeline ?? [];
  if (stored.some((entry) => entry.type === WORK_ORDER_TIMELINE_EVENT.OPENED)) {
    return stored;
  }
  const opened = {
    at: workOrder.createdAt,
    by: workOrder.openedBy,
    details: null,
    type: WORK_ORDER_TIMELINE_EVENT.OPENED
  };
  return [opened, ...stored];
}

// src/issue/issue-rules.ts
var ISSUE_TARGET_HOURS = 72;
var ISSUE_TITLE_MAX = 120;
var ISSUE_TEXT_MAX = 2e3;
var ISSUE_NUMBER_PREFIX = "ISS-";
var MS_PER_HOUR = 60 * 60 * 1e3;
var ISSUE_TRANSITIONS = {
  reopen: { from: ISSUE_STATUS.RESOLVED, to: ISSUE_STATUS.IN_PROGRESS },
  resolve: { from: ISSUE_STATUS.IN_PROGRESS, to: ISSUE_STATUS.RESOLVED },
  start: { from: ISSUE_STATUS.OPEN, to: ISSUE_STATUS.IN_PROGRESS }
};
function formatIssueNumber(issueNumber) {
  return `${ISSUE_NUMBER_PREFIX}${issueNumber}`;
}
function parseIssueNumber(text) {
  const match = /^(?:iss-)?(\d+)$/i.exec(text.trim());
  if (!match) {
    return null;
  }
  const value = Number(match[1]);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}
function issueAgeHours(issue, now) {
  const end = issue.resolvedAt ?? now;
  return Math.max(0, (new Date(end).getTime() - new Date(issue.reportedAt).getTime()) / MS_PER_HOUR);
}
function isIssuePastTarget(issue, now) {
  return issue.status !== ISSUE_STATUS.RESOLVED && issueAgeHours(issue, now) > ISSUE_TARGET_HOURS;
}
function isIssueResolvedWithinTarget(issue) {
  return issue.resolvedAt != null && issueAgeHours(issue, new Date(issue.resolvedAt)) <= ISSUE_TARGET_HOURS;
}
var WORK_ORDER_REASON_BY_ISSUE_TYPE = {
  [ISSUE_TYPE.BREAKDOWN]: WORK_ORDER_NOT_SCHEDULED_REASON.CORRECTIVE,
  [ISSUE_TYPE.DAMAGE]: WORK_ORDER_NOT_SCHEDULED_REASON.DAMAGE,
  [ISSUE_TYPE.FAULT]: WORK_ORDER_NOT_SCHEDULED_REASON.CORRECTIVE,
  [ISSUE_TYPE.RECALL]: WORK_ORDER_NOT_SCHEDULED_REASON.CORRECTIVE
};
var LINKABLE_ISSUE_STATUSES = [ISSUE_STATUS.OPEN, ISSUE_STATUS.IN_PROGRESS];
function canOpenWorkOrder(issue) {
  return LINKABLE_ISSUE_STATUSES.includes(issue.status) && !issue.workOrderId;
}

// src/jwt.ts
var JWT_KIND = {
  internal: "internal",
  service: "service",
  user: "user"
};
var MAX_REFRESH_SESSION_MS = 30 * 24 * 60 * 60 * 1e3;

// src/location/location.interface.ts
var LOCATION_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE"
};
var DIVISION_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE"
};

// src/magic-link/magic-link.interface.ts
var MAGIC_LINK_PURPOSE = {
  /** SWOOP-271: a customer accepts or refuses one generated document. */
  DOCUMENT_REVIEW: "document-review"
};
var MAGIC_LINK_DEFAULT_EXPIRY_HOURS = 24;
var MAGIC_LINK_DEFAULT_EXPIRY_IN_MS = MAGIC_LINK_DEFAULT_EXPIRY_HOURS * 60 * 60 * 1e3;
var MAGIC_LINK_RESPONSE_MAX_LENGTH = 8e3;
var MAGIC_LINK_COMMENT_MAX_LENGTH = 2e3;
var MAGIC_LINK_PURPOSES = {
  [MAGIC_LINK_PURPOSE.DOCUMENT_REVIEW]: {
    deprecated: false,
    expectsResponse: true,
    expiryInMs: MAGIC_LINK_DEFAULT_EXPIRY_IN_MS,
    referencePrefix: "REV"
  }
};
var MAGIC_LINK_SUBJECT_TYPE = {
  /**
   * The `documents` row holding the DOCX template a review was issued against (SWOOP-272).
   *
   * **An interim anchor, and deliberately the template rather than the generated PDF.** The PDF is the
   * more faithful subject — a request is about one version of one document, which is what SWOOP-270
   * will bind it to — but it is not reachable from the test screen: every Generate writes a NEW
   * `GENERATED_DOCUMENT` row, no route lists those rows, and after a reload the page holds no id for
   * one. The in-use template is already loaded and already marked server-side (`isSelected`), so the
   * panel can find its requests again across a refresh without a new read.
   *
   * What it does NOT weaken: the version the panel names still comes from the attachment snapshot
   * taken at issue, never from the template — so "the response refers to the exact version sent"
   * holds regardless of which row the subject points at.
   */
  DOCUMENT_TEMPLATE: "document-template",
  /**
   * The `documents` row holding the generated PDF a review was issued against (SWOOP-270).
   *
   * **The real anchor, and what {@link MAGIC_LINK_SUBJECT_TYPE.DOCUMENT_TEMPLATE} was standing in for.**
   * 270's AC binds a request to "the exact document version generated", and a `GENERATED_DOCUMENT` row
   * IS that version: `generateFromTemplate` writes a new row with a new `_id` per generation and never
   * overwrites one, so the row id identifies the version exactly — which is also why no `version` field
   * exists on `Document` (product removed version tracking on 8 Sep 2026).
   *
   * Two acceptance criteria fall out of this choice rather than being coded:
   * - _"Send is disabled once a request exists for the document"_ — `findBySubject` on this id;
   * - _"regenerating clears any unsent request"_ (SWOOP-269) — a regeneration has a NEW id, so the
   *   lookup finds nothing and Send re-enables with no reset action involved.
   *
   * `DOCUMENT_TEMPLATE` is kept rather than replaced: 272 issued live rows under it, and an open set
   * does not rewrite its own history. Nothing new should be issued against it.
   */
  GENERATED_DOCUMENT: "generated-document",
  /** No business object yet: the SWOOP-271 test screen issues links against a generated sample. */
  NONE: "none"
};
var MAGIC_LINK_CHANNEL = {
  EMAIL: "email",
  SMS: "sms",
  WHATSAPP: "whatsapp"
};
function resolveMagicLinkState(link) {
  if (link.respondedAt) {
    return "answered";
  }
  if (link.revokedAt) {
    return "revoked";
  }
  return link.expiresAt.getTime() <= Date.now() ? "expired" : "awaiting";
}
function resolveMagicLinkRecipient(link) {
  const last = link.deliveries.at(-1);
  return last?.address ?? null;
}

// src/magic-link/purposes/document-review.ts
var REVIEW_OUTCOME = {
  ACCEPTED: "accepted",
  REFUSED: "refused"
};
var REVIEW_OUTCOMES = [REVIEW_OUTCOME.ACCEPTED, REVIEW_OUTCOME.REFUSED];
function asDocumentReviewPayload(payload) {
  const outcome = payload.outcome;
  if (outcome !== REVIEW_OUTCOME.ACCEPTED && outcome !== REVIEW_OUTCOME.REFUSED) {
    return null;
  }
  const comment = payload.comment;
  if (comment !== null && typeof comment !== "string") {
    return null;
  }
  return { comment, outcome };
}

// src/maintenance-settings/maintenance-settings.interface.ts
var MAINTENANCE_SETTINGS_DEFAULTS = {
  defaultDailyRateKm: 50,
  graceDays: 14,
  graceKm: 500
};
var MAINTENANCE_FEATURE_KEY = {
  GRACE: "maintenance.grace",
  RATE: "maintenance.rate"
};

// src/modules.ts
var MODULE_AREA = {
  Fleet: "fleet",
  Inspections: "inspections",
  Leasing: "leasing",
  Maintenance: "maintenance"
};
var MODULE_REGISTRY = [
  {
    addOns: [
      {
        descriptionKey: "modules.addOns.fleetRegister.description",
        key: "fleet.register",
        surfaces: [
          { kind: "nav", labelKey: "modules.surfaces.vehiclesMenu", path: "/vehicles" },
          { kind: "profile-tab", labelKey: "modules.surfaces.vehicleProfile", path: "/vehicles/$vehicleId" },
          { kind: "settings", labelKey: "modules.surfaces.settingsFleet", path: "/settings/fleet" }
        ]
      }
    ],
    alwaysIncluded: true,
    descriptionKey: "modules.areas.fleet.description",
    key: MODULE_AREA.Fleet
  },
  {
    addOns: [
      {
        descriptionKey: "modules.addOns.maintenanceCore.description",
        key: "maintenance.core",
        surfaces: [
          { kind: "settings", labelKey: "modules.surfaces.maintenanceSettings", path: "/settings/maintenance" },
          {
            kind: "settings",
            labelKey: "modules.surfaces.serviceOperations",
            path: "/settings/maintenance/service-operations"
          },
          // Built in SWOOP-281: the Service reminders screen.
          { kind: "nav", labelKey: "modules.surfaces.reminders", path: "/reminders" },
          // Built in SWOOP-255: the Issues screen.
          { kind: "nav", labelKey: "modules.surfaces.issues", path: "/issues" },
          // Built in SWOOP-325: the Work orders page and its Open-work-order drawer.
          { kind: "nav", labelKey: "modules.surfaces.workOrders", path: "/work-orders" },
          // Built in SWOOP-309: the vehicle profile's Maintenance tab.
          {
            kind: "profile-tab",
            labelKey: "modules.surfaces.vehicleMaintenance",
            path: "/vehicles/$vehicleId/overview/maintenance"
          }
        ]
      }
    ],
    alwaysIncluded: false,
    descriptionKey: "modules.areas.maintenance.description",
    key: MODULE_AREA.Maintenance
  },
  {
    addOns: [
      {
        descriptionKey: "modules.addOns.inspectionsCore.description",
        key: "inspections.core",
        // Ordered as the Back Office Modules card lists them, and exactly the three SWOOP-372 names. SWOOP-378
        // built the Inspections menu (`/inspections`) and Settings > Inspections (`/settings/inspections`) as
        // empty pages, both hidden from a tenant without the module. The vehicle profile's Inspections tab
        // remains SOON and has no route yet, so it declares no path. The Open repairs list (SWOOP-376) is not
        // advertised as a separate surface.
        surfaces: [
          { kind: "nav", labelKey: "modules.surfaces.inspections", path: "/inspections" },
          { kind: "settings", labelKey: "modules.surfaces.inspectionSettings", path: "/settings/inspections" },
          { kind: "profile-tab", labelKey: "modules.surfaces.vehicleInspections", soon: true }
        ]
      }
    ],
    alwaysIncluded: false,
    descriptionKey: "modules.areas.inspections.description",
    key: MODULE_AREA.Inspections
  },
  {
    addOns: [
      {
        descriptionKey: "modules.addOns.leasingCore.description",
        key: "leasing.core",
        // Ordered as the Back Office Modules card lists them. The Leasing menu and Contracts shipped with
        // SWOOP-298; the Leasing tab of Territory Settings with SWOOP-302 (leasing settings are per
        // territory, so it is a territory tab rather than a tenant-wide Settings entry).
        surfaces: [
          { kind: "nav", labelKey: "modules.surfaces.leasingMenu", path: "/leasing" },
          { kind: "nav", labelKey: "modules.surfaces.contracts", path: "/leasing/contracts" },
          {
            kind: "settings",
            labelKey: "modules.surfaces.leasingSettings",
            path: "/settings/territories/$territoryId/leasing"
          }
        ]
      }
    ],
    alwaysIncluded: false,
    descriptionKey: "modules.areas.leasing.description",
    key: MODULE_AREA.Leasing
  }
];
var SURFACE_PATH_AREAS = new Map(
  MODULE_REGISTRY.flatMap(
    (area) => area.addOns.flatMap(
      (addOn) => addOn.surfaces.flatMap((surface) => surface.path === void 0 ? [] : [[surface.path, area.key]])
    )
  )
);
function moduleAreaForPath(path) {
  return SURFACE_PATH_AREAS.get(path);
}

// src/order/order.interface.ts
var TRANSPORT_TYPE = {
  DELIVERY: "delivery",
  PICKUP: "pickup"
};
var ORDER_STATUS = {
  ANOMALY: "anomaly",
  ARRIVED_AT_DELIVERY: "arrived_at_delivery",
  ARRIVED_AT_PICKUP: "arrived_at_pickup",
  CANCELLED: "cancelled",
  CARRIER_ACCEPTED: "carrier_accepted",
  CARRIER_ASSIGNED: "carrier_assigned",
  CARRIER_REJECTED: "carrier_rejected",
  COMPLETED: "completed",
  DRAFT: "draft",
  DRIVER_ACCEPTED: "driver_accepted",
  DRIVER_ASSIGNED: "driver_assigned",
  DRIVING_TO_DELIVERY: "driving_to_delivery",
  DRIVING_TO_PICKUP: "driving_to_pickup",
  FAILED: "failed",
  IN_TRANSIT: "in_transit",
  NEW: "new",
  PICKUP_COMPLETED: "pickup_completed",
  WAITING_FOR_PICKUP: "waiting_for_pickup"
};
var ORDER_STATUS_BUCKET = {
  DONE: [ORDER_STATUS.COMPLETED, ORDER_STATUS.FAILED, ORDER_STATUS.CANCELLED],
  IN_PROGRESS: [
    ORDER_STATUS.ARRIVED_AT_PICKUP,
    ORDER_STATUS.WAITING_FOR_PICKUP,
    ORDER_STATUS.PICKUP_COMPLETED,
    ORDER_STATUS.IN_TRANSIT,
    ORDER_STATUS.DRIVING_TO_DELIVERY,
    ORDER_STATUS.ARRIVED_AT_DELIVERY
  ],
  TODO: [
    ORDER_STATUS.ANOMALY,
    ORDER_STATUS.DRAFT,
    ORDER_STATUS.NEW,
    ORDER_STATUS.CARRIER_ASSIGNED,
    ORDER_STATUS.DRIVER_ASSIGNED,
    ORDER_STATUS.CARRIER_ACCEPTED,
    ORDER_STATUS.DRIVER_ACCEPTED,
    ORDER_STATUS.CARRIER_REJECTED,
    ORDER_STATUS.DRIVING_TO_PICKUP
  ]
};

// src/permissions/built-in.ts
var BUILT_IN_ROLE_KEY = {
  ACCOUNT_MANAGER: "ACCOUNT_MANAGER",
  // SWOOP-430: the Billing Officer runs invoicing for a territory or a whole tenant.
  BILLING_OFFICER: "BILLING_OFFICER",
  DRIVER: "DRIVER",
  FLEET_MANAGER: "FLEET_MANAGER",
  INSPECTOR: "INSPECTOR",
  LOCATION_MANAGER: "LOCATION_MANAGER",
  TENANT_ADMIN: "TENANT_ADMIN",
  TERRITORY_ADMIN: "TERRITORY_ADMIN"
};
var builtInRoles = {
  [BUILT_IN_ROLE_KEY.ACCOUNT_MANAGER]: {
    permissions: [
      // SWOOP-414: modify an On lease agreement's monthly price, extra-km price or end date. Held by the
      // Account Manager and the Tenant Admin only — deliberately NOT by the Fleet Manager or the Territory
      // Admin, who hold the other agreement grants. The over-limit exception (`agreement-terms-over-limit:manage`,
      // a price or term change above 20 %) is the Tenant Admin's alone, so it is NOT granted here.
      "agreement-terms:manage",
      // SWOOP-366/398: print the Lease Delivery Note and upload/replace the signed handover copy. Assigning a
      // vehicle and recording the handover are Fleet Manager only (`agreement-assignments:manage`).
      "agreements:list",
      // SWOOP-399: manage relief vehicles on agreements; reading follows contracts:list.
      "agreements:manage",
      "backoffice:access",
      // SWOOP-322: ACCOUNT_MANAGER is a staff role that is also given Front Office access plus the
      // customer permissions, so it can draft contracts (starting a contract means finding or
      // creating a customer). Its Leasing surfaces stay behind the Leasing module.
      // SWOOP-345: `contracts:list` gates the Contracts list, its menu link and address, and reading one
      // contract — granted to the four roles that can reach Leasing Contracts (not LOCATION_MANAGER).
      // SWOOP-397: duplicating a master contract as a new draft is its own write grant, deliberately NOT
      // folded into `contracts:manage` — the AC limits Duplicate to the Account Manager and the Tenant Admin,
      // whereas `contracts:manage` is also held by the Territory Admin. Granted here and on TENANT_ADMIN only.
      "contract-duplicates:manage",
      // SWOOP-416: generating a stamp duty invoice request on a signed contract is its own write grant, the same
      // audience as `contract-duplicates:manage` (SWOOP-397 precedent): Account Manager and Tenant Admin only, NOT
      // the Territory Admin who holds `contracts:manage`. Granted here and on TENANT_ADMIN only.
      "contract-fee-rebills:manage",
      // SWOOP-419: add, edit and delete a contract's or an agreement's customer PO numbers. Its own write grant,
      // deliberately NOT folded into `contracts:manage` — the AC limits it to the Account Manager and the Tenant
      // Admin (the `contract-duplicates:manage` precedent), whereas `contracts:manage` is also held by the
      // Territory Admin, who sees POs read-only. Reading POs follows `contracts:list`.
      "contract-po:manage",
      "contracts:list",
      "contracts:manage",
      "customers:list",
      "customers:manage",
      "frontoffice:access",
      "invitations:manage",
      "invitations:read",
      // SWOOP-339: read-only here — see the TENANT_ADMIN block.
      "leasing-settings:list",
      // SWOOP-295: the single Leasing permission — who sees the Leasing module once it is enabled for
      // the tenant. Granted to every web role (not DRIVER); it has no effect while the module is off.
      // Later leasing epics add their own codes rather than widening this one.
      "leasing:access",
      "permissions:read",
      "roles:manage",
      "roles:read",
      "tenant-defaults:read",
      // SWOOP-261 / PR #129 review: module entitlement is a STAFF grant, deliberately NOT folded into
      // `tenants:manage`. TENANT_ADMIN holds `tenants:manage` (below) but has no `backoffice:access`,
      // so reusing it would have let a tenant admin call
      // `PATCH /tenants/<their-own-id>/modules/maintenance` and self-grant a paid module. Only
      // ACCOUNT_MANAGER may carry this code.
      "tenant-modules:manage",
      "tenants:initialize",
      "tenants:manage",
      "tenants:read",
      "territories:edit",
      "territories:list",
      "territories:manage",
      "tracking:read",
      "users:list",
      "users:manage"
    ]
  },
  // SWOOP-430: the Billing Officer owns a territory's (or a whole tenant's) invoicing — the Billing menu and
  // the Territory › Billing settings tab. A standalone role, stacked on whatever else a user holds, so it
  // carries ONLY what billing needs. `billing:list`/`:manage` gate the Billing menu and its actions; the Tenant
  // Admin and the Territory Admin (own territory) hold them too, and no other built-in role does (SWOOP-430, ticket
  // update of 4 Nov 2026). `billing-settings:list`/`:manage` gate the Territory › Billing defaults tab, also held by
  // Tenant Admin and Territory Admin. `territories:list` lets it reach Settings › Territories and
  // the per-territory detail page (`canAccessTerritorySettings`); it is NOT given `locations:list`, so it cannot
  // open the location settings page. Deliberately NO `leasing:access` — a billing officer has no business in
  // Leasing. NOT bound to a module (`ROLE_REQUIRED_MODULE`): the Billing surfaces are not module-gated.
  [BUILT_IN_ROLE_KEY.BILLING_OFFICER]: {
    permissions: [
      "billing-settings:list",
      "billing-settings:manage",
      "billing:list",
      "billing:manage",
      "frontoffice:access",
      "territories:list"
    ]
  },
  // SWOOP-200: DRIVER is a mobile-only built-in role — deliberately no frontoffice:access
  // or backoffice:access. Mobile gates on `mobile:access` (apps/hetivo-mobile-driver/src/
  // app/(authenticated)/_layout.tsx); the web apps gate on their own :access codes, so
  // omitting both here is what keeps DRIVER out of the frontoffice/backoffice entirely.
  // SWOOP-199: granted to a user ALONGSIDE their existing roles (never in place of them)
  // when they are added as a driver.
  [BUILT_IN_ROLE_KEY.DRIVER]: {
    permissions: ["mobile:access"]
  },
  [BUILT_IN_ROLE_KEY.FLEET_MANAGER]: {
    permissions: [
      // SWOOP-366/398: the Fleet Manager alone assigns, changes or removes an agreement's core vehicle and
      // records its handover (`agreement-assignments:manage`); `agreements:list` prints the Lease Delivery
      // Note and uploads/replaces the signed copy.
      "agreement-assignments:manage",
      "agreements:list",
      // SWOOP-399: manage relief vehicles on agreements; reading follows contracts:list.
      "agreements:manage",
      // SWOOP-345: read-only Leasing Contracts access. FLEET_MANAGER lists and opens contracts but does
      // NOT hold `contracts:manage`, so it cannot archive, delete or restore.
      "contracts:list",
      "customers:list",
      "customers:manage",
      "divisions:list",
      "divisions:manage",
      "documents:list",
      "documents:manage",
      "drivers:list",
      "drivers:manage",
      "energy-sources-price:list",
      "energy-sources-price:manage",
      "energy-sources:list",
      "expense-categories:list",
      "expenses:list",
      "expenses:manage",
      "frontoffice:access",
      // SWOOP-255: the Issues screen (Maintenance module). Same maintenance-ownership rationale as
      // service-plans: whoever runs the fleet reports, works and resolves its faults.
      "issues:list",
      "issues:manage",
      // SWOOP-339: read-only here — see the TENANT_ADMIN block.
      "leasing-settings:list",
      "leasing:access",
      "locations:list",
      "locations:manage",
      "permissions:read",
      "pool-types:list",
      "roles:read",
      // SWOOP-241: FLEET_MANAGER also gets `service-operations:manage`, not just `:list`. This is a
      // DELIBERATE DEVIATION from the "master-list management is TENANT_ADMIN-only" precedent used by
      // expense-categories and pool-types (both give FLEET_MANAGER only `:list`). The SWOOP-241 user
      // story is "As a Fleet Manager, I want to add operations specific to our fleet", and the epic
      // names the Fleet Manager as the owner of maintenance policy throughout — so `:manage` is
      // intentional here, not drift. Precedent for FLEET_MANAGER holding `:manage` on fleet master
      // data already exists: vehicle-groups and vehicle-ownerships.
      "service-operations:list",
      "service-operations:manage",
      // SWOOP-287: service plans follow the same maintenance-ownership rationale as service-operations
      // above — FLEET_MANAGER manages the tenant's maintenance policy, so it gets `:manage` too, not
      // just `:list`. Same deliberate deviation from the TENANT_ADMIN-only master-list precedent.
      "service-plans:list",
      "service-plans:manage",
      "suppliers:list",
      "suppliers:manage",
      "territories:edit",
      "territories:list",
      "territories:manage",
      "vehicle-groups:list",
      "vehicle-groups:manage",
      // SWOOP-378: the epic's role table gives the Fleet manager VIEW access to a vehicle's inspections on its
      // profile tab and nothing more — so `:list` only (no `:manage`), and deliberately NOT `inspections:list`,
      // which would unlock the Inspections menu the table does not give them. Guards the vehicle profile's
      // Inspections tab (still SOON, no route yet).
      "vehicle-inspections:list",
      "vehicle-makes:list",
      "vehicle-models:list",
      "vehicle-ownerships:list",
      "vehicle-ownerships:manage",
      "vehicle-types:list",
      "vehicles:list",
      "vehicles:manage",
      // SWOOP-325: work orders follow the same maintenance-ownership rationale as service-operations /
      // service-plans above — the Fleet Manager owns the tenant's maintenance operations, so it manages
      // (opens) work orders, not just reads them. Same deliberate deviation from the TENANT_ADMIN-only
      // master-list precedent.
      "work-orders:list",
      "work-orders:manage"
    ]
  },
  // SWOOP-378: INSPECTOR is a standalone role, stacked on whatever else a user holds (a driver or fleet
  // manager may also be an inspector) — so it carries ONLY what inspecting needs and never widens another
  // role. `locations:list` feeds the inspection list's location filter. The Inspector deliberately does NOT
  // see Settings > Inspections: that page is gated by `tenants:read` (the Settings permission Tenant Admins
  // hold) plus the module, and the ticket's AC says an inspector cannot see it. So never give INSPECTOR
  // `tenants:read`. SWOOP-387: it reads the inspection templates it works from (`inspection-templates:list`)
  // but never holds `inspection-templates:manage`, which only a tenant admin does.
  // The role is bound to the Inspections module via `ROLE_REQUIRED_MODULE` (role.interface.ts).
  [BUILT_IN_ROLE_KEY.INSPECTOR]: {
    permissions: [
      "frontoffice:access",
      "inspection-templates:list",
      "inspections:list",
      "inspections:manage",
      "locations:list",
      "repairs:list",
      "repairs:manage"
    ]
  },
  [BUILT_IN_ROLE_KEY.LOCATION_MANAGER]: {
    permissions: [
      "divisions:list",
      "frontoffice:access",
      "leasing:access",
      "locations:list",
      "locations:manage",
      "permissions:read",
      "roles:read",
      "tracking:read",
      "users:list",
      "users:manage"
    ]
  },
  [BUILT_IN_ROLE_KEY.TENANT_ADMIN]: {
    permissions: [
      // SWOOP-414: the over-limit exception — a price or term change above 20 % — is the Tenant Admin's alone.
      // Paired with `agreement-terms:manage` below, which the Account Manager also holds.
      "agreement-terms-over-limit:manage",
      // SWOOP-414: modify an On lease agreement's monthly price, extra-km price or end date (see ACCOUNT_MANAGER).
      "agreement-terms:manage",
      // SWOOP-366/398: print the Lease Delivery Note and upload/replace the signed handover copy. Assigning a
      // vehicle and recording the handover are Fleet Manager only (`agreement-assignments:manage`).
      "agreements:list",
      // SWOOP-399: manage relief vehicles on agreements; reading follows contracts:list.
      "agreements:manage",
      // SWOOP-430: the Territory › Billing defaults tab. Held by Tenant Admin, Territory Admin (own territory)
      // and the Billing Officer.
      "billing-settings:list",
      "billing-settings:manage",
      // SWOOP-430: the Billing menu and its actions. Held by Tenant Admin, Territory Admin (own territory) and the
      // Billing Officer; no other built-in role (not the Account Manager or the Fleet Manager).
      "billing:list",
      "billing:manage",
      // SWOOP-397: see the ACCOUNT_MANAGER block — Duplicate is Account Manager and Tenant Admin only.
      "contract-duplicates:manage",
      // SWOOP-416: see the ACCOUNT_MANAGER block — generating a fee re-bill is Account Manager and Tenant Admin only.
      "contract-fee-rebills:manage",
      // SWOOP-419: add, edit and delete customer PO numbers — Account Manager and Tenant Admin only (see the
      // ACCOUNT_MANAGER block). The Territory Admin and the Fleet Manager see POs read-only.
      "contract-po:manage",
      // SWOOP-345: see the ACCOUNT_MANAGER block.
      "contracts:list",
      "contracts:manage",
      "customers:list",
      "customers:manage",
      "divisions:list",
      "divisions:manage",
      // SWOOP-267 / SWOOP-265: the document-generation epic's own resource — the DOCX templates
      // uploaded on the Document test screen, distinct from the existing `documents:*` family
      // (vehicle/tenant document metadata + SAS upload). TENANT_ADMIN-only ON PURPOSE: the story
      // requires the screen to be reachable "only by a tenant admin", and `documents:*` could not
      // express that because FLEET_MANAGER holds it too. Do NOT grant this pair to another role
      // without revisiting that AC.
      "document-templates:list",
      "document-templates:manage",
      "documents:list",
      "documents:manage",
      "drivers:list",
      "drivers:manage",
      "energy-sources-price:list",
      "energy-sources-price:manage",
      "energy-sources:list",
      "expense-categories:list",
      "expense-categories:manage",
      "expenses:list",
      "expenses:manage",
      "frontoffice:access",
      // SWOOP-387: the Inspector reads inspection templates, only a tenant admin manages them.
      "inspection-templates:list",
      "inspection-templates:manage",
      "invitations:manage",
      "invitations:read",
      // SWOOP-255: see the FLEET_MANAGER block above.
      "issues:list",
      "issues:manage",
      // SWOOP-339: the lease options a territory offers get their own pair. It is deliberately NOT
      // `territories:edit` — Fleet Manager and Account Manager hold that, so they could edit what the
      // ticket makes read-only for them — and NOT a widening of `leasing:access` (see its note on
      // ACCOUNT_MANAGER). `:list` goes to the four roles that can reach the Leasing tab today;
      // `:manage` is TENANT_ADMIN and TERRITORY_ADMIN only.
      "leasing-settings:list",
      "leasing-settings:manage",
      "leasing:access",
      "locations:list",
      "locations:manage",
      "permissions:read",
      "pool-types:list",
      "pool-types:manage",
      "roles:manage",
      "roles:read",
      // SWOOP-241: see the FLEET_MANAGER block above for why `service-operations:manage` is granted
      // alongside `:list` here too (TENANT_ADMIN gaining `:manage` is the expected default — the note
      // there documents why FLEET_MANAGER also gets it, which is the deliberate part).
      "service-operations:list",
      "service-operations:manage",
      // SWOOP-287: service plans, granted to TENANT_ADMIN alongside `:list` (see the FLEET_MANAGER
      // block for why the Fleet Manager also gets `:manage`).
      "service-plans:list",
      "service-plans:manage",
      "suppliers:list",
      "suppliers:manage",
      "tenant-defaults:read",
      "tenant-defaults:write",
      "tenants:initialize",
      "tenants:manage",
      "tenants:read",
      "territories:edit",
      "territories:list",
      "territories:manage",
      "tracking:read",
      "users:list",
      "users:manage",
      "vehicle-groups:list",
      "vehicle-groups:manage",
      "vehicle-makes:list",
      "vehicle-models:list",
      "vehicle-ownerships:list",
      "vehicle-ownerships:manage",
      "vehicle-types:list",
      "vehicles:list",
      "vehicles:manage",
      "webhooks:manage",
      "webhooks:read",
      // SWOOP-325: Tenant Admin can READ work orders but not open them (D3). Only the Fleet Manager
      // opens work orders, so TENANT_ADMIN gets `:list` without `:manage` — unlike service-plans /
      // service-operations, where it holds both.
      "work-orders:list"
    ]
  },
  [BUILT_IN_ROLE_KEY.TERRITORY_ADMIN]: {
    permissions: [
      // SWOOP-366/398: print the Lease Delivery Note and upload/replace the signed handover copy. Assigning a
      // vehicle and recording the handover are Fleet Manager only (`agreement-assignments:manage`).
      "agreements:list",
      // SWOOP-399: manage relief vehicles on agreements; reading follows contracts:list.
      "agreements:manage",
      // SWOOP-430: the Territory › Billing defaults tab — the Territory Admin manages it for the territories
      // they administer (own-territory scope enforced by the route).
      "billing-settings:list",
      "billing-settings:manage",
      // SWOOP-430: the Billing menu and its actions, for the territories they administer (scope enforced by the
      // Billing routes). See the TENANT_ADMIN block.
      "billing:list",
      "billing:manage",
      // SWOOP-345: see the ACCOUNT_MANAGER block.
      "contracts:list",
      "contracts:manage",
      "customers:list",
      "customers:manage",
      "divisions:list",
      "divisions:manage",
      "drivers:list",
      "drivers:manage",
      "energy-sources-price:list",
      "energy-sources-price:manage",
      "frontoffice:access",
      // SWOOP-339: see the TENANT_ADMIN block.
      "leasing-settings:list",
      "leasing-settings:manage",
      "leasing:access",
      "locations:list",
      "locations:manage",
      "permissions:read",
      "roles:manage",
      "roles:read",
      "suppliers:list",
      "suppliers:manage",
      // SWOOP-302: `territories:edit` without `territories:manage` — a Territory Admin edits the
      // territories they administer but cannot create or delete one. Every role that holds `:manage`
      // also holds `:edit`, since PATCH /territories/:id checks `:edit` alone (the guard enforces one code).
      "territories:edit",
      "territories:list",
      "users:list",
      "users:manage"
    ]
  }
};

// src/permissions/role.interface.ts
var ROLE_SCOPE = {
  carrier: "carrier",
  location: "location",
  tenant: "tenant",
  territory: "territory"
};
var ROLE_REQUIRED_SCOPE = {
  ACCOUNT_MANAGER: null,
  // SWOOP-430: a billing officer runs invoicing for a whole territory or a whole tenant, so EITHER a
  // territory-level OR a tenant-level scope satisfies the role (array, like INSPECTOR). It is deliberately
  // NOT module-bound (absent from `ROLE_REQUIRED_MODULE`) — the Billing surfaces are not gated by a module.
  // Who may give it: a tenant-wide caller or Back Office with any scope; a Territory Admin only with a territory
  // scope inside the territories they administer (they hold the same Billing grants there since the 4 Nov ticket
  // update), never a tenant scope — `UserAccessService.assertCanAssign` bounds it by scope.
  BILLING_OFFICER: [ROLE_SCOPE.territory, ROLE_SCOPE.tenant],
  // SWOOP-200: no scope is required for DRIVER. Territory association for drivers is owned
  // by the `drivers` collection (SWOOP-199), not by scoped user roles — requiring e.g.
  // `territory` here would 400 on unrelated user edits that merely happen to include the
  // DRIVER role. This value IS enforced, not a lookup-only default: `validateRoleScopes` in
  // apps/hetivo-back-admin-auth/src/user/user.service.ts treats `null` and `tenant`
  // identically as "no scope required" and skips the check for both.
  // SWOOP-199: a driver's real territory/location scope lives on their `scopes` array (synced
  // from the `drivers` record). Do NOT tighten this to `territory` — a location-attached driver
  // carries location-level scope only (no tenant/territory scope) and would then fail
  // create/update with a 400.
  DRIVER: ROLE_SCOPE.tenant,
  FLEET_MANAGER: ROLE_SCOPE.tenant,
  // SWOOP-378: an inspector works the sites they are given — either a whole territory or individual
  // locations — so ANY ONE of those two levels satisfies the role. A tenant-only scope does not: the
  // role is deliberately not tenant-wide. It is still module-bound, so only a tenant-wide caller (or Back
  // Office) may newly give it — a Territory Admin may not, since its grants exceed their own (G2, see
  // `UserAccessService.assertCanAssign`).
  INSPECTOR: [ROLE_SCOPE.territory, ROLE_SCOPE.location],
  LOCATION_MANAGER: ROLE_SCOPE.location,
  TENANT_ADMIN: ROLE_SCOPE.tenant,
  TERRITORY_ADMIN: ROLE_SCOPE.territory
};
function isBuiltInRoleKey(role) {
  return Object.prototype.hasOwnProperty.call(ROLE_REQUIRED_SCOPE, role);
}
function getRequiredScopeLevels(role) {
  const required = ROLE_REQUIRED_SCOPE[role];
  if (required === null) return [];
  return typeof required === "string" ? [required] : required;
}
var ROLE_REQUIRED_MODULE = {
  INSPECTOR: MODULE_AREA.Inspections
};
function getRoleRequiredModule(role) {
  return isBuiltInRoleKey(role) ? ROLE_REQUIRED_MODULE[role] : void 0;
}

// src/permissions/hetivo-scope-access-filter.interface.ts
var DEFAULT_HETIVO_SCOPE_FIELD_MAPPING = {
  carrier: "carrierId",
  location: "locationId",
  territory: "territoryId"
};

// src/pool-type/pool-type.interface.ts
var POOL_TYPE_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive"
};

// src/service-auth.ts
var SERVICE_ROLES = {
  BILLING_SOURCE_READ: "BillingSource.Read",
  // SWOOP-430: admin-auth reads and writes a territory's billing defaults (document language, reporting
  // currency, exchange rates) on invoicing's `tenant_billing_settings` through its internal endpoint.
  INVOICING_BILLING_SETTINGS_MANAGE: "Invoicing.BillingSettingsManage",
  INVOICING_STATUS_READ: "Invoicing.StatusRead",
  INVOICING_TRIGGER: "Invoicing.Trigger"
};
var SERVICE_APPS = {
  ADMIN_AUTH: "hetivo-back-admin-auth",
  INVOICING: "hetivo-back-invoicing"
};
var SERVICE_ROLE_GRANTS = {
  [SERVICE_ROLES.BILLING_SOURCE_READ]: [SERVICE_APPS.INVOICING],
  [SERVICE_ROLES.INVOICING_BILLING_SETTINGS_MANAGE]: [SERVICE_APPS.ADMIN_AUTH],
  [SERVICE_ROLES.INVOICING_STATUS_READ]: [SERVICE_APPS.ADMIN_AUTH],
  [SERVICE_ROLES.INVOICING_TRIGGER]: []
};

// src/service-bus-topics.ts
var SERVICE_BUS_TOPICS = {
  ACCOUNTING_SYNC_REQUESTED: "on-accounting-sync-requested",
  EMAIL_SEND_REQUESTED: "on-email-send-requested",
  EMAIL_SEND_RESPONSE: "on-email-send-response",
  EMAIL_STATUS_UPDATED: "on-email-status-updated",
  GEOCODING_REQUEST: "on-geocoding-request",
  GEOCODING_RESPONSE: "on-geocoding-response",
  INVOICE_ISSUE_BATCH_REQUESTED: "on-invoice-issue-batch-requested",
  INVOICE_RUN_REQUESTED: "on-invoice-run-requested",
  LOCATION_CREATED: "on-location-created",
  LOCATION_UPDATED: "on-location-updated",
  SUPPLIER_CREATED: "on-supplier-created",
  SUPPLIER_UPDATED: "on-supplier-updated"
};

// src/service-operation/service-operation.interface.ts
var SERVICE_OPERATION_CATEGORY = {
  BRAKES: "brakes",
  COMFORT: "comfort",
  COOLING: "cooling",
  ELECTRICAL: "electrical",
  ENGINE: "engine",
  LEGAL_SAFETY: "legal_safety",
  LUBRICATION: "lubrication",
  TYRES: "tyres"
};
var SERVICE_OPERATION_ORIGIN = {
  CUSTOM: "custom",
  PREDEFINED: "predefined"
};
var SERVICE_OPERATION_DEFAULTS = [
  {
    category: SERVICE_OPERATION_CATEGORY.LUBRICATION,
    code: "ENGINE_OIL_REPLACEMENT",
    defaultParts: "Engine oil 5 L",
    name: "Engine oil replacement"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.LUBRICATION,
    code: "OIL_FILTER_REPLACEMENT",
    defaultParts: "Oil filter 1",
    name: "Oil filter replacement"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.ENGINE,
    code: "AIR_FILTER_REPLACEMENT",
    defaultParts: "Air filter 1",
    name: "Air filter replacement"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.COMFORT,
    code: "CABIN_FILTER_REPLACEMENT",
    defaultParts: "Cabin filter 1",
    name: "Cabin filter replacement"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.BRAKES,
    code: "BRAKE_INSPECTION",
    name: "Brake inspection"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.BRAKES,
    code: "BRAKE_PADS_REPLACEMENT",
    defaultParts: "Brake pad set 1",
    name: "Brake pads replacement"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.BRAKES,
    code: "BRAKE_FLUID_REPLACEMENT",
    defaultParts: "Brake fluid 1 L",
    name: "Brake fluid replacement"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.TYRES,
    code: "TYRE_INSPECTION",
    name: "Tyre inspection"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.TYRES,
    code: "TYRE_ROTATION",
    name: "Tyre rotation"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.COOLING,
    code: "COOLANT_REPLACEMENT",
    defaultParts: "Coolant 5 L",
    name: "Coolant replacement"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.ELECTRICAL,
    code: "SPARK_PLUGS_REPLACEMENT",
    defaultParts: "Spark plug set 1",
    name: "Spark plugs replacement"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.ELECTRICAL,
    code: "BATTERY_TEST",
    name: "Battery test"
  },
  {
    category: SERVICE_OPERATION_CATEGORY.LEGAL_SAFETY,
    code: "ANNUAL_SAFETY_INSPECTION",
    name: "Annual safety inspection"
  }
];

// src/service-package/service-package.interface.ts
var TYRE_CHANGE_BASIS = { perDistance: "perDistance", perYear: "perYear" };
var REPLACEMENT_CAR_RESTRICTION = {
  any: "any",
  convenient: "convenient",
  sameCategory: "sameCategory"
};
var SERVICE_PACKAGE_STATUS = { active: "active", retired: "retired" };
var SERVICE_PACKAGE_RESERVED_NAME = "No";
var SERVICE_PACKAGE_RESERVED_NAME_KEY = SERVICE_PACKAGE_RESERVED_NAME.toLowerCase();
var SERVICE_PACKAGE_LIMITS = {
  nameMax: 60
};

// src/service-plan/service-plan.interface.ts
var SERVICE_PLAN_TYPE = {
  CUSTOM: "CUSTOM",
  MANUFACTURER: "MANUFACTURER"
};
var SERVICE_PLAN_INTERVAL_TRIGGER = {
  MILEAGE_ONLY: "mileage_only",
  TIME_ONLY: "time_only",
  WHICHEVER_FIRST: "whichever_first"
};
var SERVICE_PLAN_MAX_INTERVALS = 50;
var SERVICE_PLAN_MAX_INTERVAL_ITEMS = 100;
var SERVICE_PLAN_YEAR_MODE = {
  ALL: "all",
  RANGE: "range",
  SINGLE: "single"
};

// src/service-plan/service-plan-specificity.ts
function isConstrained(value) {
  if (value === null || value === void 0) {
    return false;
  }
  return typeof value === "string" ? value.trim() !== "" : true;
}
function normalizeText(value) {
  if (value === null || value === void 0) {
    return null;
  }
  const trimmed = value.trim().toLowerCase();
  return trimmed === "" ? null : trimmed;
}
function yearBounds(year) {
  if (year.mode === SERVICE_PLAN_YEAR_MODE.SINGLE) {
    const value = year.from ?? Number.NEGATIVE_INFINITY;
    return { hi: value, lo: value };
  }
  if (year.mode === SERVICE_PLAN_YEAR_MODE.RANGE) {
    return { hi: year.to ?? Number.POSITIVE_INFINITY, lo: year.from ?? Number.NEGATIVE_INFINITY };
  }
  return { hi: Number.POSITIVE_INFINITY, lo: Number.NEGATIVE_INFINITY };
}
function yearsIntersect(a, b) {
  const boundsA = yearBounds(a);
  const boundsB = yearBounds(b);
  return boundsA.lo <= boundsB.hi && boundsB.lo <= boundsA.hi;
}
function valuesIntersect(a, b) {
  if (!isConstrained(a) || !isConstrained(b)) {
    return true;
  }
  return a === b;
}
function engineIntersects(a, b) {
  const normA = normalizeText(a);
  const normB = normalizeText(b);
  if (normA === null || normB === null) {
    return true;
  }
  return normA === normB;
}
function applicabilitiesIntersect(a, b) {
  return valuesIntersect(a.makeId, b.makeId) && valuesIntersect(a.model, b.model) && valuesIntersect(a.trimModelId, b.trimModelId) && valuesIntersect(a.energySourceId, b.energySourceId) && valuesIntersect(a.transmission, b.transmission) && engineIntersects(a.engine, b.engine) && yearsIntersect(a.year, b.year);
}
function yearLevel(year) {
  if (year.mode === SERVICE_PLAN_YEAR_MODE.SINGLE) {
    return 2;
  }
  return year.mode === SERVICE_PLAN_YEAR_MODE.RANGE ? 1 : 0;
}
function specificityTuple(applicability) {
  const otherConstrainedCount = [
    applicability.makeId,
    applicability.model,
    applicability.energySourceId,
    applicability.transmission
  ].filter(isConstrained).length;
  return [
    isConstrained(applicability.trimModelId) ? 1 : 0,
    isConstrained(applicability.engine) ? 1 : 0,
    yearLevel(applicability.year),
    otherConstrainedCount
  ];
}
function compareTuples(a, b) {
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) {
      return a[index] > b[index] ? 1 : -1;
    }
  }
  return 0;
}
function compareSpecificity(a, b) {
  const tupleComparison = compareTuples(specificityTuple(a.applicability), specificityTuple(b.applicability));
  if (tupleComparison !== 0) {
    return tupleComparison;
  }
  if (a.type === b.type) {
    return 0;
  }
  return a.type === SERVICE_PLAN_TYPE.MANUFACTURER ? 1 : -1;
}

// src/service-plan/service-plan-status.ts
var SERVICE_PLAN_STATUS = {
  ACTIVE: "active",
  EXPIRED: "expired",
  INACTIVE: "inactive"
};
function servicePlanStatus(plan, now) {
  if (!plan.active) {
    return SERVICE_PLAN_STATUS.INACTIVE;
  }
  if (plan.effectiveTo != null && new Date(plan.effectiveTo).getTime() < now.getTime()) {
    return SERVICE_PLAN_STATUS.EXPIRED;
  }
  return SERVICE_PLAN_STATUS.ACTIVE;
}

// src/service-plan/service-plan-assignment.ts
function vehicleMatchesApplicability(vehicle, applicability) {
  return matchesExact(applicability.makeId, vehicle.makeId) && matchesExact(applicability.model, vehicle.modelCode) && matchesExact(applicability.trimModelId, vehicle.modelId) && matchesEngine(applicability.engine, vehicle.engineId) && matchesExact(applicability.energySourceId, vehicle.energySourceId) && matchesExact(applicability.transmission, vehicle.transmission) && vehicleMatchesYear(vehicle.year, applicability);
}
function matchesExact(constraint, value) {
  return constraint == null || value === constraint;
}
function matchesEngine(constraint, engineId) {
  const engine = constraint?.trim();
  return !engine || engineId?.toLowerCase() === engine.toLowerCase();
}
function vehicleMatchesYear(vehicleYear, applicability) {
  const { year } = applicability;
  if (year.mode === SERVICE_PLAN_YEAR_MODE.SINGLE && year.from != null) {
    return vehicleYear === year.from;
  }
  if (year.mode === SERVICE_PLAN_YEAR_MODE.RANGE && year.from != null && year.to != null) {
    return vehicleYear >= year.from && vehicleYear <= year.to;
  }
  return true;
}
function isServicePlanInEffect(plan, now) {
  if (servicePlanStatus(plan, now) !== SERVICE_PLAN_STATUS.ACTIVE) {
    return false;
  }
  return plan.effectiveFrom == null || new Date(plan.effectiveFrom).getTime() <= now.getTime();
}
function resolveServicePlanAssignment(vehicle, plans, now) {
  const candidates = plans.filter(
    (plan) => isServicePlanInEffect(plan, now) && vehicleMatchesApplicability(vehicle, plan.applicability)
  );
  if (candidates.length === 0) {
    return { servicePlanConflictIds: [], servicePlanIds: [] };
  }
  const best = candidates.reduce((top, plan) => compareSpecificity(plan, top) > 0 ? plan : top);
  const tied = candidates.filter((plan) => compareSpecificity(plan, best) === 0);
  if (tied.length === 1) {
    return { servicePlanConflictIds: [], servicePlanIds: [best.id] };
  }
  return { servicePlanConflictIds: tied.map((plan) => plan.id).sort(), servicePlanIds: [] };
}

// src/service-plan/service-plan-preview.interface.ts
var SERVICE_PLAN_PREVIEW_VEHICLE_LIMIT = 50;
var SERVICE_PLAN_LOSS_REASON = {
  /** The draft is switched off. */
  PLAN_INACTIVE: "PLAN_INACTIVE",
  /** The draft is on, but its effective dates exclude today (expired or not yet started). */
  PLAN_NOT_IN_EFFECT: "PLAN_NOT_IN_EFFECT",
  /** The draft now ties with another plan for the vehicle, so neither applies. */
  TIE: "TIE",
  /** The vehicle falls outside the draft's applicability. */
  NO_LONGER_MATCHES: "NO_LONGER_MATCHES",
  /** The vehicle still matches, but another plan is now more specific for it. */
  OTHER_PLAN_WINS: "OTHER_PLAN_WINS"
};
function previewRequiresConfirmation(preview) {
  return preview.losing.length > 0 || preview.takenFrom.length > 0 || preview.ties.length > 0 || preview.removedIntervals.length > 0 && preview.following > 0;
}

// src/service-plan/service-plan-rules.ts
function triggerThresholds(trigger) {
  return {
    mileage: trigger !== SERVICE_PLAN_INTERVAL_TRIGGER.TIME_ONLY,
    months: trigger !== SERVICE_PLAN_INTERVAL_TRIGGER.MILEAGE_ONLY
  };
}
function missingIntervalThresholds(interval) {
  const required = triggerThresholds(interval.trigger);
  const missing = [];
  if (required.mileage && interval.mileageKm == null) {
    missing.push("mileageKm");
  }
  if (required.months && interval.months == null) {
    missing.push("months");
  }
  return missing;
}
function servicePlanYearIssues(year) {
  if (year.mode === SERVICE_PLAN_YEAR_MODE.SINGLE) {
    return year.from == null ? ["fromRequired"] : [];
  }
  if (year.mode === SERVICE_PLAN_YEAR_MODE.RANGE) {
    const issues = [];
    if (year.from == null) {
      issues.push("fromRequired");
    }
    if (year.to == null) {
      issues.push("toRequired");
    }
    if (year.from != null && year.to != null && year.from > year.to) {
      issues.push("rangeInverted");
    }
    return issues;
  }
  return [];
}
function applicabilityDependencyIssues(applicability) {
  const issues = [];
  if (applicability.model !== null && applicability.makeId === null) {
    issues.push("modelRequiresMake");
  }
  if (applicability.trimModelId != null && applicability.model === null) {
    issues.push("trimRequiresModel");
  }
  return issues;
}

// src/service-plan/service-plan-update-diff.ts
function sameValue(a, b) {
  return (a ?? null) === (b ?? null);
}
function sameApplicability(a, b) {
  return sameValue(a.energySourceId, b.energySourceId) && sameValue(a.engine, b.engine) && sameValue(a.makeId, b.makeId) && sameValue(a.model, b.model) && sameValue(a.transmission, b.transmission) && sameValue(a.trimModelId, b.trimModelId) && a.year.mode === b.year.mode && sameValue(a.year.from, b.year.from) && sameValue(a.year.to, b.year.to);
}
function sameInstant(a, b) {
  return sameValue(a == null ? null : new Date(a).getTime(), b == null ? null : new Date(b).getTime());
}
function changesPlanWinner(before, after) {
  return before.active !== after.active || before.type !== after.type || !sameInstant(before.effectiveFrom, after.effectiveFrom) || !sameInstant(before.effectiveTo, after.effectiveTo) || !sameApplicability(before.applicability, after.applicability);
}
function normalizeInterval(interval) {
  return {
    code: interval.code,
    graceDays: interval.graceDays,
    graceKm: interval.graceKm,
    items: interval.items.map((item) => ({
      notes: item.notes ?? null,
      parts: item.parts ?? null,
      required: item.required,
      serviceOperationId: String(item.serviceOperationId)
    })),
    mileageKm: interval.mileageKm ?? null,
    months: interval.months ?? null,
    name: interval.name,
    trigger: interval.trigger
  };
}
function sameIntervals(a, b) {
  return JSON.stringify(a.map(normalizeInterval)) === JSON.stringify(b.map(normalizeInterval));
}
function diffServicePlanUpdate(stored, patch) {
  const diff = {};
  diffScalarFields(diff, stored, patch);
  diffStructuralFields(diff, stored, patch);
  return diff;
}
function diffScalarFields(diff, stored, patch) {
  if (patch.active !== void 0 && patch.active !== stored.active) {
    diff.active = patch.active;
  }
  if (patch.code !== void 0 && patch.code !== stored.code) {
    diff.code = patch.code;
  }
  if (patch.name !== void 0 && patch.name !== stored.name) {
    diff.name = patch.name;
  }
  if (patch.description !== void 0 && !sameValue(patch.description, stored.description)) {
    diff.description = patch.description;
  }
}
function diffStructuralFields(diff, stored, patch) {
  if (patch.effectiveFrom !== void 0 && !sameInstant(patch.effectiveFrom, stored.effectiveFrom)) {
    diff.effectiveFrom = patch.effectiveFrom;
  }
  if (patch.effectiveTo !== void 0 && !sameInstant(patch.effectiveTo, stored.effectiveTo)) {
    diff.effectiveTo = patch.effectiveTo;
  }
  if (patch.applicability !== void 0 && !sameApplicability(stored.applicability, patch.applicability)) {
    diff.applicability = patch.applicability;
  }
  if (patch.intervals !== void 0 && !sameIntervals(stored.intervals, patch.intervals)) {
    diff.intervals = patch.intervals;
  }
}

// src/service-reminder/cohort-daily-rate.ts
var COHORT_MIN_MEMBERS = 5;
var COHORT_SCOPE = {
  TYPE: "TYPE",
  TYPE_POOL: "TYPE_POOL",
  TYPE_POOL_TERRITORY: "TYPE_POOL_TERRITORY"
};
var WIDENING = [COHORT_SCOPE.TYPE_POOL_TERRITORY, COHORT_SCOPE.TYPE_POOL, COHORT_SCOPE.TYPE];
function groupKey(key, scope) {
  switch (scope) {
    case COHORT_SCOPE.TYPE_POOL_TERRITORY:
      return key.territoryId === null ? null : `${scope}|${key.vehicleTypeId}|${key.poolTypeId}|${key.territoryId}`;
    case COHORT_SCOPE.TYPE_POOL:
      return `${scope}|${key.vehicleTypeId}|${key.poolTypeId}`;
    case COHORT_SCOPE.TYPE:
      return `${scope}|${key.vehicleTypeId}`;
  }
}
function buildCohortIndex(members) {
  const grouped = /* @__PURE__ */ new Map();
  for (const member of members) {
    for (const scope of WIDENING) {
      const key = groupKey(member, scope);
      if (key !== null) {
        const group = grouped.get(key);
        if (group === void 0) {
          grouped.set(key, [member]);
        } else {
          group.push(member);
        }
      }
    }
  }
  const groups = /* @__PURE__ */ new Map();
  for (const [key, group] of grouped) {
    const sortedRates = group.map((member) => member.kmPerDay).sort((a, b) => a - b);
    groups.set(key, {
      median: median(sortedRates),
      ratesByVehicle: new Map(group.map((member) => [member.vehicleId, member.kmPerDay])),
      sortedRates
    });
  }
  return { groups };
}
function median(sorted) {
  const middle = Math.floor(sorted.length / 2);
  const value = sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
  return Math.round(value * 10) / 10;
}
function withoutVehicle(group, vehicleId) {
  const own = group.ratesByVehicle.get(vehicleId);
  if (own === void 0) {
    return { median: group.median, members: group.sortedRates.length };
  }
  const rest = [...group.sortedRates];
  rest.splice(rest.indexOf(own), 1);
  return { median: rest.length === 0 ? 0 : median(rest), members: rest.length };
}
function cohortRate(index, key) {
  for (const scope of WIDENING) {
    const groupId = groupKey(key, scope);
    const group = groupId === null ? void 0 : index.groups.get(groupId);
    if (group !== void 0) {
      const { median: kmPerDay, members } = withoutVehicle(group, key.vehicleId);
      if (members >= COHORT_MIN_MEMBERS) {
        return { kmPerDay, members, scope };
      }
    }
  }
  return null;
}

// src/vehicle-history/vehicle-history.interface.ts
var VEHICLE_HISTORY_TYPE = {
  ENERGY_LEVEL: "ENERGY_LEVEL",
  ODOMETER_READING: "ODOMETER_READING"
};
var ODOMETER_MAX_KM = 9999999;

// src/service-reminder/service-reminder.interface.ts
var SERVICE_REMINDER_STATUS = {
  ACTIVE: "ACTIVE",
  CANCELLED: "CANCELLED",
  COMPLETED: "COMPLETED",
  DISMISSED: "DISMISSED",
  SNOOZED: "SNOOZED"
};
var SERVICE_REMINDER_URGENCY = {
  DUE: "DUE",
  OVERDUE: "OVERDUE",
  UPCOMING: "UPCOMING"
};

// src/service-reminder/service-reminder-calculator.ts
var URGENCY_RANK = {
  [SERVICE_REMINDER_URGENCY.DUE]: 1,
  [SERVICE_REMINDER_URGENCY.OVERDUE]: 2,
  [SERVICE_REMINDER_URGENCY.UPCOMING]: 0
};
function toOdometerReading(entry) {
  if (entry.type !== VEHICLE_HISTORY_TYPE.ODOMETER_READING || typeof entry.value !== "number" || !Number.isFinite(entry.value)) {
    return null;
  }
  return { date: new Date(entry.timestamp), odometer: entry.value };
}
function resolveBaseline(vehicle, readings) {
  const first = readings.reduce(
    (earliest, reading) => earliest == null || new Date(reading.date).getTime() < new Date(earliest.date).getTime() ? reading : earliest,
    null
  );
  if (first != null) {
    return { date: new Date(first.date), odometer: first.odometer };
  }
  return { date: new Date(vehicle.acquisitionDate ?? vehicle.createdAt), odometer: null };
}
function computeDuePoint(baseline, interval, lastReading, defaultDailyRateKm) {
  const uses = triggerThresholds(interval.trigger);
  const dueDate = uses.months && interval.months != null ? addMonthsUtc(baseline.date, interval.months) : null;
  const dueOdometer = uses.mileage && interval.mileageKm != null && baseline.odometer != null ? nextMileageMark(baseline.odometer, interval.mileageKm) : null;
  const from = lastReading ?? (baseline.odometer == null ? null : { date: baseline.date, odometer: baseline.odometer });
  return { dueDate, dueOdometer, projectedDate: projectDate(dueOdometer, from, defaultDailyRateKm) };
}
function computeNextDueDate(duePoint) {
  const { dueDate, projectedDate } = duePoint;
  if (dueDate == null) {
    return projectedDate;
  }
  if (projectedDate == null) {
    return dueDate;
  }
  return projectedDate.getTime() < dueDate.getTime() ? projectedDate : dueDate;
}
function nextMileageMark(fromKm, intervalKm) {
  if (!Number.isFinite(intervalKm) || intervalKm <= 0 || !Number.isFinite(fromKm)) {
    return null;
  }
  return (Math.floor(Math.max(0, fromKm) / intervalKm) + 1) * intervalKm;
}
function projectDate(dueOdometer, from, rateKmPerDay) {
  if (dueOdometer == null || from == null || !Number.isFinite(rateKmPerDay) || rateKmPerDay <= 0) {
    return null;
  }
  return addDays(from.date, Math.ceil((dueOdometer - from.odometer) / rateKmPerDay));
}
function utcDay2(date) {
  const value = new Date(date);
  return Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
}
function dateSignal(due, graceDays, today) {
  const dueDay = utcDay2(due);
  const todayDay = utcDay2(today);
  if (todayDay > dueDay) {
    return SERVICE_REMINDER_URGENCY.OVERDUE;
  }
  if (todayDay >= dueDay - graceDays * MS_PER_DAY) {
    return SERVICE_REMINDER_URGENCY.DUE;
  }
  return SERVICE_REMINDER_URGENCY.UPCOMING;
}
function distanceSignal(dueOdometer, graceKm, odometer) {
  if (odometer > dueOdometer) {
    return SERVICE_REMINDER_URGENCY.OVERDUE;
  }
  if (odometer >= dueOdometer - graceKm) {
    return SERVICE_REMINDER_URGENCY.DUE;
  }
  return SERVICE_REMINDER_URGENCY.UPCOMING;
}
function deriveUrgency(duePoint, interval, lastOdometer, today) {
  const uses = triggerThresholds(interval.trigger);
  const signals = [];
  if (uses.months && duePoint.dueDate != null) {
    signals.push(dateSignal(duePoint.dueDate, interval.graceDays, today));
  }
  if (uses.mileage && duePoint.dueOdometer != null && lastOdometer != null) {
    signals.push(distanceSignal(duePoint.dueOdometer, interval.graceKm, lastOdometer));
  }
  if (uses.mileage && duePoint.projectedDate != null) {
    signals.push(dateSignal(duePoint.projectedDate, interval.graceDays, today));
  }
  return signals.reduce(
    (worst, signal) => URGENCY_RANK[signal] > URGENCY_RANK[worst] ? signal : worst,
    SERVICE_REMINDER_URGENCY.UPCOMING
  );
}

// src/service-reminder/daily-rate.ts
var DAILY_RATE_SOURCE = {
  /** The median own rate of similar vehicles (SWOOP-330, the SWOOP-246 cohort rule). */
  COHORT: "COHORT",
  /** A leased vehicle's `contractMileageLimit` (km per year, Acquisition tab) ÷ 365 (SWOOP-246 Q7). */
  CONTRACT_MILEAGE: "CONTRACT_MILEAGE",
  /** The vehicle's own readings: (last − first odometer) ÷ days between them (SWOOP-330). */
  OWN_HISTORY: "OWN_HISTORY",
  /** `MaintenanceSettings.defaultDailyRateKm` (Settings › Maintenance › General). */
  TENANT_DEFAULT: "TENANT_DEFAULT"
};
var CONTRACT_MILEAGE_DAYS_PER_YEAR = 365;
var CONTRACT_MILEAGE_MIN_KM_PER_DAY = 1;
function contractDailyRate(contractMileageLimitKm) {
  if (contractMileageLimitKm == null || !Number.isFinite(contractMileageLimitKm)) {
    return null;
  }
  const kmPerDay = Math.round(contractMileageLimitKm / CONTRACT_MILEAGE_DAYS_PER_YEAR * 10) / 10;
  return kmPerDay >= CONTRACT_MILEAGE_MIN_KM_PER_DAY ? kmPerDay : null;
}
function resolveDailyRate(input) {
  if (input.own != null) {
    return {
      basis: {
        first: { date: toCalendarDay(input.own.first.date).toISOString(), odometer: input.own.first.odometer },
        kind: "READINGS",
        last: { date: toCalendarDay(input.own.last.date).toISOString(), odometer: input.own.last.odometer }
      },
      kmPerDay: input.own.kmPerDay,
      source: DAILY_RATE_SOURCE.OWN_HISTORY
    };
  }
  const contract = contractDailyRate(input.contractMileageLimitKm);
  if (contract !== null) {
    return { basis: null, kmPerDay: contract, source: DAILY_RATE_SOURCE.CONTRACT_MILEAGE };
  }
  if (input.cohort != null) {
    return {
      basis: { kind: "COHORT", members: input.cohort.members, scope: input.cohort.scope },
      kmPerDay: input.cohort.kmPerDay,
      source: DAILY_RATE_SOURCE.COHORT
    };
  }
  return { basis: null, kmPerDay: input.defaultDailyRateKm, source: DAILY_RATE_SOURCE.TENANT_DEFAULT };
}

// src/service-reminder/own-daily-rate.ts
var OWN_RATE_MIN_SPAN_DAYS = 30;
var OWN_RATE_MIN_KM_PER_DAY = 1;
function readingSpanDays(bound) {
  return Math.round(
    (toCalendarDay(bound.last.date).getTime() - toCalendarDay(bound.first.date).getTime()) / MS_PER_DAY
  );
}
function spansOwnRate(bound) {
  return bound !== void 0 && readingSpanDays(bound) >= OWN_RATE_MIN_SPAN_DAYS;
}
function ownDailyRate(bound) {
  if (bound === void 0) {
    return null;
  }
  const days = readingSpanDays(bound);
  if (days < OWN_RATE_MIN_SPAN_DAYS) {
    return null;
  }
  const kmPerDay = Math.round((bound.last.odometer - bound.first.odometer) / days * 10) / 10;
  return Number.isFinite(kmPerDay) && kmPerDay >= OWN_RATE_MIN_KM_PER_DAY ? { first: bound.first, kmPerDay, last: bound.last } : null;
}

// src/service-reminder/service-reminder-queue.ts
var MS_PER_DAY3 = 24 * 60 * 60 * 1e3;
var SERVICE_REMINDER_MAX_SNOOZES = 2;
var SERVICE_REMINDER_MAX_SNOOZE_DAYS = 30;
var SERVICE_REMINDER_REASON_MAX_LENGTH = 500;
var LIVE_REMINDER_STATUSES = [SERVICE_REMINDER_STATUS.ACTIVE, SERVICE_REMINDER_STATUS.SNOOZED];
var LISTED_REMINDER_STATUSES = [...LIVE_REMINDER_STATUSES, SERVICE_REMINDER_STATUS.COMPLETED];
var LINKABLE_REMINDER_STATUSES = [
  SERVICE_REMINDER_STATUS.ACTIVE,
  SERVICE_REMINDER_STATUS.SNOOZED
];
function graceKmToDays(graceKm, dailyRateKm) {
  return dailyRateKm > 0 ? Math.ceil(graceKm / dailyRateKm) : 0;
}
function computeDueFrom(duePoint, interval, dailyRateKm) {
  const nextDueDate = computeNextDueDate(duePoint);
  if (nextDueDate == null) {
    return null;
  }
  const kmDays = triggerThresholds(interval.trigger).mileage ? graceKmToDays(interval.graceKm, dailyRateKm) : 0;
  return addDays(nextDueDate, -Math.max(interval.graceDays, kmDays));
}
function graceWindowDays(nextDueDate, dueFrom) {
  if (nextDueDate == null || dueFrom == null) {
    return null;
  }
  return Math.round((nextDueDate.getTime() - dueFrom.getTime()) / MS_PER_DAY3);
}
function classifyQueueUrgency(row, today) {
  const { nextDueDate } = row;
  if (nextDueDate == null) {
    return SERVICE_REMINDER_URGENCY.UPCOMING;
  }
  const windowMs = nextDueDate.getTime() - (row.dueFrom ?? nextDueDate).getTime();
  const comparedDate = queueComparedDate(row) ?? nextDueDate;
  const windowStart = comparedDate.getTime() - windowMs;
  if (comparedDate.getTime() < today.getTime()) {
    return SERVICE_REMINDER_URGENCY.OVERDUE;
  }
  if (windowStart <= today.getTime()) {
    return SERVICE_REMINDER_URGENCY.DUE;
  }
  return SERVICE_REMINDER_URGENCY.UPCOMING;
}
function queueComparedDate(row) {
  const snoozedUntil = row.status === SERVICE_REMINDER_STATUS.SNOOZED ? row.snoozedUntil : null;
  return snoozedUntil ?? row.nextDueDate;
}
function restartBaselineFromDuePoint(row, interval, context) {
  const { nextDueDate } = row;
  if (nextDueDate == null) {
    return null;
  }
  const uses = triggerThresholds(interval.trigger);
  const bothSignals = uses.months && uses.mileage;
  const date = !bothSignals || dateFellDue(row, context.today) ? restartDate(row, nextDueDate, interval, context.today) : row.baselineDate;
  const odometer = !bothSignals || distanceFellDue(row, context.lastOdometer) ? restartOdometer(row, interval, context.lastOdometer) : row.baselineOdometer;
  return { date, odometer };
}
function restartDate(row, nextDueDate, interval, today) {
  return triggerThresholds(interval.trigger).months ? advanceDateUntilAfter(row.dueDate ?? nextDueDate, interval.months, today) : new Date(Math.min(nextDueDate.getTime(), today.getTime()));
}
function restartOdometer(row, interval, lastOdometer) {
  const mark = row.dueOdometer ?? row.baselineOdometer;
  return triggerThresholds(interval.trigger).mileage ? advanceMarkPast(mark, interval.mileageKm, lastOdometer) : mark;
}
function dateFellDue(row, today) {
  const { dueDate, projectedDate } = row;
  if (dueDate == null) {
    return false;
  }
  return projectedDate == null || dueDate.getTime() <= projectedDate.getTime() || dueDate.getTime() <= today.getTime();
}
function distanceFellDue(row, lastOdometer) {
  const { dueDate, dueOdometer, projectedDate } = row;
  const compared = projectedDate != null && (dueDate == null || projectedDate.getTime() <= dueDate.getTime());
  const reached = dueOdometer != null && lastOdometer != null && dueOdometer <= lastOdometer;
  return compared || reached;
}
function advanceDateUntilAfter(dueDate, months, today) {
  if (months == null || months <= 0) {
    return dueDate;
  }
  let steps = 0;
  while (addMonthsUtc(dueDate, (steps + 1) * months).getTime() <= today.getTime()) {
    steps += 1;
  }
  return addMonthsUtc(dueDate, steps * months);
}
function advanceMarkPast(mark, intervalKm, lastOdometer) {
  if (mark == null || lastOdometer == null || intervalKm == null || intervalKm <= 0) {
    return mark;
  }
  return mark + Math.max(0, Math.floor((lastOdometer - mark) / intervalKm)) * intervalKm;
}
function minSnoozeDate(comparedDate, today) {
  const tomorrow = addDays(today, 1);
  return comparedDate == null ? tomorrow : new Date(Math.max(tomorrow.getTime(), addDays(comparedDate, 1).getTime()));
}
function maxSnoozeDate(today) {
  return addDays(today, SERVICE_REMINDER_MAX_SNOOZE_DAYS);
}

// src/service-reminder/plan-reminder-changes.ts
function sameInstant2(a, b) {
  if (a == null || b == null) {
    return a == null && b == null;
  }
  return new Date(a).getTime() === new Date(b).getTime();
}
function isEligible(vehicle, plan, now) {
  return vehicle.status !== VEHICLE_STATUS.DRAFT && vehicle.status !== VEHICLE_STATUS.ARCHIVED && vehicle.servicePlanConflictIds.length === 0 && vehicle.servicePlanIds.length === 1 && vehicle.servicePlanIds[0] === plan.id && isServicePlanInEffect(plan, now);
}
function byTimestampThenOdometer(a, b) {
  return a.date.getTime() - b.date.getTime() || a.odometer - b.odometer;
}
function resolveVehicleStart(vehicle, readings) {
  const days = [...readings].sort(byTimestampThenOdometer).map((reading) => ({ date: toCalendarDay(reading.date), odometer: reading.odometer }));
  const baseline = resolveBaseline(
    {
      acquisitionDate: vehicle.acquisitionDate == null ? void 0 : toCalendarDay(vehicle.acquisitionDate),
      createdAt: toCalendarDay(vehicle.createdAt)
    },
    days
  );
  return { baseline, lastReading: days.at(-1) ?? null };
}
function buildNewRow(input, planId, interval, start, effectiveTo) {
  const duePoint = computeDuePoint(start.baseline, interval, start.lastReading, input.dailyRateKm);
  return {
    baselineDate: start.baseline.date,
    baselineOdometer: start.baseline.odometer,
    computedAt: input.now,
    ...duePoint,
    dueFrom: computeDueFrom(duePoint, interval, input.dailyRateKm),
    effectiveTo,
    intervalCode: interval.code,
    nextDueDate: computeNextDueDate(duePoint),
    originalDueDate: null,
    planId,
    snoozeCount: 0,
    snoozedUntil: null,
    status: SERVICE_REMINDER_STATUS.ACTIVE,
    statusReason: null,
    vehicleId: input.vehicle.id
  };
}
function reactivate(input, row, interval, start, effectiveTo) {
  const duePoint = computeDuePoint(start.baseline, interval, start.lastReading, input.dailyRateKm);
  return {
    baseline: start.baseline,
    duePoint,
    dueFrom: computeDueFrom(duePoint, interval, input.dailyRateKm),
    effectiveTo,
    id: row.id,
    intervalCode: interval.code,
    nextDueDate: computeNextDueDate(duePoint),
    reactivate: true
  };
}
function latestPoint(baseline, lastReading) {
  if (baseline.odometer == null) {
    return lastReading;
  }
  const baselineDay = new Date(baseline.date).getTime();
  const baselineIsLater = lastReading != null && (baselineDay > lastReading.date.getTime() || baselineDay === lastReading.date.getTime() && baseline.odometer > lastReading.odometer);
  if (lastReading == null || baselineIsLater && baseline.odometer >= lastReading.odometer) {
    return { date: new Date(baseline.date), odometer: baseline.odometer };
  }
  return lastReading;
}
function holdsValues(row, values) {
  return sameInstant2(values.effectiveTo, row.effectiveTo) && sameInstant2(values.dueDate, row.dueDate) && sameInstant2(values.dueFrom, row.dueFrom) && values.dueOdometer === row.dueOdometer && sameInstant2(values.projectedDate, row.projectedDate) && sameInstant2(values.nextDueDate, row.nextDueDate);
}
function refresh(input, row, interval, start, effectiveTo) {
  const rederive = row.baselineOdometer == null;
  const baseline = rederive ? start.baseline : { date: row.baselineDate, odometer: row.baselineOdometer };
  const duePoint = computeDuePoint(baseline, interval, latestPoint(baseline, start.lastReading), input.dailyRateKm);
  const nextDueDate = computeNextDueDate(duePoint);
  const dueFrom = computeDueFrom(duePoint, interval, input.dailyRateKm);
  const baselineMoved = rederive && (!sameInstant2(baseline.date, row.baselineDate) || baseline.odometer !== row.baselineOdometer);
  if (!baselineMoved && holdsValues(row, { ...duePoint, dueFrom, effectiveTo, nextDueDate })) {
    return null;
  }
  const change = {
    dueFrom,
    duePoint,
    effectiveTo,
    id: row.id,
    intervalCode: interval.code,
    nextDueDate
  };
  if (baselineMoved) {
    change.baseline = baseline;
  }
  return change;
}
function planReminderChanges(input) {
  const { vehicle, plan, existing, readings, now } = input;
  const considered = existing.filter((row) => row.status !== SERVICE_REMINDER_STATUS.COMPLETED);
  const live = considered.filter((row) => row.status !== SERVICE_REMINDER_STATUS.CANCELLED);
  if (plan == null || !isEligible(vehicle, plan, now)) {
    return { cancel: live.map((row) => row.id), create: [], recompute: [] };
  }
  const intervalCodes = new Set(plan.intervals.map((interval) => interval.code));
  const cancel = live.filter((row) => row.planId !== plan.id || !intervalCodes.has(row.intervalCode)).map((row) => row.id);
  const start = resolveVehicleStart(vehicle, readings);
  const effectiveTo = plan.effectiveTo ?? null;
  const create = [];
  const recompute = [];
  for (const interval of plan.intervals) {
    const row = considered.find(
      (candidate) => candidate.planId === plan.id && candidate.intervalCode === interval.code
    );
    if (row == null) {
      create.push(buildNewRow(input, plan.id, interval, start, effectiveTo));
    } else if (row.status === SERVICE_REMINDER_STATUS.CANCELLED) {
      recompute.push(reactivate(input, row, interval, start, effectiveTo));
    } else {
      const change = refresh(input, row, interval, start, effectiveTo);
      if (change != null) {
        recompute.push(change);
      }
    }
  }
  return { cancel, create, recompute };
}

// src/service-reminder/reading-age.ts
var READING_AGE_LEVEL = {
  AGING: "AGING",
  OLD: "OLD",
  RECENT: "RECENT"
};
var READING_AGE_RECENT_MAX_DAYS = 30;
var READING_AGE_AGING_MAX_DAYS = 90;
function readingAgeLevel(ageDays) {
  if (!(ageDays > READING_AGE_RECENT_MAX_DAYS)) {
    return READING_AGE_LEVEL.RECENT;
  }
  return ageDays > READING_AGE_AGING_MAX_DAYS ? READING_AGE_LEVEL.OLD : READING_AGE_LEVEL.AGING;
}

// src/service-reminder/service-reminder-queue.interface.ts
var SERVICE_REMINDER_QUEUE_TAB = {
  ALL: "ALL",
  COMPLETED: "COMPLETED",
  SNOOZED: "SNOOZED",
  ...SERVICE_REMINDER_URGENCY
};

// src/supplier/supplier.interface.ts
var SUPPLIER_TYPE = {
  INSURANCE: "INSURANCE",
  // A supplier that leases vehicles out (SWOOP-238): the vehicle Lease-details Lessor field only lists
  // ACTIVE suppliers of this type, scoped to the vehicle's territory (SWOOP-244).
  LESSOR: "LESSOR",
  // A supplier that sells vehicles (SWOOP-146): the Add-Vehicle Acquisition step's Vendor/supplier
  // field only lists ACTIVE suppliers of this type.
  VEHICLE_DEALER: "VEHICLE_DEALER",
  // A workshop that services vehicles (SWOOP-245). Deliberately NOT gated by the Maintenance
  // module: a fleet team records the workshops it uses whether or not it runs work orders in
  // Hetivo, so this is an ordinary supplier type like the other three, available to every tenant.
  //
  // Kept LAST on purpose, breaking this map's alphabetical order: the suppliers action bar builds
  // its Type filter from `Object.values(SUPPLIER_TYPE)`, so insertion order IS the dropdown order,
  // and the design lists Garage after Vehicle dealer. Re-sorting this map reorders the UI.
  GARAGE: "GARAGE"
};
var SUPPLIER_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE"
};
var GARAGE_KIND = {
  /** Run in-house by the fleet team. */
  OWN: "OWN",
  /** Contracted supplier, invoices the fleet. */
  OUTSIDE: "OUTSIDE"
};
var GARAGE_KINDS = Object.values(GARAGE_KIND);
var GARAGE_SPECIALITY = {
  AIR_CONDITIONING: "AIR_CONDITIONING",
  ALIGNMENT: "ALIGNMENT",
  BODYWORK: "BODYWORK",
  DIAGNOSTICS: "DIAGNOSTICS",
  ELECTRICAL: "ELECTRICAL",
  GLASS: "GLASS",
  MECHANICAL: "MECHANICAL",
  PAINT: "PAINT",
  SERVICING: "SERVICING",
  TYRES: "TYRES"
};
var GARAGE_SPECIALITIES = Object.values(GARAGE_SPECIALITY);

// src/template/template.interface.ts
var TEMPLATE_TYPE = {
  CARRIER: "carrier",
  SHIPPER: "shipper"
};

// src/tenant.ts
var SUPPORTED_COUNTRIES = ["FR", "LB"];
var TENANT_STATUS = {
  Active: "active",
  Archived: "archived",
  Draft: "draft",
  Inactive: "inactive",
  InitializationFailed: "initialization_failed",
  Initializing: "initializing"
};
var TENANT_STATUSES = [
  TENANT_STATUS.Active,
  TENANT_STATUS.Archived,
  TENANT_STATUS.Inactive
];

// src/test-factories/object.factory.ts
var ObjectFactory = class {
  /**
   * Build a new instance of T with optional overrides.
   * Overrides are spread on top of defaults, so you only specify what differs.
   */
  build(overrides = {}) {
    return {
      ...this.getDefaults(),
      ...overrides
    };
  }
  /**
   * Create a modified copy of an existing object with partial overrides.
   * Useful for testing mutations, updates, or "before/after" scenarios.
   */
  modify(base, overrides) {
    return {
      ...base,
      ...overrides
    };
  }
};

// src/test-factories/base-document.factory.ts
var counter = 0;
var BaseDocumentFactory = class extends ObjectFactory {
  getDefaults() {
    counter += 1;
    return {
      createdAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      deletedAt: null,
      id: `test-id-${counter}`,
      tenantId: "aaaa00001111bbbb2222cccc",
      updatedAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z")
    };
  }
};
var baseDocumentFactory = new BaseDocumentFactory();

// src/test-factories/document-template.factory.ts
var DocumentTemplateFactory = class extends ObjectFactory {
  getDefaults() {
    const base = baseDocumentFactory.build();
    return {
      ...base,
      blobPath: "document-templates/11111111-1111-1111-1111-111111111111",
      containerName: base.tenantId,
      contentType: DOCUMENT_TEMPLATE_MIME_TYPE,
      fileName: "proposal.docx",
      isSelected: false,
      name: "Proposal",
      owner: { id: base.tenantId, type: DOCUMENT_OWNER_TYPE.TENANT },
      placeholders: ["customerName", "quotation", "total"],
      sizeBytes: 32768,
      type: DOCUMENT_TYPE.DOCUMENT_TEMPLATE,
      uploadedAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z")
    };
  }
};
var documentTemplateFactory = new DocumentTemplateFactory();

// src/test-factories/contract.factory.ts
var completeLine = () => ({
  annualMileage: 2e4,
  dealerSupplierId: "507f1f77bcf86cd799439015",
  engineId: "ENG-1800-HYBRID",
  extraKmPrice: 0.12,
  insurancePackageId: "507f1f77bcf86cd799439017",
  invoicedMonthlyNet: 450,
  makeId: "507f1f77bcf86cd799439013",
  modelId: "507f1f77bcf86cd799439014",
  quantity: 2,
  servicePackageId: "507f1f77bcf86cd799439016",
  termMonths: 36,
  unitPrice: 25e3
});
var ContractFactory = class extends ObjectFactory {
  getDefaults() {
    const lines = [completeLine()];
    return {
      ...baseDocumentFactory.build(),
      ...computeContractTotals(lines, DEFAULT_TERRITORY_VAT_RATE, CONTRACT_DEFAULTS.vatExempt),
      accountManagerId: "507f1f77bcf86cd799439012",
      archiveReason: null,
      archivedAt: null,
      customerId: "507f1f77bcf86cd799439018",
      depositMonths: CONTRACT_DEFAULTS.depositMonths,
      document: null,
      duplicatedFromContractId: null,
      duplicatedFromNumber: null,
      endDate: /* @__PURE__ */ new Date("2029-10-31T00:00:00.000Z"),
      exemptionReference: null,
      extraFees: [],
      feeRebills: [],
      kind: CONTRACT_DEFAULTS.kind,
      leaseMode: CONTRACT_DEFAULTS.leaseMode,
      lines,
      masterContractId: null,
      mileageAllowanceMode: CONTRACT_DEFAULTS.mileageAllowanceMode,
      number: "MA-26-0001",
      paymentTerms: {
        gracePeriodDays: CONTRACT_DEFAULTS.gracePeriodDays,
        invoicingCycle: "Monthly in advance",
        lateInterestPercent: 9,
        paymentDue: "Net 30"
      },
      sentAt: null,
      signature: null,
      startDate: /* @__PURE__ */ new Date("2026-11-01T00:00:00.000Z"),
      status: CONTRACT_STATUS.READY_TO_SEND,
      statusBeforeArchive: null,
      territoryId: "507f1f77bcf86cd799439011",
      vatExempt: CONTRACT_DEFAULTS.vatExempt,
      vatRatePercent: DEFAULT_TERRITORY_VAT_RATE,
      version: 1
    };
  }
  /** Build a `CreateContract` payload (no base-document fields). */
  buildCreate(overrides = {}) {
    const { createdAt, deletedAt, id, tenantId, updatedAt, ...create } = this.getDefaults();
    return { ...create, ...overrides };
  }
};
var contractFactory = new ContractFactory();
var ContractListItemFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      addendaCount: 0,
      addendaInProgress: 0,
      currency: TERRITORY_CURRENCY.USD,
      customerId: "507f1f77bcf86cd799439018",
      customerName: "Malia Holding",
      id: "66f0000000000000000000a1",
      kind: CONTRACT_KIND.MASTER,
      monthlyGross: 999,
      number: "MA-26-0010",
      sentAt: null,
      status: CONTRACT_STATUS.READY_TO_SEND,
      statusBeforeArchive: null,
      updatedAt: "2026-10-22T09:14:00.000Z",
      vehicleCount: 2,
      version: 1
    };
  }
};
var contractListItemFactory = new ContractListItemFactory();

// src/test-factories/customer.factory.ts
var CustomerFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      contactPerson: "Lina Mrad",
      email: "fleet@cedarlogistics.com",
      name: "Cedar Logistics SAL",
      phone: "+961 1 480 220",
      status: CUSTOMER_STATUS.ACTIVE,
      taxId: "LB-4471209",
      territoryId: "507f1f77bcf86cd799439011",
      type: CUSTOMER_TYPE.COMPANY
    };
  }
  /** Build a CreateCustomer body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      contactPerson: "Lina Mrad",
      email: "fleet@cedarlogistics.com",
      name: "Cedar Logistics SAL",
      phone: "+961 1 480 220",
      status: CUSTOMER_STATUS.ACTIVE,
      taxId: "LB-4471209",
      territoryId: "507f1f77bcf86cd799439011",
      type: CUSTOMER_TYPE.COMPANY,
      ...overrides
    };
  }
  /** Build an INDIVIDUAL customer — no contactPerson, no taxId (AC#3). */
  buildIndividual(overrides = {}) {
    return {
      ...baseDocumentFactory.build(),
      email: "hala.fares@gmail.com",
      name: "Hala Fares",
      phone: "+961 70 559 803",
      status: CUSTOMER_STATUS.ACTIVE,
      territoryId: "507f1f77bcf86cd799439011",
      type: CUSTOMER_TYPE.INDIVIDUAL,
      ...overrides
    };
  }
};
var customerFactory = new CustomerFactory();

// src/test-factories/magic-link.factory.ts
var MagicLinkFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      attachments: [
        {
          attachmentId: "3f2504e0-4f89-41d3-9a0c-0305e82c3301",
          blobPath: "magic-links/3f2504e0-4f89-41d3-9a0c-0305e82c3301.pdf",
          containerName: "aaaa00001111bbbb2222cccc",
          contentType: "application/pdf",
          fileName: "lease-contract.pdf",
          sizeBytes: 204800,
          version: 1
        }
      ],
      deliveries: [
        {
          address: "customer@example.com",
          channel: MAGIC_LINK_CHANNEL.EMAIL,
          providerMessageId: null,
          sentAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z")
        }
      ],
      expiresAt: new Date(Date.now() + MAGIC_LINK_DEFAULT_EXPIRY_IN_MS),
      firstOpenedAt: null,
      purpose: MAGIC_LINK_PURPOSE.DOCUMENT_REVIEW,
      reference: "REV-0001A2B3",
      respondedAt: null,
      response: null,
      revokedAt: null,
      subject: { id: null, type: MAGIC_LINK_SUBJECT_TYPE.NONE },
      tokenHash: "0".repeat(64)
    };
  }
};
var magicLinkFactory = new MagicLinkFactory();
var MagicLinkRequestViewFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      attachments: [
        {
          attachmentId: "3f2504e0-4f89-41d3-9a0c-0305e82c3301",
          contentType: "application/pdf",
          fileName: "lease-contract.pdf",
          sizeBytes: 204800,
          version: 1
        }
      ],
      deliveries: [
        {
          address: "customer@example.com",
          channel: MAGIC_LINK_CHANNEL.EMAIL,
          sentAt: "2026-01-01T00:00:00.000Z"
        }
      ],
      expiresAt: "2026-01-02T00:00:00.000Z",
      firstOpenedAt: null,
      id: "bbbb00001111cccc2222dddd",
      purpose: MAGIC_LINK_PURPOSE.DOCUMENT_REVIEW,
      reference: "REV-0001A2B3",
      respondedAt: null,
      response: null,
      state: "awaiting"
    };
  }
};
var magicLinkRequestViewFactory = new MagicLinkRequestViewFactory();

// src/test-factories/driver.factory.ts
var DriverFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      licenseExpiry: null,
      licenseNumber: "DL-123456",
      locationIds: [],
      status: DRIVER_STATUS.ACTIVE,
      territoryId: "aaaaaaaaaaaaaaaaaaaaaaaa",
      userId: "bbbbbbbbbbbbbbbbbbbbbbbb"
    };
  }
  /** Build a CreateDriver payload (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      licenseExpiry: null,
      licenseNumber: "DL-123456",
      locationIds: [],
      status: DRIVER_STATUS.ACTIVE,
      territoryId: "aaaaaaaaaaaaaaaaaaaaaaaa",
      userId: "bbbbbbbbbbbbbbbbbbbbbbbb",
      ...overrides
    };
  }
};
var driverFactory = new DriverFactory();

// src/test-factories/driver-list-item.factory.ts
var DriverListItemFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...driverFactory.build(),
      userEmail: "jane.doe@example.com",
      userFirstName: "Jane",
      userLastName: "Doe",
      userPhone: null
    };
  }
};
var driverListItemFactory = new DriverListItemFactory();

// src/test-factories/energy-price.factory.ts
var EnergyPriceByTerritoryFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      component: PRICE_COMPONENT.SINGLE,
      endDate: null,
      energySourceId: ENERGY_SOURCE_IDS.GAS,
      price: { currency: TERRITORY_CURRENCY.EUR, value: 1.85 },
      startDate: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      territoryId: "aaaa00001111bbbb2222dddd",
      unit: PRICE_UNIT.PER_LITER
    };
  }
  /** Build a CreateEnergyPriceByTerritory payload (no base document fields; currency is snapshotted by the service). */
  buildCreate(overrides = {}) {
    return {
      component: PRICE_COMPONENT.SINGLE,
      endDate: null,
      energySourceId: ENERGY_SOURCE_IDS.GAS,
      startDate: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      territoryId: "aaaa00001111bbbb2222dddd",
      unit: PRICE_UNIT.PER_LITER,
      value: 1.85,
      ...overrides
    };
  }
};
var energyPriceByTerritoryFactory = new EnergyPriceByTerritoryFactory();

// src/test-factories/energy-source.factory.ts
var EnergySourceFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      category: ENERGY_SOURCE_CATEGORY.COMBUSTION,
      id: ENERGY_SOURCE_IDS.GAS,
      description: "Refined petroleum fuel for spark-ignition engines",
      label: { ar: "\u0628\u0646\u0632\u064A\u0646", en: "Gasoline / Petrol", fr: "Essence" },
      priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_LITER }]
    };
  }
  /** Build a CreateEnergySource payload (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      category: ENERGY_SOURCE_CATEGORY.COMBUSTION,
      id: ENERGY_SOURCE_IDS.GAS,
      description: "Refined petroleum fuel for spark-ignition engines",
      label: { ar: "\u0628\u0646\u0632\u064A\u0646", en: "Gasoline / Petrol", fr: "Essence" },
      priceComponents: [{ component: PRICE_COMPONENT.SINGLE, unit: PRICE_UNIT.PER_LITER }],
      ...overrides
    };
  }
};
var energySourceFactory = new EnergySourceFactory();

// src/test-factories/expense-category.factory.ts
var ExpenseCategoryFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "FUEL_ENERGY",
      description: "Fuel and energy costs",
      family: EXPENSE_CATEGORY_FAMILY.OPERATIONAL,
      label: "Fuel / Energy",
      origin: EXPENSE_CATEGORY_ORIGIN.PREDEFINED,
      status: EXPENSE_CATEGORY_STATUS.ACTIVE
    };
  }
  /** Build a CreateExpenseCategory payload (no base document fields, no origin — forced server-side). */
  buildCreate(overrides = {}) {
    return {
      code: "PARKING",
      description: "Parking fees",
      family: EXPENSE_CATEGORY_FAMILY.OPERATIONAL,
      label: "Parking",
      status: EXPENSE_CATEGORY_STATUS.ACTIVE,
      ...overrides
    };
  }
};
var expenseCategoryFactory = new ExpenseCategoryFactory();

// src/test-factories/vehicle-expense.factory.ts
var VehicleExpenseFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      categoryId: "cccccccccccccccccccccccc",
      createdBy: "dddddddddddddddddddddddd",
      currency: TERRITORY_CURRENCY.EUR,
      date: /* @__PURE__ */ new Date("2026-06-01T00:00:00.000Z"),
      description: "Full tank",
      driverId: null,
      loggedVia: EXPENSE_LOGGED_VIA.WEB,
      netAmount: 100,
      vatAmount: 20,
      vehicleId: "eeeeeeeeeeeeeeeeeeeeeeee"
    };
  }
  /**
   * Build a CreateVehicleExpense payload (no base document fields, no currency / loggedVia /
   * createdBy — all set server-side).
   */
  buildCreate(overrides = {}) {
    return {
      categoryId: "cccccccccccccccccccccccc",
      date: /* @__PURE__ */ new Date("2026-06-01T00:00:00.000Z"),
      description: "Full tank",
      driverId: null,
      netAmount: 100,
      vatAmount: 20,
      vehicleId: "eeeeeeeeeeeeeeeeeeeeeeee",
      ...overrides
    };
  }
};
var vehicleExpenseFactory = new VehicleExpenseFactory();

// src/test-factories/vehicle-expense-list-item.factory.ts
var VehicleExpenseListItemFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...vehicleExpenseFactory.build(),
      attachmentCount: 0,
      driverName: "Jane Doe"
    };
  }
};
var vehicleExpenseListItemFactory = new VehicleExpenseListItemFactory();

// src/test-factories/order.factory.ts
var OrderFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      customer: { id: "aaaaaaaaaaaaaaaaaaaaaaaa", firstName: "John", lastName: "Doe" },
      delivery: {
        site: { code: "SITE01", color: "#000", id: "site-1", name: "Warehouse" }
      },
      orderType: { code: "STD", id: "ot-1", label: "Standard" },
      pickupSite: { code: "PICKUP01", color: "#fff", id: "site-2", name: "Pickup Point" },
      referenceNumber: "REF-001",
      shipperCode: "SHIPPER01",
      siteId: "site-1",
      spaceId: "space-1",
      status: ORDER_STATUS.NEW,
      transportType: TRANSPORT_TYPE.DELIVERY
    };
  }
  /** Build a CreateOrder body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      customer: { firstName: "John", lastName: "Doe" },
      delivery: { durationTime: 30, siteCode: "DEL01" },
      orderTypeCode: "STD",
      pickup: { durationTime: 15, siteCode: "PICKUP01" },
      referenceNumber: "REF-001",
      shipperCode: "SHIPPER01",
      siteCode: "SITE01",
      transportType: TRANSPORT_TYPE.DELIVERY,
      ...overrides
    };
  }
};
var orderFactory = new OrderFactory();

// src/test-factories/email-template.factory.ts
var EmailTemplateFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      templateId: "d-sendgrid-template-id",
      templateName: "welcome-email"
    };
  }
};
var emailTemplateFactory = new EmailTemplateFactory();

// src/test-factories/emitted-email.factory.ts
var EmittedEmailFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      events: [],
      identifiers: { tenantId: "tenant-test" },
      responseTopic: "",
      sgMessageId: "",
      status: EMAIL_STATUS.Pending,
      templateData: { name: "John" },
      templateName: "welcome-email",
      to: "john@example.com"
    };
  }
};
var emittedEmailFactory = new EmittedEmailFactory();

// src/test-factories/firebase-token.factory.ts
var FirebaseDecodedIdTokenFactory = class extends ObjectFactory {
  getDefaults() {
    const now = Math.floor(Date.now() / 1e3);
    return {
      aud: "hetivo-dev",
      auth_time: now,
      email: "test@example.com",
      email_verified: true,
      exp: now + 3600,
      firebase: {
        identities: {},
        sign_in_provider: "microsoft.com"
      },
      iat: now,
      iss: "https://securetoken.google.com/hetivo-dev",
      name: "Test User",
      sub: "firebase-uid-001",
      uid: "firebase-uid-001"
    };
  }
};
var firebaseTokenFactory = new FirebaseDecodedIdTokenFactory();

// src/test-factories/inspection.factory.ts
var InspectionRecordFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      completedAt: null,
      dueOn: toCalendarDay(/* @__PURE__ */ new Date()),
      schedule: INSPECTION_SCHEDULE.MONTHLY,
      source: INSPECTION_SOURCE.SCHEDULED,
      startedAt: null,
      startedBy: null,
      status: INSPECTION_STATUS.DUE,
      templateId: "507f1f77bcf86cd799439011",
      templateVersion: null,
      vehicleId: "507f1f77bcf86cd799439051"
    };
  }
};
var inspectionRecordFactory = new InspectionRecordFactory();
var inProgressInspection = (overrides = {}) => inspectionRecordFactory.build({
  startedAt: /* @__PURE__ */ new Date(),
  startedBy: "507f1f77bcf86cd799439061",
  status: INSPECTION_STATUS.IN_PROGRESS,
  templateVersion: 1,
  ...overrides
});
var completedInspection = (overrides = {}) => inProgressInspection({
  completedAt: /* @__PURE__ */ new Date(),
  status: INSPECTION_STATUS.COMPLETED,
  ...overrides
});

// src/test-factories/inspection-template.factory.ts
function anyInspectionAppliesTo(overrides = {}) {
  return { makeId: null, model: null, trimModelId: null, ...overrides };
}
function defaultSections() {
  return [
    {
      id: "507f1f77bcf86cd799439021",
      items: [
        { id: "507f1f77bcf86cd799439031", kind: INSPECTION_ITEM_KIND.PASS_FAIL, label: "Tyres in good condition" }
      ],
      name: "Exterior"
    }
  ];
}
var InspectionTemplateFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      appliesTo: anyInspectionAppliesTo(),
      createdAt: "2026-01-01T00:00:00.000Z",
      id: "507f1f77bcf86cd799439011",
      name: "Weekly check",
      schedule: INSPECTION_SCHEDULE.ON_DEMAND,
      sections: defaultSections(),
      updatedAt: "2026-01-01T00:00:00.000Z",
      version: 1
    };
  }
};
var InspectionTemplateRecordFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      appliesTo: anyInspectionAppliesTo(),
      name: "Weekly check",
      nameKey: "weekly check",
      schedule: INSPECTION_SCHEDULE.ON_DEMAND,
      sections: defaultSections(),
      version: 1
    };
  }
};
var InspectionTemplateVersionRecordFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      appliesTo: anyInspectionAppliesTo(),
      createdBy: "507f1f77bcf86cd799439041",
      name: "Weekly check",
      schedule: INSPECTION_SCHEDULE.ON_DEMAND,
      sections: defaultSections(),
      templateId: "507f1f77bcf86cd799439011",
      version: 1
    };
  }
};
var inspectionTemplateFactory = new InspectionTemplateFactory();
var inspectionTemplateRecordFactory = new InspectionTemplateRecordFactory();
var inspectionTemplateVersionRecordFactory = new InspectionTemplateVersionRecordFactory();

// src/test-factories/insurance-package.factory.ts
var InsurancePackageFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      cover: "Third-party liability and theft",
      deductible: 500,
      name: "Comprehensive",
      nameKey: "comprehensive",
      ratePercent: 3.25,
      status: INSURANCE_PACKAGE_STATUS.active,
      territoryId: "507f1f77bcf86cd799439011"
    };
  }
  /** Build a `CreateInsurancePackage` payload (no base-document fields). */
  buildCreate(overrides = {}) {
    const { cover, deductible, name, nameKey, ratePercent, status, territoryId } = this.getDefaults();
    return { cover, deductible, name, nameKey, ratePercent, status, territoryId, ...overrides };
  }
};
var insurancePackageFactory = new InsurancePackageFactory();

// src/test-factories/invitation.factory.ts
var InvitationFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      activationLink: "http://localhost:3100/frontoffice/activate?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9",
      channel: INVITATION_CHANNEL.EMAIL,
      email: "test@example.com",
      expiresAt: new Date(Date.now() + INVITATION_EXPIRY_DAYS_IN_MS),
      lastSentAt: null,
      status: INVITATION_STATUS.CREATED,
      tokenNonce: 1,
      userId: "aabbccddeeff001122334455"
    };
  }
};
var invitationFactory = new InvitationFactory();

// src/test-factories/issue.factory.ts
var VEHICLE_ID = "eeeeeeeeeeeeeeeeeeeeeeee";
var REPORTER_ID = "dddddddddddddddddddddddd";
var REPORTED_AT = /* @__PURE__ */ new Date("2026-08-20T07:40:00.000Z");
var IssueFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      description: "ABS light came on during the morning run and stayed on after restarting.",
      number: 1187,
      odometerKm: 23980,
      reportedAt: REPORTED_AT,
      reporterId: REPORTER_ID,
      resolutionNote: null,
      resolvedAt: null,
      status: ISSUE_STATUS.OPEN,
      title: "ABS warning light stays on",
      type: ISSUE_TYPE.FAULT,
      vehicleId: VEHICLE_ID,
      workOrderId: null,
      workOrderNumber: null
    };
  }
  /** The fields the issue service writes on create (no base document fields). */
  buildCreate(overrides = {}) {
    const issue = this.build();
    return {
      description: issue.description,
      number: issue.number,
      odometerKm: issue.odometerKm,
      reportedAt: issue.reportedAt,
      reporterId: issue.reporterId,
      resolutionNote: issue.resolutionNote,
      resolvedAt: issue.resolvedAt,
      status: issue.status,
      title: issue.title,
      type: issue.type,
      vehicleId: issue.vehicleId,
      workOrderId: issue.workOrderId,
      workOrderNumber: issue.workOrderNumber,
      ...overrides
    };
  }
  /** A `POST /issues` body. */
  buildReportBody(overrides = {}) {
    return {
      description: "ABS light came on during the morning run and stayed on after restarting.",
      odometerKm: 23980,
      reportedAt: "2026-08-20",
      title: "ABS warning light stays on",
      type: ISSUE_TYPE.FAULT,
      vehicleId: VEHICLE_ID,
      ...overrides
    };
  }
};
var issueFactory = new IssueFactory();
var IssueTimelineEntryFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      actorId: REPORTER_ID,
      at: REPORTED_AT,
      issueId: "ffffffffffffffffffffffff",
      issueType: ISSUE_TYPE.FAULT,
      kind: ISSUE_TIMELINE_KIND.REPORTED
    };
  }
};
var issueTimelineEntryFactory = new IssueTimelineEntryFactory();
var IssueTimelineEntryViewFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      actor: { id: REPORTER_ID, name: "Rami Khoury" },
      at: REPORTED_AT,
      id: baseDocumentFactory.build().id,
      issueType: ISSUE_TYPE.FAULT,
      kind: ISSUE_TIMELINE_KIND.REPORTED
    };
  }
};
var issueTimelineEntryViewFactory = new IssueTimelineEntryViewFactory();
var IssueListItemFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      displayNumber: formatIssueNumber(1187),
      id: baseDocumentFactory.build().id,
      number: 1187,
      reportedAt: REPORTED_AT,
      reporter: { id: REPORTER_ID, name: "Rami Khoury" },
      resolvedAt: null,
      status: ISSUE_STATUS.OPEN,
      title: "ABS warning light stays on",
      type: ISSUE_TYPE.FAULT,
      vehicle: {
        id: VEHICLE_ID,
        licensePlate: "132847 / B",
        makeName: { ar: "\u062A\u0648\u064A\u0648\u062A\u0627", en: "Toyota", fr: "Toyota" },
        model: "Corolla",
        vehicleId: "1042",
        year: 2022
      },
      workOrderId: null,
      workOrderNumber: null
    };
  }
};
var issueListItemFactory = new IssueListItemFactory();
var IssueDetailFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...issueListItemFactory.build(),
      description: "ABS light came on during the morning run and stayed on after restarting.",
      odometerKm: 23980,
      resolutionNote: null,
      timeline: [issueTimelineEntryViewFactory.build()]
    };
  }
};
var issueDetailFactory = new IssueDetailFactory();
var IssueStatsFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      kpis: { inProgress: 2, open: 2, pastTarget: 2, resolvedThisMonth: 2 },
      tabs: { all: 6, inProgress: 2, open: 2, resolved: 2 }
    };
  }
};
var issueStatsFactory = new IssueStatsFactory();

// src/test-factories/leasing-settings.factory.ts
var LeasingSettingsFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...LEASING_SETTINGS_DEFAULTS,
      terms: [...LEASING_SETTINGS_DEFAULTS.terms],
      annualMileages: [...LEASING_SETTINGS_DEFAULTS.annualMileages],
      invoicingCycles: [...LEASING_SETTINGS_DEFAULTS.invoicingCycles],
      paymentDueOptions: [...LEASING_SETTINGS_DEFAULTS.paymentDueOptions],
      extraFees: LEASING_SETTINGS_DEFAULTS.extraFees.map((fee) => ({ ...fee }))
    };
  }
};
var leasingSettingsFactory = new LeasingSettingsFactory();

// src/test-factories/location.factory.ts
var divisionCounter = 0;
var LocationFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      color: "#FF5733",
      name: "Main Shop",
      status: LOCATION_STATUS.ACTIVE,
      territoryId: "territory-id"
    };
  }
  /** Build a CreateLocation body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      color: "#FF5733",
      name: "Main Shop",
      status: LOCATION_STATUS.ACTIVE,
      territoryId: "territory-id",
      ...overrides
    };
  }
  /** Build an embedded Division sub-document. */
  buildDivision(overrides = {}) {
    divisionCounter += 1;
    return {
      // Unique ObjectId-shaped default so repeat calls don't collide.
      _id: divisionCounter.toString(16).padStart(24, "0"),
      name: "Division A",
      status: DIVISION_STATUS.ACTIVE,
      ...overrides
    };
  }
};
var locationFactory = new LocationFactory();

// src/test-factories/maintenance-settings.factory.ts
var MaintenanceSettingsFactory = class extends ObjectFactory {
  getDefaults() {
    return { ...MAINTENANCE_SETTINGS_DEFAULTS };
  }
};
var maintenanceSettingsFactory = new MaintenanceSettingsFactory();

// src/test-factories/pool-type.factory.ts
var PoolTypeFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      description: "Shared fleet pool",
      label: "Global",
      status: POOL_TYPE_STATUS.ACTIVE
    };
  }
  /** Build a CreatePoolType body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      description: "Shared fleet pool",
      label: "Global",
      status: POOL_TYPE_STATUS.ACTIVE,
      ...overrides
    };
  }
};
var poolTypeFactory = new PoolTypeFactory();

// src/test-factories/relief.factory.ts
var ReliefFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      agreedReturnDate: /* @__PURE__ */ new Date("2026-10-11T00:00:00.000Z"),
      agreementId: "a1a1a1a1a1a1a1a1a1a1a1a1",
      chargeAmount: 25,
      chargePeriods: [],
      chargeType: RELIEF_CHARGE_TYPE.PER_DAY,
      closedAt: null,
      contractId: "c1c1c1c1c1c1c1c1c1c1c1c1",
      currency: TERRITORY_CURRENCY.USD,
      customerId: "c2c2c2c2c2c2c2c2c2c2c2c2",
      daysKept: null,
      driverId: "d1d1d1d1d1d1d1d1d1d1d1d1",
      driverSnapshot: {
        licenseExpiry: /* @__PURE__ */ new Date("2030-01-01T00:00:00.000Z"),
        licenseNumber: "LIC-123",
        name: "Jane Driver",
        phone: "+961 70 000 000"
      },
      handedOverAt: null,
      handoverDate: /* @__PURE__ */ new Date("2026-10-01T00:00:00.000Z"),
      handoverTime: "09:00",
      invoiceStatus: null,
      noteDownloadedAt: null,
      noteDownloadedByUserId: null,
      odometerReturn: null,
      odometerStart: 12e3,
      overrunDays: null,
      overrunRatePerDay: 25,
      returnDate: null,
      signedNoteDocumentId: null,
      status: RELIEF_STATUS.PREPARED,
      territoryId: "e1e1e1e1e1e1e1e1e1e1e1e1",
      totalGross: null,
      totalNet: null,
      totalVat: null,
      vatExempt: false,
      vatRatePercent: 11,
      vehicleId: "f1f1f1f1f1f1f1f1f1f1f1f1",
      vehicleSnapshot: {
        licensePlate: "ABC-123",
        makeName: "Renault",
        modelName: "Clio"
      }
    };
  }
};
var ReliefViewFactory = class extends ObjectFactory {
  getDefaults() {
    const relief = new ReliefFactory().build();
    return {
      agreedReturnDate: "2026-10-11",
      agreementId: relief.agreementId,
      agreementNumber: "MA-26-0012-01-01",
      chargeAmount: relief.chargeAmount,
      chargePeriods: [],
      chargeType: relief.chargeType,
      closedAt: null,
      contractId: relief.contractId,
      createdAt: "2026-10-01T00:00:00.000Z",
      currency: relief.currency,
      customerId: relief.customerId,
      daysKept: null,
      driverId: relief.driverId,
      driverSnapshot: { ...relief.driverSnapshot, licenseExpiry: "2030-01-01" },
      handedOverAt: null,
      handoverDate: "2026-10-01",
      handoverTime: relief.handoverTime,
      id: relief.id,
      invoiceStatus: null,
      liveOverrunDays: 0,
      noteDownloadedAt: null,
      noteDownloadedByUserId: null,
      odometerReturn: null,
      odometerStart: relief.odometerStart,
      overrunDays: null,
      overrunRatePerDay: relief.overrunRatePerDay,
      returnDate: null,
      signedNoteDocumentId: null,
      signedNoteFileName: null,
      status: relief.status,
      territoryId: relief.territoryId,
      totalGross: null,
      totalNet: null,
      totalVat: null,
      updatedAt: "2026-10-01T00:00:00.000Z",
      vatExempt: relief.vatExempt,
      vatRatePercent: relief.vatRatePercent,
      vehicleId: relief.vehicleId,
      vehicleSnapshot: relief.vehicleSnapshot
    };
  }
};
var reliefFactory = new ReliefFactory();
var reliefViewFactory = new ReliefViewFactory();

// src/test-factories/role.factory.ts
var RoleFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      key: "TENANT_ADMIN",
      permissions: ["permissions:read", "roles:read", "roles:write", "users:read", "users:write"]
    };
  }
};
var roleFactory = new RoleFactory();

// src/test-factories/service-operation.factory.ts
var ServiceOperationFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      category: SERVICE_OPERATION_CATEGORY.LUBRICATION,
      code: "ENGINE_OIL_REPLACEMENT",
      defaultParts: "Engine oil 5 L",
      name: "Engine oil replacement",
      origin: SERVICE_OPERATION_ORIGIN.PREDEFINED
    };
  }
  /** Build a CreateServiceOperation payload (no base document fields, no origin — forced server-side). */
  buildCreate(overrides = {}) {
    return {
      category: SERVICE_OPERATION_CATEGORY.TYRES,
      code: "TYRE_ROTATION",
      name: "Tyre rotation",
      ...overrides
    };
  }
};
var serviceOperationFactory = new ServiceOperationFactory();

// src/test-factories/service-package.factory.ts
var ServicePackageFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "PKG-01",
      dailyPrice: 25,
      name: "VIP",
      nameKey: "vip",
      replacementCar: {
        countPerContract: 1,
        included: true,
        maxDays: 10,
        restriction: REPLACEMENT_CAR_RESTRICTION.sameCategory
      },
      status: SERVICE_PACKAGE_STATUS.active,
      territoryId: "507f1f77bcf86cd799439011",
      tyreChange: { basis: TYRE_CHANGE_BASIS.perYear, count: 2, included: true, intervalKm: null }
    };
  }
  /** Build a `CreateServicePackage` payload (no base-document fields). */
  buildCreate(overrides = {}) {
    const { code, dailyPrice, name, nameKey, replacementCar, status, territoryId, tyreChange } = this.getDefaults();
    return { code, dailyPrice, name, nameKey, replacementCar, status, territoryId, tyreChange, ...overrides };
  }
};
var servicePackageFactory = new ServicePackageFactory();

// src/vehicle-make/vehicle-make.constants.ts
var VEHICLE_MAKE_IDS = {
  TOYOTA: "adadadadadadadadadadad01",
  HONDA: "adadadadadadadadadadad02",
  NISSAN: "adadadadadadadadadadad03",
  FORD: "adadadadadadadadadadad04",
  CHEV: "adadadadadadadadadadad05",
  HYUNDAI: "adadadadadadadadadadad06",
  KIA: "adadadadadadadadadadad07",
  VOLKS: "adadadadadadadadadadad08",
  RENAULT: "adadadadadadadadadadad09",
  PEUG: "adadadadadadadadadadad0a",
  FIAT: "adadadadadadadadadadad0b",
  SKODA: "adadadadadadadadadadad0c",
  MAZDA: "adadadadadadadadadadad0d",
  SUBARU: "adadadadadadadadadadad0e",
  SUZUKI: "adadadadadadadadadadad0f",
  BMW: "adadadadadadadadadadad10",
  MERC: "adadadadadadadadadadad11",
  AUDI: "adadadadadadadadadadad12",
  LEXUS: "adadadadadadadadadadad13",
  PORSCHE: "adadadadadadadadadadad14",
  VOLVO: "adadadadadadadadadadad15",
  JAG: "adadadadadadadadadadad16",
  LANDR: "adadadadadadadadadadad17",
  TESLA: "adadadadadadadadadadad18",
  BYD: "adadadadadadadadadadad19",
  RIVIAN: "adadadadadadadadadadad1a",
  NIO: "adadadadadadadadadadad1b",
  POLESTAR: "adadadadadadadadadadad1c",
  ISUZU: "adadadadadadadadadadad1d",
  MAN: "adadadadadadadadadadad1e",
  SCANIA: "adadadadadadadadadadad1f",
  DAF: "adadadadadadadadadadad20",
  IVECO: "adadadadadadadadadadad21",
  HINO: "adadadadadadadadadadad22",
  MACK: "adadadadadadadadadadad23",
  FREIGHT: "adadadadadadadadadadad24"
};
var VEHICLE_MAKES = [
  // ─── Passenger / EV ──────────────────────────────────────────────────────────
  { id: VEHICLE_MAKE_IDS.TOYOTA, label: { ar: "Toyota", en: "Toyota", fr: "Toyota" } },
  { id: VEHICLE_MAKE_IDS.HONDA, label: { ar: "Honda", en: "Honda", fr: "Honda" } },
  { id: VEHICLE_MAKE_IDS.NISSAN, label: { ar: "Nissan", en: "Nissan", fr: "Nissan" } },
  { id: VEHICLE_MAKE_IDS.FORD, label: { ar: "Ford", en: "Ford", fr: "Ford" } },
  { id: VEHICLE_MAKE_IDS.CHEV, label: { ar: "Chevrolet", en: "Chevrolet", fr: "Chevrolet" } },
  { id: VEHICLE_MAKE_IDS.HYUNDAI, label: { ar: "Hyundai", en: "Hyundai", fr: "Hyundai" } },
  { id: VEHICLE_MAKE_IDS.KIA, label: { ar: "Kia", en: "Kia", fr: "Kia" } },
  { id: VEHICLE_MAKE_IDS.VOLKS, label: { ar: "Volkswagen", en: "Volkswagen", fr: "Volkswagen" } },
  { id: VEHICLE_MAKE_IDS.RENAULT, label: { ar: "Renault", en: "Renault", fr: "Renault" } },
  { id: VEHICLE_MAKE_IDS.PEUG, label: { ar: "Peugeot", en: "Peugeot", fr: "Peugeot" } },
  { id: VEHICLE_MAKE_IDS.FIAT, label: { ar: "Fiat", en: "Fiat", fr: "Fiat" } },
  { id: VEHICLE_MAKE_IDS.SKODA, label: { ar: "\u0160koda", en: "\u0160koda", fr: "\u0160koda" } },
  { id: VEHICLE_MAKE_IDS.MAZDA, label: { ar: "Mazda", en: "Mazda", fr: "Mazda" } },
  { id: VEHICLE_MAKE_IDS.SUBARU, label: { ar: "Subaru", en: "Subaru", fr: "Subaru" } },
  { id: VEHICLE_MAKE_IDS.SUZUKI, label: { ar: "Suzuki", en: "Suzuki", fr: "Suzuki" } },
  { id: VEHICLE_MAKE_IDS.BMW, label: { ar: "BMW", en: "BMW", fr: "BMW" } },
  { id: VEHICLE_MAKE_IDS.MERC, label: { ar: "Mercedes-Benz", en: "Mercedes-Benz", fr: "Mercedes-Benz" } },
  { id: VEHICLE_MAKE_IDS.AUDI, label: { ar: "Audi", en: "Audi", fr: "Audi" } },
  { id: VEHICLE_MAKE_IDS.LEXUS, label: { ar: "Lexus", en: "Lexus", fr: "Lexus" } },
  { id: VEHICLE_MAKE_IDS.PORSCHE, label: { ar: "Porsche", en: "Porsche", fr: "Porsche" } },
  { id: VEHICLE_MAKE_IDS.VOLVO, label: { ar: "Volvo", en: "Volvo", fr: "Volvo" } },
  { id: VEHICLE_MAKE_IDS.JAG, label: { ar: "Jaguar", en: "Jaguar", fr: "Jaguar" } },
  { id: VEHICLE_MAKE_IDS.LANDR, label: { ar: "Land Rover", en: "Land Rover", fr: "Land Rover" } },
  { id: VEHICLE_MAKE_IDS.TESLA, label: { ar: "Tesla", en: "Tesla", fr: "Tesla" } },
  { id: VEHICLE_MAKE_IDS.BYD, label: { ar: "BYD", en: "BYD", fr: "BYD" } },
  { id: VEHICLE_MAKE_IDS.RIVIAN, label: { ar: "Rivian", en: "Rivian", fr: "Rivian" } },
  { id: VEHICLE_MAKE_IDS.NIO, label: { ar: "NIO", en: "NIO", fr: "NIO" } },
  { id: VEHICLE_MAKE_IDS.POLESTAR, label: { ar: "Polestar", en: "Polestar", fr: "Polestar" } },
  // ─── Commercial / Heavy ──────────────────────────────────────────────────────
  { id: VEHICLE_MAKE_IDS.ISUZU, label: { ar: "Isuzu", en: "Isuzu", fr: "Isuzu" } },
  { id: VEHICLE_MAKE_IDS.MAN, label: { ar: "MAN", en: "MAN", fr: "MAN" } },
  { id: VEHICLE_MAKE_IDS.SCANIA, label: { ar: "Scania", en: "Scania", fr: "Scania" } },
  { id: VEHICLE_MAKE_IDS.DAF, label: { ar: "DAF", en: "DAF", fr: "DAF" } },
  { id: VEHICLE_MAKE_IDS.IVECO, label: { ar: "Iveco", en: "Iveco", fr: "Iveco" } },
  { id: VEHICLE_MAKE_IDS.HINO, label: { ar: "Hino", en: "Hino", fr: "Hino" } },
  { id: VEHICLE_MAKE_IDS.MACK, label: { ar: "Mack", en: "Mack", fr: "Mack" } },
  { id: VEHICLE_MAKE_IDS.FREIGHT, label: { ar: "Freightliner", en: "Freightliner", fr: "Freightliner" } }
];

// src/test-factories/service-plan.factory.ts
function anyApplicability(overrides = {}) {
  return {
    energySourceId: null,
    engine: null,
    makeId: null,
    model: null,
    transmission: null,
    trimModelId: null,
    year: { mode: SERVICE_PLAN_YEAR_MODE.ALL },
    ...overrides
  };
}
function buildInterval(overrides = {}) {
  return {
    code: "A",
    graceDays: 14,
    graceKm: 500,
    items: [{ notes: void 0, parts: void 0, required: true, serviceOperationId: "507f1f77bcf86cd799439011" }],
    mileageKm: 15e3,
    months: 12,
    name: "Service A",
    trigger: SERVICE_PLAN_INTERVAL_TRIGGER.WHICHEVER_FIRST,
    ...overrides
  };
}
var ServicePlanFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      active: true,
      applicability: anyApplicability({ makeId: VEHICLE_MAKE_IDS.TOYOTA, model: "Corolla" }),
      code: "PLAN-A",
      description: void 0,
      effectiveFrom: void 0,
      effectiveTo: null,
      intervals: [buildInterval()],
      name: "Toyota Corolla plan",
      type: SERVICE_PLAN_TYPE.CUSTOM
    };
  }
  /** Build a CreateServicePlan payload (no base document fields, no `type` — forced server-side). */
  buildCreate(overrides = {}) {
    return {
      active: true,
      applicability: anyApplicability(),
      code: "PLAN-A",
      intervals: [buildInterval()],
      name: "Fleet-wide plan",
      ...overrides
    };
  }
};
var servicePlanFactory = new ServicePlanFactory();

// src/test-factories/service-reminder.factory.ts
var ServiceReminderFactory = class extends ObjectFactory {
  getDefaults() {
    return { ...baseDocumentFactory.build(), ...this.buildCreate() };
  }
  /** Build a CreateServiceReminder payload (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      baselineDate: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      baselineOdometer: 13500,
      computedAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      dueDate: /* @__PURE__ */ new Date("2027-01-01T00:00:00.000Z"),
      dueFrom: /* @__PURE__ */ new Date("2026-10-14T00:00:00.000Z"),
      dueOdometer: 28500,
      effectiveTo: null,
      intervalCode: "A",
      nextDueDate: /* @__PURE__ */ new Date("2026-10-28T00:00:00.000Z"),
      originalDueDate: null,
      planId: "507f1f77bcf86cd799439021",
      projectedDate: /* @__PURE__ */ new Date("2026-10-28T00:00:00.000Z"),
      snoozeCount: 0,
      snoozedUntil: null,
      status: SERVICE_REMINDER_STATUS.ACTIVE,
      statusReason: null,
      vehicleId: "507f1f77bcf86cd799439031",
      ...overrides
    };
  }
  /** Build a reminder as the read API returns it, with its read-time projections. */
  buildView(overrides = {}) {
    return {
      ...this.build(),
      intervalName: "Service A",
      intervalRule: { mileageKm: 15e3, months: 12, trigger: SERVICE_PLAN_INTERVAL_TRIGGER.WHICHEVER_FIRST },
      operations: [
        { name: "Engine oil replacement", required: true },
        { name: "Cabin filter", required: false }
      ],
      urgency: SERVICE_REMINDER_URGENCY.UPCOMING,
      ...overrides
    };
  }
  /**
   * Build one row of the Service reminders queue (SWOOP-281): an Oil change of a Toyota Corolla 2022, every
   * 10,000 km or 12 months, due at 23,500 km with a 15-day grace window, urgency Overdue.
   */
  buildQueueItem(overrides = {}) {
    return {
      compareDate: "2026-08-05T00:00:00.000Z",
      completedAt: null,
      completedOdometer: null,
      dailyRateKm: 34,
      dailyRateBasis: null,
      dailyRateSource: "TENANT_DEFAULT",
      dueDate: "2026-09-01T00:00:00.000Z",
      dueOdometer: 23500,
      graceWindowDays: 15,
      id: "507f1f77bcf86cd799439051",
      intervalCode: "A",
      intervalName: "Oil change",
      lastReading: { date: "2026-08-19T00:00:00.000Z", odometer: 23980 },
      plan: { code: "A", id: "507f1f77bcf86cd799439021", name: "Corolla Standard" },
      projectedDate: "2026-08-05T00:00:00.000Z",
      remainingDays: -15,
      rule: { mileageKm: 1e4, months: 12, trigger: SERVICE_PLAN_INTERVAL_TRIGGER.WHICHEVER_FIRST },
      snooze: null,
      snoozeUnavailable: null,
      status: SERVICE_REMINDER_STATUS.ACTIVE,
      urgency: SERVICE_REMINDER_URGENCY.OVERDUE,
      vehicle: {
        id: "507f1f77bcf86cd799439031",
        licensePlate: "132847 / B",
        makeId: "507f1f77bcf86cd799439041",
        modelCode: "Corolla",
        modelId: "507f1f77bcf86cd799439042",
        vehicleGroupId: "507f1f77bcf86cd799439043",
        vehicleId: "1042",
        year: 2022
      },
      vehicleUnderMaintenance: false,
      version: "2026-08-19T08:00:00.000Z",
      workOrder: null,
      ...overrides
    };
  }
  /** Build a vehicle's reminders response: one plan in force, no conflict, a latest reading, one reminder. */
  buildVehicleResponse(overrides = {}) {
    return {
      conflict: null,
      // No dated reading by default: its age (SWOOP-246) depends on today, so a test that needs one sets it.
      lastReading: null,
      latestOdometer: 19800,
      planInForce: { code: "PLAN-A", id: "507f1f77bcf86cd799439021", name: "Toyota Corolla plan" },
      reminders: [this.buildView()],
      ...overrides
    };
  }
};
var serviceReminderFactory = new ServiceReminderFactory();

// src/test-factories/supplier.factory.ts
var SupplierFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      email: "contact@acme-insurance.com",
      legalName: "Acme Insurance Ltd",
      phone: "+33612345678",
      status: SUPPLIER_STATUS.ACTIVE,
      territoryId: "507f1f77bcf86cd799439011",
      type: SUPPLIER_TYPE.INSURANCE
    };
  }
  /** Build a CreateSupplier body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      email: "contact@acme-insurance.com",
      legalName: "Acme Insurance Ltd",
      phone: "+33612345678",
      status: SUPPLIER_STATUS.ACTIVE,
      territoryId: "507f1f77bcf86cd799439011",
      type: SUPPLIER_TYPE.INSURANCE,
      ...overrides
    };
  }
};
var supplierFactory = new SupplierFactory();

// src/test-factories/table-preference.factory.ts
var TablePreferenceFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      columnOrder: ["vehicle", "licensePlate", "year", "status"],
      hiddenColumns: ["vin"],
      tableId: "vehicles",
      userId: "bbbb11112222cccc3333dddd"
    };
  }
  /** Build a CreateTablePreference body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      columnOrder: ["vehicle", "licensePlate", "year", "status"],
      hiddenColumns: ["vin"],
      tableId: "vehicles",
      userId: "bbbb11112222cccc3333dddd",
      ...overrides
    };
  }
};
var tablePreferenceFactory = new TablePreferenceFactory();

// src/test-factories/tenant-default.factory.ts
var TenantDefaultFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build()
    };
  }
};
var tenantDefaultFactory = new TenantDefaultFactory();

// src/test-factories/tenant.factory.ts
var TenantFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      defaultCountry: "FR",
      enabledModules: [],
      initializedAt: null,
      language: "fr",
      mainContact: {
        email: "contact@example.com",
        firstName: "Jean",
        lastName: "Dupont"
      },
      name: "Test Tenant",
      segment: "direct-customers",
      status: TENANT_STATUS.Inactive
    };
  }
};
var tenantFactory = new TenantFactory();

// src/test-factories/territory.factory.ts
var TerritoryFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      currency: TERRITORY_CURRENCY.EUR,
      defaultLanguage: TERRITORY_LANGUAGE.FR,
      description: "\xCEle-de-France regional territory",
      name: "\xCEle-de-France",
      status: TERRITORY_STATUS.ACTIVE,
      zone: { countries: ["FR"], type: TERRITORY_ZONE_TYPE.COUNTRY }
    };
  }
  /** Build a CreateTerritory body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      currency: TERRITORY_CURRENCY.EUR,
      defaultLanguage: TERRITORY_LANGUAGE.FR,
      description: "\xCEle-de-France regional territory",
      name: "\xCEle-de-France",
      status: TERRITORY_STATUS.ACTIVE,
      zone: { countries: ["FR"], type: TERRITORY_ZONE_TYPE.COUNTRY },
      ...overrides
    };
  }
};
var territoryFactory = new TerritoryFactory();

// src/test-factories/tracking-result.factory.ts
var TrackingResultFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      destination: "Lyon, France",
      estimatedDelivery: "2026-03-10",
      lastUpdate: "2026-03-04T10:00:00.000Z",
      origin: "Paris, France",
      status: "in_transit",
      trackingNumber: "PKG-12345"
    };
  }
};
var trackingResultFactory = new TrackingResultFactory();

// src/user/user.interface.ts
var USER_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  PENDING: "pending"
};

// src/test-factories/user.factory.ts
var UserFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      canBeReinvited: true,
      email: "test@example.com",
      firstName: "Test",
      lastActive: null,
      lastName: "User",
      phone: null,
      roles: ["TENANT_ADMIN"],
      scopes: [],
      status: USER_STATUS.ACTIVE
    };
  }
};
var userFactory = new UserFactory();

// src/test-factories/user-with-resolved-scopes.factory.ts
var UserWithResolvedScopesFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...userFactory.build(),
      resolvedScopes: []
    };
  }
};
var userWithResolvedScopesFactory = new UserWithResolvedScopesFactory();

// src/test-factories/vehicle-agreement-summary.factory.ts
var VehicleAgreementSummaryFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      agreementId: "a1a1a1a1a1a1a1a1a1a1a1a1",
      contractId: "c1c1c1c1c1c1c1c1c1c1c1c1",
      customerName: "Cedar Logistics",
      customerPhone: "+961 1 902 771",
      driverName: "Georges Aoun",
      driverPhone: { dialCode: "+961", number: "3123456" },
      endDate: /* @__PURE__ */ new Date("2026-11-12T00:00:00.000Z"),
      handoverDate: /* @__PURE__ */ new Date("2026-10-28T00:00:00.000Z"),
      handoverOdometer: 24500,
      handoverTime: "09:15",
      number: "MA-26-0012-01-03",
      status: AGREEMENT_STATUS.ON_LEASE
    };
  }
  /** An individual customer's lease: no company driver. */
  buildIndividual(overrides = {}) {
    return {
      ...this.build(),
      customerName: "Hala Fares",
      customerPhone: "+961 70 559 803",
      driverName: null,
      driverPhone: null,
      ...overrides
    };
  }
  /** An agreement assigned but not handed over yet: hand-over pending, no end date. */
  buildPending(overrides = {}) {
    return {
      ...this.build(),
      endDate: null,
      handoverDate: null,
      handoverOdometer: null,
      handoverTime: null,
      status: AGREEMENT_STATUS.ASSIGNED,
      ...overrides
    };
  }
};
var vehicleAgreementSummaryFactory = new VehicleAgreementSummaryFactory();
var buildVehicleAgreementSummary = (overrides = {}) => vehicleAgreementSummaryFactory.build(overrides);

// src/vehicle-allocation/vehicle-allocation.interface.ts
var VEHICLE_ALLOCATION_TYPE = {
  /**
   * Allocated for as long as the holder needs it — typically written into an employment contract.
   * Open-ended by definition: it is ended by UNALLOCATING the vehicle, never by an end date.
   */
  PERMANENT: "PERMANENT",
  /** Allocated for a defined period — a few days for a customer visit, a site transfer. */
  TEMPORARY: "TEMPORARY"
};

// src/test-factories/vehicle-allocation.factory.ts
var START_AT = /* @__PURE__ */ new Date("2026-07-29T11:36:00.000Z");
var VehicleAllocationFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      allocationType: VEHICLE_ALLOCATION_TYPE.TEMPORARY,
      driverId: "cccccccccccccccccccccccc",
      endAt: null,
      startAt: START_AT,
      vehicleId: "dddddddddddddddddddddddd"
    };
  }
  /** Build a CreateVehicleAllocation payload (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      allocationType: VEHICLE_ALLOCATION_TYPE.TEMPORARY,
      driverId: "cccccccccccccccccccccccc",
      endAt: null,
      startAt: START_AT,
      vehicleId: "dddddddddddddddddddddddd",
      ...overrides
    };
  }
};
var vehicleAllocationFactory = new VehicleAllocationFactory();
var VehicleAllocationDetailFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...vehicleAllocationFactory.build(),
      distanceKm: null,
      driverDeleted: false,
      driverLicenseExpiry: null,
      driverOutsideCallerScope: false,
      handoverReading: null,
      returnReading: null,
      userFirstName: "Elie",
      userLastName: "Rahme",
      userPhone: null
    };
  }
};
var vehicleAllocationDetailFactory = new VehicleAllocationDetailFactory();

// src/vehicle-group/vehicle-group.interface.ts
var VEHICLE_GROUP_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive"
};

// src/test-factories/vehicle-group.factory.ts
var VehicleGroupFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      description: "Light commercial vans",
      label: "Van",
      status: VEHICLE_GROUP_STATUS.ACTIVE
    };
  }
  /** Build a CreateVehicleGroup body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      description: "Light commercial vans",
      label: "Van",
      status: VEHICLE_GROUP_STATUS.ACTIVE,
      ...overrides
    };
  }
};
var vehicleGroupFactory = new VehicleGroupFactory();

// src/test-factories/vehicle-history.factory.ts
var VehicleHistoryEntryFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      timestamp: /* @__PURE__ */ new Date("2024-06-01T00:00:00.000Z"),
      type: VEHICLE_HISTORY_TYPE.ODOMETER_READING,
      userId: "507f1f77bcf86cd799439099",
      value: 12e3,
      vehicleId: "507f1f77bcf86cd799439013"
    };
  }
  /** Build a CreateVehicleHistoryEntry payload (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      timestamp: /* @__PURE__ */ new Date("2024-06-01T00:00:00.000Z"),
      type: VEHICLE_HISTORY_TYPE.ODOMETER_READING,
      userId: "507f1f77bcf86cd799439099",
      value: 12e3,
      vehicleId: "507f1f77bcf86cd799439013",
      ...overrides
    };
  }
};
var vehicleHistoryEntryFactory = new VehicleHistoryEntryFactory();

// src/test-factories/vehicle-insurance.factory.ts
var VehicleInsuranceFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      accidentExcess: 500,
      cost: 1200,
      endDate: /* @__PURE__ */ new Date("2025-06-01T00:00:00.000Z"),
      insuranceCompanyId: "507f1f77bcf86cd799439055",
      notes: void 0,
      policyNumber: "POL-0001",
      startDate: /* @__PURE__ */ new Date("2024-06-01T00:00:00.000Z"),
      theftExcessPercentage: 5,
      totalLossExcessPercentage: 10,
      vehicleId: "507f1f77bcf86cd799439013"
    };
  }
  /** Build a CreateVehicleInsurance payload (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      accidentExcess: 500,
      cost: 1200,
      endDate: /* @__PURE__ */ new Date("2025-06-01T00:00:00.000Z"),
      insuranceCompanyId: "507f1f77bcf86cd799439055",
      policyNumber: "POL-0001",
      startDate: /* @__PURE__ */ new Date("2024-06-01T00:00:00.000Z"),
      theftExcessPercentage: 5,
      totalLossExcessPercentage: 10,
      vehicleId: "507f1f77bcf86cd799439013",
      ...overrides
    };
  }
};
var vehicleInsuranceFactory = new VehicleInsuranceFactory();

// src/test-factories/vehicle-make.factory.ts
var VehicleMakeFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      id: VEHICLE_MAKE_IDS.TOYOTA,
      label: { ar: "Toyota", en: "Toyota", fr: "Toyota" }
    };
  }
  /** Build a CreateVehicleMake payload (no audit fields). */
  buildCreate(overrides = {}) {
    return {
      id: VEHICLE_MAKE_IDS.TOYOTA,
      label: { ar: "Toyota", en: "Toyota", fr: "Toyota" },
      ...overrides
    };
  }
};
var vehicleMakeFactory = new VehicleMakeFactory();

// src/test-factories/vehicle-model.factory.ts
var VehicleModelFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      engines: [],
      // FK to a vehicle-makes._id — VEHICLE_MAKE_IDS.TOYOTA.
      makeId: "adadadadadadadadadadad01",
      model: "Corolla",
      periodEnd: null,
      periodStart: /* @__PURE__ */ new Date("2019-01-01T00:00:00.000Z"),
      providerExternalId: null,
      trim: null
    };
  }
  /** Build a CreateVehicleModel payload (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      engines: [],
      // FK to a vehicle-makes._id — VEHICLE_MAKE_IDS.TOYOTA.
      makeId: "adadadadadadadadadadad01",
      model: "Corolla",
      periodEnd: null,
      periodStart: /* @__PURE__ */ new Date("2019-01-01T00:00:00.000Z"),
      providerExternalId: null,
      trim: null,
      ...overrides
    };
  }
};
var vehicleModelFactory = new VehicleModelFactory();

// src/vehicle-ownership/vehicle-ownership.interface.ts
var VEHICLE_OWNERSHIP_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive"
};
var VEHICLE_OWNERSHIP_CODE_LEASED = "leased";
var PROTECTED_VEHICLE_OWNERSHIP_CODES = [VEHICLE_OWNERSHIP_CODE_LEASED, "owned"];

// src/test-factories/vehicle-ownership.factory.ts
var VehicleOwnershipFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "owned",
      description: "Vehicles owned outright by the fleet",
      label: "Owned",
      status: VEHICLE_OWNERSHIP_STATUS.ACTIVE
    };
  }
  /** Build a CreateVehicleOwnership body (no base document fields). Code is user-provided. */
  buildCreate(overrides = {}) {
    return {
      code: "owned",
      description: "Vehicles owned outright by the fleet",
      label: "Owned",
      status: VEHICLE_OWNERSHIP_STATUS.ACTIVE,
      ...overrides
    };
  }
};
var vehicleOwnershipFactory = new VehicleOwnershipFactory();

// src/vehicle-type/vehicle-type.interface.ts
var VEHICLE_TYPE_CATEGORY = {
  COMMERCIAL: "COMMERCIAL",
  MOBILITY: "MOBILITY",
  PASSENGER: "PASSENGER",
  PUBLIC_TRANSPORT: "PUBLIC_TRANSPORT"
};

// src/vehicle-type/vehicle-type.constants.ts
var VEHICLE_TYPE_IDS = {
  SEDAN: "7e7e7e7e7e7e7e7e7e7e7e01",
  HATCH: "7e7e7e7e7e7e7e7e7e7e7e02",
  SUV: "7e7e7e7e7e7e7e7e7e7e7e03",
  CROSS: "7e7e7e7e7e7e7e7e7e7e7e04",
  COUPE: "7e7e7e7e7e7e7e7e7e7e7e05",
  CONV: "7e7e7e7e7e7e7e7e7e7e7e06",
  WAGON: "7e7e7e7e7e7e7e7e7e7e7e07",
  VAN: "7e7e7e7e7e7e7e7e7e7e7e08",
  MPV: "7e7e7e7e7e7e7e7e7e7e7e09",
  LUX: "7e7e7e7e7e7e7e7e7e7e7e0a",
  PICKUP: "7e7e7e7e7e7e7e7e7e7e7e0b",
  LCV: "7e7e7e7e7e7e7e7e7e7e7e0c",
  BOX: "7e7e7e7e7e7e7e7e7e7e7e0d",
  FLATBED: "7e7e7e7e7e7e7e7e7e7e7e0e",
  REEFER: "7e7e7e7e7e7e7e7e7e7e7e0f",
  HGV: "7e7e7e7e7e7e7e7e7e7e7e10",
  TRAILER: "7e7e7e7e7e7e7e7e7e7e7e11",
  SEMI: "7e7e7e7e7e7e7e7e7e7e7e12",
  DELIVERY: "7e7e7e7e7e7e7e7e7e7e7e13",
  CARSHARE: "7e7e7e7e7e7e7e7e7e7e7e14",
  SCOOTER: "7e7e7e7e7e7e7e7e7e7e7e15",
  MOTOR: "7e7e7e7e7e7e7e7e7e7e7e16",
  EBIKE: "7e7e7e7e7e7e7e7e7e7e7e17",
  BIKE: "7e7e7e7e7e7e7e7e7e7e7e18",
  MICRO: "7e7e7e7e7e7e7e7e7e7e7e19",
  BUS: "7e7e7e7e7e7e7e7e7e7e7e1a",
  MINIBUS: "7e7e7e7e7e7e7e7e7e7e7e1b",
  COACH: "7e7e7e7e7e7e7e7e7e7e7e1c",
  SHUTTLE: "7e7e7e7e7e7e7e7e7e7e7e1d"
};
var VEHICLE_TYPES = [
  // ─── PASSENGER ─────────────────────────────────────────────────────────────
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.SEDAN,
    label: { ar: "\u0633\u064A\u062F\u0627\u0646", en: "Sedan", fr: "Berline" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.HATCH,
    label: { ar: "\u0647\u0627\u062A\u0634\u0628\u0627\u0643", en: "Hatchback", fr: "Compacte (\xE0 hayon)" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.SUV,
    label: { ar: "\u0633\u064A\u0627\u0631\u0629 \u0631\u064A\u0627\u0636\u064A\u0629 \u0645\u062A\u0639\u062F\u062F\u0629 \u0627\u0644\u0627\u0633\u062A\u062E\u062F\u0627\u0645\u0627\u062A", en: "SUV", fr: "SUV / V\xE9hicule utilitaire sport" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.CROSS,
    label: { ar: "\u0643\u0631\u0648\u0633 \u0623\u0648\u0641\u0631", en: "Crossover", fr: "Crossover urbain" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.COUPE,
    label: { ar: "\u0643\u0648\u0628\u064A\u0647", en: "Coupe", fr: "Coup\xE9" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.CONV,
    label: { ar: "\u0633\u064A\u0627\u0631\u0629 \u0645\u0643\u0634\u0648\u0641\u0629", en: "Convertible", fr: "Cabriolet" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.WAGON,
    label: { ar: "\u0633\u062A\u064A\u0634\u0646 \u0648\u0627\u063A\u0646", en: "Wagon", fr: "Break" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.VAN,
    label: { ar: "\u0641\u0627\u0646 \u0631\u0643\u0627\u0628", en: "Passenger Van", fr: "Van passagers" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.MPV,
    label: { ar: "\u0633\u064A\u0627\u0631\u0629 \u0645\u062A\u0639\u062F\u062F\u0629 \u0627\u0644\u0623\u063A\u0631\u0627\u0636", en: "MPV / Minivan", fr: "Monospace" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PASSENGER,
    id: VEHICLE_TYPE_IDS.LUX,
    label: { ar: "\u0633\u064A\u0627\u0631\u0629 \u0641\u0627\u062E\u0631\u0629", en: "Luxury Vehicle", fr: "V\xE9hicule de luxe" }
  },
  // ─── COMMERCIAL ────────────────────────────────────────────────────────────
  {
    category: VEHICLE_TYPE_CATEGORY.COMMERCIAL,
    id: VEHICLE_TYPE_IDS.PICKUP,
    label: { ar: "\u0634\u0627\u062D\u0646\u0629 \u0628\u064A\u0643 \u0623\u0628", en: "Pickup Truck", fr: "Pick-up" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.COMMERCIAL,
    id: VEHICLE_TYPE_IDS.LCV,
    label: { ar: "\u0645\u0631\u0643\u0628\u0629 \u062A\u062C\u0627\u0631\u064A\u0629 \u062E\u0641\u064A\u0641\u0629", en: "Light Commercial Vehicle", fr: "V\xE9hicule utilitaire l\xE9ger" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.COMMERCIAL,
    id: VEHICLE_TYPE_IDS.BOX,
    label: { ar: "\u0634\u0627\u062D\u0646\u0629 \u0635\u0646\u062F\u0648\u0642\u064A\u0629", en: "Box Truck", fr: "Camion fourgon" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.COMMERCIAL,
    id: VEHICLE_TYPE_IDS.FLATBED,
    label: { ar: "\u0634\u0627\u062D\u0646\u0629 \u0645\u0633\u0637\u062D\u0629", en: "Flatbed Truck", fr: "Camion plateau" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.COMMERCIAL,
    id: VEHICLE_TYPE_IDS.REEFER,
    label: { ar: "\u0634\u0627\u062D\u0646\u0629 \u0645\u0628\u0631\u062F\u0629", en: "Refrigerated Truck", fr: "Camion frigorifique" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.COMMERCIAL,
    id: VEHICLE_TYPE_IDS.HGV,
    label: { ar: "\u0645\u0631\u0643\u0628\u0629 \u0628\u0636\u0627\u0626\u0639 \u062B\u0642\u064A\u0644\u0629", en: "Heavy Goods Vehicle", fr: "Poids lourd" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.COMMERCIAL,
    id: VEHICLE_TYPE_IDS.TRAILER,
    label: { ar: "\u0634\u0627\u062D\u0646\u0629 \u0645\u0642\u0637\u0648\u0631\u0629", en: "Trailer Truck", fr: "Camion remorque" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.COMMERCIAL,
    id: VEHICLE_TYPE_IDS.SEMI,
    label: { ar: "\u0646\u0635\u0641 \u0645\u0642\u0637\u0648\u0631\u0629", en: "Semi-Trailer", fr: "Semi-remorque" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.COMMERCIAL,
    id: VEHICLE_TYPE_IDS.DELIVERY,
    label: { ar: "\u0645\u0631\u0643\u0628\u0629 \u062A\u0648\u0635\u064A\u0644", en: "Delivery Vehicle", fr: "V\xE9hicule de livraison" }
  },
  // ─── MOBILITY ──────────────────────────────────────────────────────────────
  {
    category: VEHICLE_TYPE_CATEGORY.MOBILITY,
    id: VEHICLE_TYPE_IDS.CARSHARE,
    label: { ar: "\u0645\u0631\u0643\u0628\u0629 \u062A\u0634\u0627\u0631\u0643 \u0627\u0644\u0633\u064A\u0627\u0631\u0627\u062A", en: "Car Sharing Vehicle", fr: "V\xE9hicule d'autopartage" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.MOBILITY,
    id: VEHICLE_TYPE_IDS.SCOOTER,
    label: { ar: "\u0633\u0643\u0648\u062A\u0631", en: "Scooter", fr: "Scooter" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.MOBILITY,
    id: VEHICLE_TYPE_IDS.MOTOR,
    label: { ar: "\u062F\u0631\u0627\u062C\u0629 \u0646\u0627\u0631\u064A\u0629", en: "Motorcycle", fr: "Moto" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.MOBILITY,
    id: VEHICLE_TYPE_IDS.EBIKE,
    label: { ar: "\u062F\u0631\u0627\u062C\u0629 \u0643\u0647\u0631\u0628\u0627\u0626\u064A\u0629", en: "Electric Bike", fr: "V\xE9lo \xE9lectrique" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.MOBILITY,
    id: VEHICLE_TYPE_IDS.BIKE,
    label: { ar: "\u062F\u0631\u0627\u062C\u0629 \u0647\u0648\u0627\u0626\u064A\u0629", en: "Bicycle", fr: "V\xE9lo" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.MOBILITY,
    id: VEHICLE_TYPE_IDS.MICRO,
    label: { ar: "\u0645\u0631\u0643\u0628\u0629 \u062A\u0646\u0642\u0644 \u0635\u063A\u064A\u0631\u0629", en: "Micro Mobility Vehicle", fr: "V\xE9hicule de micromobilit\xE9" }
  },
  // ─── PUBLIC_TRANSPORT ──────────────────────────────────────────────────────
  {
    category: VEHICLE_TYPE_CATEGORY.PUBLIC_TRANSPORT,
    id: VEHICLE_TYPE_IDS.BUS,
    label: { ar: "\u062D\u0627\u0641\u0644\u0629", en: "Bus", fr: "Bus / Autobus" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PUBLIC_TRANSPORT,
    id: VEHICLE_TYPE_IDS.MINIBUS,
    label: { ar: "\u062D\u0627\u0641\u0644\u0629 \u0635\u063A\u064A\u0631\u0629", en: "Minibus", fr: "Minibus" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PUBLIC_TRANSPORT,
    id: VEHICLE_TYPE_IDS.COACH,
    label: { ar: "\u062D\u0627\u0641\u0644\u0629 \u0633\u064A\u0627\u062D\u064A\u0629", en: "Coach", fr: "Autocar" }
  },
  {
    category: VEHICLE_TYPE_CATEGORY.PUBLIC_TRANSPORT,
    id: VEHICLE_TYPE_IDS.SHUTTLE,
    label: { ar: "\u062D\u0627\u0641\u0644\u0629 \u0645\u0643\u0648\u0643\u064A\u0629", en: "Shuttle", fr: "Navette" }
  }
];

// src/test-factories/vehicle-type.factory.ts
var VehicleTypeFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      category: VEHICLE_TYPE_CATEGORY.PASSENGER,
      id: VEHICLE_TYPE_IDS.SEDAN,
      label: { ar: "\u0633\u064A\u062F\u0627\u0646", en: "Sedan", fr: "Berline" }
    };
  }
  /** Build a CreateVehicleType payload (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      category: VEHICLE_TYPE_CATEGORY.PASSENGER,
      id: VEHICLE_TYPE_IDS.SEDAN,
      label: { ar: "\u0633\u064A\u062F\u0627\u0646", en: "Sedan", fr: "Berline" },
      ...overrides
    };
  }
};
var vehicleTypeFactory = new VehicleTypeFactory();

// src/vehicle-option/vehicle-option.interface.ts
var VEHICLE_OPTION_CATEGORY = {
  COMFORT_INTERIOR: "COMFORT_INTERIOR",
  EXTERIOR_FEATURES: "EXTERIOR_FEATURES",
  FLEET_OPERATIONAL: "FLEET_OPERATIONAL",
  MULTIMEDIA_CONNECTIVITY: "MULTIMEDIA_CONNECTIVITY",
  SAFETY_DRIVER_ASSISTANCE: "SAFETY_DRIVER_ASSISTANCE",
  SECURITY: "SECURITY"
};

// src/vehicle-option/vehicle-option.constants.ts
var VEHICLE_OPTION_IDS = {
  // ─── SAFETY_DRIVER_ASSISTANCE ──────────────────────────────────────────────
  PARKING_SENSORS: "0b710b710b710b710b710b01",
  REAR_CAMERA: "0b710b710b710b710b710b02",
  // ─── COMFORT_INTERIOR ──────────────────────────────────────────────────────
  AIR_CONDITIONING: "0b710b710b710b710b710b03",
  HEATING_SYSTEM: "0b710b710b710b710b710b04",
  LEATHER_SEATS: "0b710b710b710b710b710b05",
  HEATED_SEATS: "0b710b710b710b710b710b06",
  ELECTRIC_SEATS: "0b710b710b710b710b710b07",
  CENTRAL_LOCKING: "0b710b710b710b710b710b08",
  // ─── MULTIMEDIA_CONNECTIVITY ───────────────────────────────────────────────
  BLUETOOTH: "0b710b710b710b710b710b09",
  APPLE_CARPLAY: "0b710b710b710b710b710b0a",
  ANDROID_AUTO: "0b710b710b710b710b710b0b",
  NAVIGATION_SYSTEM: "0b710b710b710b710b710b0c",
  // ─── EXTERIOR_FEATURES ─────────────────────────────────────────────────────
  FOG_LIGHTS: "0b710b710b710b710b710b0d",
  LED_HEADLIGHTS: "0b710b710b710b710b710b0e",
  SPARE_WHEEL: "0b710b710b710b710b710b0f",
  // ─── FLEET_OPERATIONAL ─────────────────────────────────────────────────────
  GPS_TRACKER_INSTALLED: "0b710b710b710b710b710b10",
  TELEMATICS_SYSTEM_INSTALLED: "0b710b710b710b710b710b11",
  // ─── SECURITY ──────────────────────────────────────────────────────────────
  ALARM_SYSTEM: "0b710b710b710b710b710b12",
  ANTI_THEFT_SYSTEM: "0b710b710b710b710b710b13",
  REMOTE_LOCKING: "0b710b710b710b710b710b14",
  VEHICLE_TRACKING_DEVICE: "0b710b710b710b710b710b15"
};
var VEHICLE_AVAILABLE_OPTIONS = [
  // ─── SAFETY_DRIVER_ASSISTANCE ──────────────────────────────────────────────
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.SAFETY_DRIVER_ASSISTANCE,
    id: VEHICLE_OPTION_IDS.PARKING_SENSORS,
    vehicleOptionCode: "PARKING_SENSORS"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.SAFETY_DRIVER_ASSISTANCE,
    id: VEHICLE_OPTION_IDS.REAR_CAMERA,
    vehicleOptionCode: "REAR_CAMERA"
  },
  // ─── COMFORT_INTERIOR ──────────────────────────────────────────────────────
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.COMFORT_INTERIOR,
    id: VEHICLE_OPTION_IDS.AIR_CONDITIONING,
    vehicleOptionCode: "AIR_CONDITIONING"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.COMFORT_INTERIOR,
    id: VEHICLE_OPTION_IDS.HEATING_SYSTEM,
    vehicleOptionCode: "HEATING_SYSTEM"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.COMFORT_INTERIOR,
    id: VEHICLE_OPTION_IDS.LEATHER_SEATS,
    vehicleOptionCode: "LEATHER_SEATS"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.COMFORT_INTERIOR,
    id: VEHICLE_OPTION_IDS.HEATED_SEATS,
    vehicleOptionCode: "HEATED_SEATS"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.COMFORT_INTERIOR,
    id: VEHICLE_OPTION_IDS.ELECTRIC_SEATS,
    vehicleOptionCode: "ELECTRIC_SEATS"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.COMFORT_INTERIOR,
    id: VEHICLE_OPTION_IDS.CENTRAL_LOCKING,
    vehicleOptionCode: "CENTRAL_LOCKING"
  },
  // ─── MULTIMEDIA_CONNECTIVITY ───────────────────────────────────────────────
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.MULTIMEDIA_CONNECTIVITY,
    id: VEHICLE_OPTION_IDS.BLUETOOTH,
    vehicleOptionCode: "BLUETOOTH"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.MULTIMEDIA_CONNECTIVITY,
    id: VEHICLE_OPTION_IDS.APPLE_CARPLAY,
    vehicleOptionCode: "APPLE_CARPLAY"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.MULTIMEDIA_CONNECTIVITY,
    id: VEHICLE_OPTION_IDS.ANDROID_AUTO,
    vehicleOptionCode: "ANDROID_AUTO"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.MULTIMEDIA_CONNECTIVITY,
    id: VEHICLE_OPTION_IDS.NAVIGATION_SYSTEM,
    vehicleOptionCode: "NAVIGATION_SYSTEM"
  },
  // ─── EXTERIOR_FEATURES ─────────────────────────────────────────────────────
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.EXTERIOR_FEATURES,
    id: VEHICLE_OPTION_IDS.FOG_LIGHTS,
    vehicleOptionCode: "FOG_LIGHTS"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.EXTERIOR_FEATURES,
    id: VEHICLE_OPTION_IDS.LED_HEADLIGHTS,
    vehicleOptionCode: "LED_HEADLIGHTS"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.EXTERIOR_FEATURES,
    id: VEHICLE_OPTION_IDS.SPARE_WHEEL,
    vehicleOptionCode: "SPARE_WHEEL"
  },
  // ─── FLEET_OPERATIONAL ─────────────────────────────────────────────────────
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.FLEET_OPERATIONAL,
    id: VEHICLE_OPTION_IDS.GPS_TRACKER_INSTALLED,
    vehicleOptionCode: "GPS_TRACKER_INSTALLED"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.FLEET_OPERATIONAL,
    id: VEHICLE_OPTION_IDS.TELEMATICS_SYSTEM_INSTALLED,
    vehicleOptionCode: "TELEMATICS_SYSTEM_INSTALLED"
  },
  // ─── SECURITY ──────────────────────────────────────────────────────────────
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.SECURITY,
    id: VEHICLE_OPTION_IDS.ALARM_SYSTEM,
    vehicleOptionCode: "ALARM_SYSTEM"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.SECURITY,
    id: VEHICLE_OPTION_IDS.ANTI_THEFT_SYSTEM,
    vehicleOptionCode: "ANTI_THEFT_SYSTEM"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.SECURITY,
    id: VEHICLE_OPTION_IDS.REMOTE_LOCKING,
    vehicleOptionCode: "REMOTE_LOCKING"
  },
  {
    categoryCode: VEHICLE_OPTION_CATEGORY.SECURITY,
    id: VEHICLE_OPTION_IDS.VEHICLE_TRACKING_DEVICE,
    vehicleOptionCode: "VEHICLE_TRACKING_DEVICE"
  }
];

// src/test-factories/vehicle.factory.ts
var VehicleFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      divisionId: "507f1f77bcf86cd799439012",
      energySourceId: ENERGY_SOURCE_IDS.GAS,
      licensePlate: "AB-123-CD",
      locationId: "507f1f77bcf86cd799439011",
      makeId: VEHICLE_MAKE_IDS.TOYOTA,
      modelCode: "corolla",
      modelId: "507f1f77bcf86cd799439013",
      poolTypeId: "507f1f77bcf86cd799439015",
      registrationExpiryDate: /* @__PURE__ */ new Date("2027-01-01T00:00:00.000Z"),
      requestedStatus: VEHICLE_STATUS.ACTIVE,
      servicePlanConflictIds: [],
      servicePlanIds: [],
      status: VEHICLE_STATUS.DRAFT,
      vehicleGroupId: "507f1f77bcf86cd799439014",
      vehicleId: "1",
      vehicleOptions: [VEHICLE_OPTION_IDS.PARKING_SENSORS, VEHICLE_OPTION_IDS.BLUETOOTH],
      vehicleTypeId: VEHICLE_TYPE_IDS.PICKUP,
      vin: "1HGCM82633A004352",
      year: 2024
    };
  }
  /**
   * Build a LEASED vehicle: the base defaults plus a complete lease contract (SWOOP-238) — the four
   * mandatory lease fields and the four optional ones, so a single call covers both the read block and
   * the "clear it all" path.
   *
   * `vehicleOwnershipId` points at a placeholder id: the ownership list is tenant-scoped, so a test
   * that cares about resolving the row to the `leased` CODE must override it with its own fixture id.
   */
  buildLeased(overrides = {}) {
    return {
      ...this.build(),
      contractMileageLimit: 25e3,
      endOfLeaseOption: VEHICLE_END_OF_LEASE_OPTION.RETURN_TO_LESSOR,
      excessMileageCharge: 0.12,
      leaseContractReference: "LSE-2023-0447",
      leaseEndDate: /* @__PURE__ */ new Date("2027-02-28T00:00:00.000Z"),
      leaseStartDate: /* @__PURE__ */ new Date("2023-03-01T00:00:00.000Z"),
      lessorSupplierId: "507f1f77bcf86cd799439031",
      monthlyLeaseCost: 480,
      vehicleOwnershipId: "507f1f77bcf86cd799439022",
      ...overrides
    };
  }
  /** Build a CreateVehicle body (no base document fields). `vehicleId` is optional (auto-generated when omitted). */
  buildCreate(overrides = {}) {
    return {
      divisionId: "507f1f77bcf86cd799439012",
      energySourceId: ENERGY_SOURCE_IDS.GAS,
      licensePlate: "AB-123-CD",
      locationId: "507f1f77bcf86cd799439011",
      makeId: VEHICLE_MAKE_IDS.TOYOTA,
      modelId: "507f1f77bcf86cd799439013",
      poolTypeId: "507f1f77bcf86cd799439015",
      status: VEHICLE_STATUS.ACTIVE,
      vehicleGroupId: "507f1f77bcf86cd799439014",
      vehicleOptions: [],
      vehicleTypeId: VEHICLE_TYPE_IDS.PICKUP,
      vin: "1HGCM82633A004352",
      year: 2024,
      ...overrides
    };
  }
};
var vehicleFactory = new VehicleFactory();

// src/test-factories/vehicle-list-item.factory.ts
var VehicleListItemFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...vehicleFactory.build(),
      // The default row shows a driver (currentAllocation), so it is on no agreement.
      currentAgreement: null,
      currentAllocation: {
        allocationType: VEHICLE_ALLOCATION_TYPE.TEMPORARY,
        endAt: null,
        holderName: "Elie Rahme",
        startAt: /* @__PURE__ */ new Date("2026-07-29T11:36:00.000Z")
      },
      latestEnergyLevel: 80,
      latestOdometer: 12e3,
      renewingInsuranceDate: /* @__PURE__ */ new Date("2027-01-01T00:00:00.000Z"),
      // A row carries at most one of the two: a currently-allocated vehicle has no need of an upcoming
      // projection, and the default row above is currently allocated.
      upcomingAllocation: null
    };
  }
  /**
   * A row whose allocation has NOT started yet — what the list shows as "Scheduled". `currentAllocation`
   * is null because a future-dated allocation is deliberately not "currently allocated" (the Available /
   * Allocated tabs and the KPI counts depend on that).
   */
  buildScheduled(overrides = {}) {
    return {
      ...this.build(),
      currentAllocation: null,
      upcomingAllocation: {
        allocationType: VEHICLE_ALLOCATION_TYPE.TEMPORARY,
        endAt: null,
        holderName: "Elie Rahme",
        startAt: /* @__PURE__ */ new Date("2027-03-01T09:00:00.000Z")
      },
      ...overrides
    };
  }
};
var vehicleListItemFactory = new VehicleListItemFactory();

// src/test-factories/vehicle-fleet-stats.factory.ts
var VehicleFleetStatsFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      allocated: 3,
      available: 5,
      inService: 7,
      total: 9
    };
  }
};
var vehicleFleetStatsFactory = new VehicleFleetStatsFactory();

// src/test-factories/pagination.factory.ts
function buildPaginatedResponse(items, overrides = {}) {
  return {
    items,
    page: 1,
    pageSize: 20,
    total: items.length,
    totalPages: 1,
    ...overrides
  };
}

// src/vehicle-allocation/vehicle-agreement.ts
var LIVE_AGREEMENT_STATUSES = [
  AGREEMENT_STATUS.ASSIGNED,
  AGREEMENT_STATUS.ON_LEASE
];
function isAgreementLive(status) {
  return LIVE_AGREEMENT_STATUSES.includes(status);
}
function isAgreementOverdue(summary, today) {
  if (!isAgreementLive(summary.status) || summary.endDate === null) {
    return false;
  }
  return toCalendarDay(summary.endDate).getTime() < toCalendarDay(today).getTime();
}
function toVehicleAgreementSummary(row) {
  const { agreement } = row;
  return {
    agreementId: agreement.id,
    contractId: agreement.contractId,
    customerName: row.customerName,
    customerPhone: row.customerPhone,
    driverName: row.driverName,
    driverPhone: row.driverPhone,
    endDate: agreementCurrentTerms(agreement, { termMonths: row.lineTermMonths }).endDate,
    handoverDate: agreement.handoverDate,
    handoverOdometer: agreement.handoverOdometer,
    handoverTime: agreement.handoverTime,
    number: agreement.number,
    status: agreement.status
  };
}

// src/vehicle-model/vehicle-model.constants.ts
var VEHICLE_MODELS = [
  // ─── Renault Clio (makeId: VEHICLE_MAKE_IDS.RENAULT) ───────────────────────────
  // Clio IV (2012–2019)
  {
    engines: [
      { label: "0.9 TCe 90", periodEnd: new Date(Date.UTC(2019, 5, 1)), periodStart: new Date(Date.UTC(2012, 8, 1)) },
      { label: "1.5 dCi 90", periodEnd: new Date(Date.UTC(2019, 5, 1)), periodStart: new Date(Date.UTC(2012, 8, 1)) }
    ],
    makeId: VEHICLE_MAKE_IDS.RENAULT,
    model: "Clio",
    periodEnd: new Date(Date.UTC(2019, 5, 1)),
    periodStart: new Date(Date.UTC(2012, 8, 1)),
    providerExternalId: null,
    trim: "Zen"
  },
  // Clio V (2019–present)
  {
    engines: [{ label: "1.0 TCe 90", periodEnd: null, periodStart: new Date(Date.UTC(2019, 5, 1)) }],
    makeId: VEHICLE_MAKE_IDS.RENAULT,
    model: "Clio",
    periodEnd: null,
    periodStart: new Date(Date.UTC(2019, 5, 1)),
    providerExternalId: null,
    trim: "RS Line"
  },
  {
    engines: [
      { label: "1.0 TCe 100", periodEnd: null, periodStart: new Date(Date.UTC(2019, 5, 1)) },
      { label: "1.6 E-Tech Hybrid 145", periodEnd: null, periodStart: new Date(Date.UTC(2020, 5, 1)) }
    ],
    makeId: VEHICLE_MAKE_IDS.RENAULT,
    model: "Clio",
    periodEnd: null,
    periodStart: new Date(Date.UTC(2019, 5, 1)),
    providerExternalId: null,
    trim: "Intens"
  },
  // ─── Peugeot 208 (makeId: VEHICLE_MAKE_IDS.PEUG) ───────────────────────────────
  // 208 II (2019–present)
  {
    engines: [
      { label: "1.2 PureTech 100", periodEnd: null, periodStart: new Date(Date.UTC(2019, 8, 1)) },
      { label: "1.5 BlueHDi 100", periodEnd: null, periodStart: new Date(Date.UTC(2019, 8, 1)) }
    ],
    makeId: VEHICLE_MAKE_IDS.PEUG,
    model: "208",
    periodEnd: null,
    periodStart: new Date(Date.UTC(2019, 8, 1)),
    providerExternalId: null,
    trim: "Allure"
  },
  {
    engines: [{ label: "e-208 100kW (electric)", periodEnd: null, periodStart: new Date(Date.UTC(2019, 8, 1)) }],
    makeId: VEHICLE_MAKE_IDS.PEUG,
    model: "208",
    periodEnd: null,
    periodStart: new Date(Date.UTC(2019, 8, 1)),
    providerExternalId: null,
    trim: "GT"
  }
];

// src/work-order/work-order-damage.ts
var DAMAGE_AREA = {
  FRONT: "FRONT",
  INTERIOR: "INTERIOR",
  LEFT: "LEFT",
  REAR: "REAR",
  RIGHT: "RIGHT",
  ROOF: "ROOF"
};
var DAMAGE_AREAS = Object.values(DAMAGE_AREA);
var DAMAGE_VIEW = {
  EXTERIOR: "exterior",
  INTERIOR: "interior"
};
var DAMAGE_VIEWS = Object.values(DAMAGE_VIEW);
var DAMAGE_ELEMENT = {
  boot: DAMAGE_AREA.REAR,
  boot_floor: DAMAGE_AREA.INTERIOR,
  centre_console: DAMAGE_AREA.INTERIOR,
  dashboard: DAMAGE_AREA.INTERIOR,
  door_front_left: DAMAGE_AREA.LEFT,
  door_front_right: DAMAGE_AREA.RIGHT,
  door_rear_left: DAMAGE_AREA.LEFT,
  door_rear_right: DAMAGE_AREA.RIGHT,
  front_bumper: DAMAGE_AREA.FRONT,
  front_seat_left: DAMAGE_AREA.INTERIOR,
  front_seat_right: DAMAGE_AREA.INTERIOR,
  headlight_left: DAMAGE_AREA.FRONT,
  headlight_right: DAMAGE_AREA.FRONT,
  hood: DAMAGE_AREA.FRONT,
  mirror_left: DAMAGE_AREA.LEFT,
  mirror_right: DAMAGE_AREA.RIGHT,
  rear_bumper: DAMAGE_AREA.REAR,
  rear_seat_centre: DAMAGE_AREA.INTERIOR,
  rear_seat_left: DAMAGE_AREA.INTERIOR,
  rear_seat_right: DAMAGE_AREA.INTERIOR,
  rear_window: DAMAGE_AREA.REAR,
  roof: DAMAGE_AREA.ROOF,
  steering_wheel: DAMAGE_AREA.INTERIOR,
  taillight_left: DAMAGE_AREA.REAR,
  taillight_right: DAMAGE_AREA.REAR,
  wheel_front_left: DAMAGE_AREA.LEFT,
  wheel_front_right: DAMAGE_AREA.RIGHT,
  wheel_rear_left: DAMAGE_AREA.LEFT,
  wheel_rear_right: DAMAGE_AREA.RIGHT,
  windscreen: DAMAGE_AREA.FRONT
};
var DAMAGE_ELEMENTS = Object.keys(DAMAGE_ELEMENT);
function isDamageElement(value) {
  return value != null && Object.prototype.hasOwnProperty.call(DAMAGE_ELEMENT, value);
}
function damageElementsForArea(area) {
  return DAMAGE_ELEMENTS.filter((element) => DAMAGE_ELEMENT[element] === area);
}
function isDamageElementInArea(element, area) {
  return DAMAGE_ELEMENT[element] === area;
}
var DAMAGE_ELEMENTS_BY_AREA = {
  [DAMAGE_AREA.FRONT]: damageElementsForArea(DAMAGE_AREA.FRONT),
  [DAMAGE_AREA.INTERIOR]: damageElementsForArea(DAMAGE_AREA.INTERIOR),
  [DAMAGE_AREA.LEFT]: damageElementsForArea(DAMAGE_AREA.LEFT),
  [DAMAGE_AREA.REAR]: damageElementsForArea(DAMAGE_AREA.REAR),
  [DAMAGE_AREA.RIGHT]: damageElementsForArea(DAMAGE_AREA.RIGHT),
  [DAMAGE_AREA.ROOF]: damageElementsForArea(DAMAGE_AREA.ROOF)
};
var DAMAGE_TYPE = {
  BROKEN: "BROKEN",
  CRACK: "CRACK",
  DENT: "DENT",
  OTHER: "OTHER",
  SCRATCH: "SCRATCH",
  STAIN: "STAIN",
  TEAR: "TEAR"
};
var DAMAGE_TYPES = Object.values(DAMAGE_TYPE);
var DAMAGE_ACTION = {
  MONITOR: "MONITOR",
  REPAIR: "REPAIR",
  REPLACE: "REPLACE"
};
var DAMAGE_ACTIONS = [DAMAGE_ACTION.REPAIR, DAMAGE_ACTION.REPLACE, DAMAGE_ACTION.MONITOR];
var WORK_ORDER_DAMAGE_MAX = 30;
var WORK_ORDER_DAMAGE_EDITABLE_STATUSES = [
  WORK_ORDER_STATUS.OPEN,
  WORK_ORDER_STATUS.RECEIVED
];
var WORK_ORDER_DAMAGE_FIXED_STATUSES = [
  WORK_ORDER_STATUS.OPEN,
  WORK_ORDER_STATUS.RECEIVED,
  WORK_ORDER_STATUS.AT_GARAGE
];
var WORK_ORDER_DAMAGE_NOTE_MAX = 1e3;

// src/work-order/work-order-list.interface.ts
var WORK_ORDER_TYPE_FILTER = {
  CORRECTIVE: "CORRECTIVE",
  DAMAGE: "DAMAGE",
  PREVENTIVE: "PREVENTIVE",
  SCHEDULED: "SCHEDULED"
};
export {
  ACCOUNTING_MAPPING_TYPE,
  ACCOUNTING_SYNC_JOB_STATUS,
  ACCOUNTING_SYNC_OPERATION,
  ACCOUNTING_TARGET,
  AGREEMENT_HANDOVER_STATE,
  AGREEMENT_STATUS,
  AUTOMATION_MODE,
  BASE_CURRENCY_FX_POLICY,
  BILLING_EXCHANGE_RATES_ADDED_PER_SAVE_MAX,
  BILLING_EXCHANGE_RATE_CURRENCIES,
  BILLING_EXCHANGE_RATE_SOURCE_MAX_LENGTH,
  BILLING_SOURCE,
  BUILT_IN_ROLE_KEY,
  BaseDocumentFactory,
  CASCADING_DELETE_DOCUMENT_TYPES,
  CHARGE_SOURCE_TYPE,
  CHARGE_STATE,
  COHORT_MIN_MEMBERS,
  COHORT_SCOPE,
  CONTRACT_ADDENDUM_MASTER_STATUSES,
  CONTRACT_ADDENDUM_SETTLED_STATUSES,
  CONTRACT_ARCHIVABLE_STATUSES,
  CONTRACT_DEFAULTS,
  CONTRACT_DELETABLE_STATUSES,
  CONTRACT_DOCUMENT_STATUS,
  CONTRACT_EDITABLE_STATUSES,
  CONTRACT_EXTENSION_STATUS,
  CONTRACT_FEE_CODE,
  CONTRACT_FEE_CODES,
  CONTRACT_FEE_CODE_REGISTRY,
  CONTRACT_KIND,
  CONTRACT_LEASING_STATUSES,
  CONTRACT_LIMITS,
  CONTRACT_MILEAGE_DAYS_PER_YEAR,
  CONTRACT_MILEAGE_MIN_KM_PER_DAY,
  CONTRACT_PO_MANAGE_PERMISSION,
  CONTRACT_QUOTATION_ACCEPTED_EXTENSIONS,
  CONTRACT_QUOTATION_ACCEPTED_MIME_TYPES,
  CONTRACT_QUOTATION_MAX_FILE_SIZE,
  CONTRACT_SIGNED_COPY_STATUSES,
  CONTRACT_SIGNED_STATUSES,
  CONTRACT_SIGNED_VIA,
  CONTRACT_STATUS,
  CONTRACT_VAT_CERTIFICATE_ACCEPTED_EXTENSIONS,
  CONTRACT_VAT_CERTIFICATE_ACCEPTED_MIME_TYPES,
  CONTRACT_VAT_CERTIFICATE_MAX_FILE_SIZE,
  CREDIT_REASON,
  CURRENCY_MINOR_UNITS,
  CUSTOMER_STATUS,
  CUSTOMER_TYPE,
  ContractFactory,
  ContractListItemFactory,
  CustomerFactory,
  DAILY_RATE_SOURCE,
  DAMAGE_ACTION,
  DAMAGE_ACTIONS,
  DAMAGE_AREA,
  DAMAGE_AREAS,
  DAMAGE_ELEMENT,
  DAMAGE_ELEMENTS,
  DAMAGE_ELEMENTS_BY_AREA,
  DAMAGE_NATURE,
  DAMAGE_RESPONSIBILITY,
  DAMAGE_TYPE,
  DAMAGE_TYPES,
  DAMAGE_VIEW,
  DAMAGE_VIEWS,
  DEFAULT_BUSINESS_TIME_ZONE,
  DEFAULT_HETIVO_SCOPE_FIELD_MAPPING,
  DEFAULT_INVOICING_CYCLE,
  DEFAULT_PAYMENT_DUE,
  DEFAULT_TERRITORY_VAT_RATE,
  DELIVERY_STATUS,
  DISTANCE_UNIT,
  DIVISION_STATUS,
  DOCUMENT_CATEGORY,
  DOCUMENT_CATEGORY_REGISTRY,
  DOCUMENT_CODE,
  DOCUMENT_FIELD_EMPTY_VALUE,
  DOCUMENT_FIELD_FILL_IN_VALUE,
  DOCUMENT_FIELD_VALUE_MAX_LENGTH,
  DOCUMENT_LANGUAGE,
  DOCUMENT_OWNER_TYPE,
  DOCUMENT_OWNER_TYPES,
  DOCUMENT_TEMPLATE_ACCEPTED_EXTENSIONS,
  DOCUMENT_TEMPLATE_ACCEPTED_MIME_TYPES,
  DOCUMENT_TEMPLATE_ERROR_CODE,
  DOCUMENT_TEMPLATE_MAX_FILE_SIZE,
  DOCUMENT_TEMPLATE_MAX_PLACEHOLDERS,
  DOCUMENT_TEMPLATE_MAX_UNCOMPRESSED_SIZE,
  DOCUMENT_TEMPLATE_MIME_TYPE,
  DOCUMENT_TYPE,
  DOCUMENT_TYPES,
  DOCUMENT_TYPES_BY_OWNER,
  DRIVER_SOURCE,
  DRIVER_STATUS,
  DocumentTemplateFactory,
  DriverFactory,
  DriverListItemFactory,
  EMAIL_STATUS,
  ENERGY_SOURCES,
  ENERGY_SOURCE_CATEGORY,
  ENERGY_SOURCE_IDS,
  ERP_SYNC_STATUS,
  EXPENSE_CATEGORY_DEFAULTS,
  EXPENSE_CATEGORY_FAMILY,
  EXPENSE_CATEGORY_ORIGIN,
  EXPENSE_CATEGORY_STATUS,
  EXPENSE_LOGGED_VIA,
  EXPORT_CADENCE,
  EXPORT_DELIVERY,
  EXPORT_FORMAT,
  EXTERNAL_ISSUE_STATUS,
  EXTRA_FEE_AMOUNT_MAX_ANY,
  EXTRA_FEE_BASIS,
  EXTRA_FEE_FORMULA,
  EmailTemplateFactory,
  EmittedEmailFactory,
  EnergyPriceByTerritoryFactory,
  EnergySourceFactory,
  ExpenseCategoryFactory,
  FEE_REBILL_BASIS_KIND,
  FEE_REBILL_COUNTED_STATUSES,
  FEE_REBILL_DEFAULT_STATUS,
  FEE_REBILL_STATUS,
  FirebaseDecodedIdTokenFactory,
  GARAGE_KIND,
  GARAGE_KINDS,
  GARAGE_SPECIALITIES,
  GARAGE_SPECIALITY,
  GENERATED_DOCUMENT_MIME_TYPE,
  GROUPING_KEY,
  GROUP_STATUS,
  HetivoGlobalConfiguration,
  IDEMPOTENCY_STATE,
  INSPECTION_ITEM_KIND,
  INSPECTION_SCHEDULE,
  INSPECTION_SEARCH_MAX_LENGTH,
  INSPECTION_SOURCE,
  INSPECTION_STATUS,
  INSPECTION_STATUSES,
  INSPECTION_TEMPLATE_LIMITS,
  INSURANCE_PACKAGE_LIMITS,
  INSURANCE_PACKAGE_STATUS,
  INTEGRATION_MODE,
  INTERVAL_STEPS,
  INVITATION_CHANNEL,
  INVITATION_EXPIRY_DAYS,
  INVITATION_EXPIRY_DAYS_IN_MS,
  INVITATION_STATUS,
  INVITATION_STATUS_TRANSITIONS,
  INVOICE_DELIVERY_STATUS,
  INVOICE_NUMBER_MAX_SEQUENCE,
  INVOICE_REFERENCE_KIND,
  INVOICE_TYPE,
  ISSUE_BATCH_ITEM_STATUS,
  ISSUE_BATCH_SOURCE,
  ISSUE_BATCH_STATUS,
  ISSUE_PHOTO_ACCEPTED_EXTENSIONS,
  ISSUE_PHOTO_ACCEPTED_MIME_TYPES,
  ISSUE_PHOTO_MAX_FILES,
  ISSUE_PHOTO_MAX_FILE_SIZE,
  ISSUE_STATUS,
  ISSUE_STATUSES,
  ISSUE_TARGET_HOURS,
  ISSUE_TEXT_MAX,
  ISSUE_TIMELINE_KIND,
  ISSUE_TIMELINE_KINDS,
  ISSUE_TITLE_MAX,
  ISSUE_TRANSITIONS,
  ISSUE_TYPE,
  ISSUE_TYPES,
  InspectionRecordFactory,
  InspectionTemplateFactory,
  InspectionTemplateRecordFactory,
  InspectionTemplateVersionRecordFactory,
  InsurancePackageFactory,
  InvitationFactory,
  IssueDetailFactory,
  IssueFactory,
  IssueListItemFactory,
  IssueStatsFactory,
  IssueTimelineEntryFactory,
  IssueTimelineEntryViewFactory,
  JOB_STATUS,
  JOB_TYPE,
  JWT_KIND,
  LEASE_MODE,
  LEASING_ADMIN_FEE_MAX_BY_CURRENCY,
  LEASING_SETTINGS_DEFAULTS,
  LEASING_SETTINGS_LIMITS,
  LEASING_STATUS,
  LEGAL_STATUS,
  LINE_KIND,
  LINKABLE_ISSUE_STATUSES,
  LINKABLE_REMINDER_STATUSES,
  LISTED_REMINDER_STATUSES,
  LIVE_AGREEMENT_STATUSES,
  LIVE_REMINDER_STATUSES,
  LOCATION_STATUS,
  LeasingSettingsFactory,
  LocationFactory,
  MAGIC_LINK_CHANNEL,
  MAGIC_LINK_COMMENT_MAX_LENGTH,
  MAGIC_LINK_DEFAULT_EXPIRY_HOURS,
  MAGIC_LINK_DEFAULT_EXPIRY_IN_MS,
  MAGIC_LINK_PURPOSE,
  MAGIC_LINK_PURPOSES,
  MAGIC_LINK_RESPONSE_MAX_LENGTH,
  MAGIC_LINK_SUBJECT_TYPE,
  MAINTENANCE_FEATURE_KEY,
  MAINTENANCE_SETTINGS_DEFAULTS,
  MAX_REFRESH_SESSION_MS,
  MILEAGE_ALLOWANCE_MODE,
  MODULE_AREA,
  MODULE_REGISTRY,
  MS_PER_DAY,
  MagicLinkFactory,
  MagicLinkRequestViewFactory,
  MaintenanceSettingsFactory,
  ODOMETER_MAX_KM,
  ORDER_STATUS,
  ORDER_STATUS_BUCKET,
  OWNER_ENDPOINT_MANAGED_DOCUMENT_TYPES,
  OWN_RATE_MIN_KM_PER_DAY,
  OWN_RATE_MIN_SPAN_DAYS,
  ObjectFactory,
  OrderFactory,
  PASSWORD_LOWERCASE_REGEX,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_NUMBER_REGEX,
  PASSWORD_REGEX,
  PASSWORD_SYMBOL_REGEX,
  PASSWORD_UPPERCASE_REGEX,
  PAYMENT_STATUS,
  PAYMENT_STATUS_SOURCE,
  POOL_TYPE_STATUS,
  PRICE_COMPONENT,
  PRICE_UNIT,
  PROTECTED_VEHICLE_OWNERSHIP_CODES,
  PURCHASE_ORDER_CARD_MODE,
  PURCHASE_ORDER_EXPIRY_WARNING_DAYS,
  PURCHASE_ORDER_NUMBER_MAX_LENGTH,
  PURCHASE_ORDER_SOURCE,
  PURCHASE_ORDER_STATUS,
  PURCHASE_ORDER_WRITE_GATE,
  PoolTypeFactory,
  READING_AGE_AGING_MAX_DAYS,
  READING_AGE_LEVEL,
  READING_AGE_RECENT_MAX_DAYS,
  REBILLABLE_FEE_CODES,
  RELIEF_ACTION,
  RELIEF_CHARGE_TYPE,
  RELIEF_DAILY_RATE,
  RELIEF_DAYS_PER_MONTH,
  RELIEF_INVOICE_STATUS,
  RELIEF_SELECTABLE_CHARGE_TYPES,
  RELIEF_SIGNED_NOTE_ACCEPTED_EXTENSIONS,
  RELIEF_SIGNED_NOTE_ACCEPTED_MIME_TYPES,
  RELIEF_SIGNED_NOTE_MAX_FILE_SIZE,
  RELIEF_STATUS,
  REPLACEMENT_CAR_RESTRICTION,
  REVIEW_OUTCOME,
  REVIEW_OUTCOMES,
  ROLE_REQUIRED_MODULE,
  ROLE_REQUIRED_SCOPE,
  ROLE_SCOPE,
  RUN_MODE,
  RUN_STATUS,
  ReliefFactory,
  ReliefViewFactory,
  RoleFactory,
  SERIES_RESET_POLICY,
  SERVER_MANAGED_DOCUMENT_TYPES,
  SERVICE_APPS,
  SERVICE_BUS_TOPICS,
  SERVICE_OPERATION_CATEGORY,
  SERVICE_OPERATION_DEFAULTS,
  SERVICE_OPERATION_ORIGIN,
  SERVICE_PACKAGE_LIMITS,
  SERVICE_PACKAGE_RESERVED_NAME,
  SERVICE_PACKAGE_RESERVED_NAME_KEY,
  SERVICE_PACKAGE_STATUS,
  SERVICE_PLAN_INTERVAL_TRIGGER,
  SERVICE_PLAN_LOSS_REASON,
  SERVICE_PLAN_MAX_INTERVALS,
  SERVICE_PLAN_MAX_INTERVAL_ITEMS,
  SERVICE_PLAN_PREVIEW_VEHICLE_LIMIT,
  SERVICE_PLAN_STATUS,
  SERVICE_PLAN_TYPE,
  SERVICE_PLAN_YEAR_MODE,
  SERVICE_REMINDER_MAX_SNOOZES,
  SERVICE_REMINDER_MAX_SNOOZE_DAYS,
  SERVICE_REMINDER_QUEUE_TAB,
  SERVICE_REMINDER_REASON_MAX_LENGTH,
  SERVICE_REMINDER_STATUS,
  SERVICE_REMINDER_URGENCY,
  SERVICE_ROLES,
  SERVICE_ROLE_GRANTS,
  SOURCE_PLATFORM,
  STORAGE_FOLDER,
  STORAGE_FOLDERS,
  SUPPLIER_DOCUMENT_ACCEPTED_EXTENSIONS,
  SUPPLIER_DOCUMENT_MAX_FILES,
  SUPPLIER_DOCUMENT_MAX_FILE_SIZE,
  SUPPLIER_STATUS,
  SUPPLIER_TYPE,
  SUPPORTED_COUNTRIES,
  ServiceOperationFactory,
  ServicePackageFactory,
  ServicePlanFactory,
  ServiceReminderFactory,
  SupplierFactory,
  TEMPLATE_TYPE,
  TENANT_STATUS,
  TENANT_STATUSES,
  TERRITORY_CURRENCY,
  TERRITORY_LANGUAGE,
  TERRITORY_STATUS,
  TERRITORY_ZONE_TYPE,
  TIME_OF_DAY_PATTERN,
  TRANSPORT_TYPE,
  TYRE_CHANGE_BASIS,
  TablePreferenceFactory,
  TenantDefaultFactory,
  TenantFactory,
  TerritoryFactory,
  TrackingResultFactory,
  UPLOADABLE_STORAGE_FOLDERS,
  USER_STATUS,
  UserFactory,
  UserWithResolvedScopesFactory,
  VEHICLE_ACQUISITION_TYPE,
  VEHICLE_ALLOCATION_STATUS,
  VEHICLE_ALLOCATION_TYPE,
  VEHICLE_AVAILABLE_OPTIONS,
  VEHICLE_DISPOSAL_MAX_DOCUMENTS,
  VEHICLE_DISPOSAL_MAX_PHOTOS,
  VEHICLE_DOCUMENT_ACCEPTED_EXTENSIONS,
  VEHICLE_DOCUMENT_ACCEPTED_MIME_TYPES,
  VEHICLE_DOCUMENT_MAX_FILES,
  VEHICLE_DOCUMENT_MAX_FILE_SIZE,
  VEHICLE_END_OF_LEASE_OPTION,
  VEHICLE_ENERGY_LEVEL,
  VEHICLE_EXPENSE_MAX_DOCUMENTS,
  VEHICLE_GROUP_STATUS,
  VEHICLE_HISTORY_TYPE,
  VEHICLE_IMAGE_ACCEPTED_EXTENSIONS,
  VEHICLE_IMAGE_ACCEPTED_MIME_TYPES,
  VEHICLE_IMAGE_MAX_FILES,
  VEHICLE_IMAGE_MAX_FILE_SIZE,
  VEHICLE_MAKES,
  VEHICLE_MAKE_IDS,
  VEHICLE_MODELS,
  VEHICLE_OPTION_CATEGORY,
  VEHICLE_OPTION_IDS,
  VEHICLE_OWNERSHIP_CODE_LEASED,
  VEHICLE_OWNERSHIP_STATUS,
  VEHICLE_RETIREMENT_REASON,
  VEHICLE_SECTION,
  VEHICLE_SECTIONS,
  VEHICLE_STATUS,
  VEHICLE_TRANSMISSION,
  VEHICLE_TYPES,
  VEHICLE_TYPE_CATEGORY,
  VEHICLE_TYPE_IDS,
  VOLUME_UNITS,
  VehicleAgreementSummaryFactory,
  VehicleAllocationDetailFactory,
  VehicleAllocationFactory,
  VehicleExpenseFactory,
  VehicleExpenseListItemFactory,
  VehicleFactory,
  VehicleFleetStatsFactory,
  VehicleGroupFactory,
  VehicleHistoryEntryFactory,
  VehicleInsuranceFactory,
  VehicleListItemFactory,
  VehicleMakeFactory,
  VehicleModelFactory,
  VehicleOwnershipFactory,
  VehicleTypeFactory,
  WEIGHT_UNITS,
  WORK_ORDER_DAMAGE_EDITABLE_STATUSES,
  WORK_ORDER_DAMAGE_FIXED_STATUSES,
  WORK_ORDER_DAMAGE_MAX,
  WORK_ORDER_DAMAGE_NOTE_MAX,
  WORK_ORDER_DAMAGE_PHOTO_ACCEPTED_EXTENSIONS,
  WORK_ORDER_DAMAGE_PHOTO_ACCEPTED_MIME_TYPES,
  WORK_ORDER_DAMAGE_PHOTO_MAX_FILES,
  WORK_ORDER_DAMAGE_PHOTO_MAX_FILE_SIZE,
  WORK_ORDER_DOCUMENT_ACCEPTED_EXTENSIONS,
  WORK_ORDER_DOCUMENT_ACCEPTED_MIME_TYPES,
  WORK_ORDER_DOCUMENT_MAX_FILES,
  WORK_ORDER_DOCUMENT_MAX_FILE_SIZE,
  WORK_ORDER_ITEM_REASON,
  WORK_ORDER_ITEM_SOURCE,
  WORK_ORDER_NOT_SCHEDULED_REASON,
  WORK_ORDER_OPEN_STATUSES,
  WORK_ORDER_REASON_BY_ISSUE_TYPE,
  WORK_ORDER_SOURCE,
  WORK_ORDER_STATUS,
  WORK_ORDER_TEXT_MAX,
  WORK_ORDER_TIMELINE_EVENT,
  WORK_ORDER_TYPE,
  WORK_ORDER_TYPE_FILTER,
  addDays,
  addMonthsUtc,
  addUtcMonths,
  agreementAnnexFileName,
  agreementCurrentTerms,
  agreementSignedMonthlyNet,
  agreementTermMonths,
  anyApplicability,
  anyInspectionAppliesTo,
  applicabilitiesIntersect,
  applicabilityDependencyIssues,
  asDocumentReviewPayload,
  baseDocumentFactory,
  billedInvoiceOfSourceRef,
  buildAgreementDrafts,
  buildCohortIndex,
  buildContractExtraFees,
  buildInterval,
  buildPaginatedResponse,
  buildVehicleAgreementSummary,
  canArchiveContract,
  canBeAddendumMaster,
  canDeleteContract,
  canDuplicateContract,
  canEditContract,
  canOpenWorkOrder,
  canRegenerateContractDocument,
  canRestoreContract,
  canSendForSignature,
  canUploadSignedCopy,
  changesPlanWinner,
  classifyPurchaseOrder,
  classifyQueueUrgency,
  cohortRate,
  compareSpecificity,
  completedInspection,
  computeContractEndDate,
  computeContractTotals,
  computeContractValue,
  computeDueFrom,
  computeDuePoint,
  computeExtraFeeAmount,
  computeFeeRebillAmount,
  computeNextDueDate,
  computeReliefCharge,
  computeVehicleCoverage,
  contractDailyRate,
  contractExtensionFileName,
  contractFactory,
  contractLinesHaveExtraKmPrice,
  contractListItemFactory,
  customerFactory,
  damageElementsForArea,
  deriveUrgency,
  deriveWorkOrderTimeline,
  diffServicePlanUpdate,
  documentCodeFor,
  documentTemplateFactory,
  driverFactory,
  driverListItemFactory,
  earliestReliefChargeStart,
  emailTemplateFactory,
  emittedEmailFactory,
  energyPriceByTerritoryFactory,
  energySourceFactory,
  expenseCategoryFactory,
  extendedContractEnd,
  extensionMonthsNeeded,
  extraFeeAmountMax,
  fallsDueOn,
  feeRebillCountsTowardDue,
  feeRebillRemaining,
  feeRebillSourceRef,
  firebaseTokenFactory,
  formatAddendumNumber,
  formatAgreementNumber,
  formatContractNumber,
  formatExtensionNumber,
  formatInvoiceNumber,
  formatIssueNumber,
  getRequiredScopeLevels,
  getRoleRequiredModule,
  graceKmToDays,
  graceWindowDays,
  handoverDocumentFileName,
  inProgressInspection,
  inspectionRecordFactory,
  inspectionTemplateFactory,
  inspectionTemplateRecordFactory,
  inspectionTemplateVersionRecordFactory,
  insurancePackageFactory,
  invitationFactory,
  invoiceCounterKey,
  invoiceOverdue,
  isAddendumInForce,
  isAgreementLive,
  isAgreementOverdue,
  isBuiltInRoleKey,
  isContractSigned,
  isDamageElement,
  isDamageElementInArea,
  isDecimalString,
  isDueBy,
  isInspectionFleetVehicle,
  isIssuePastTarget,
  isIssueResolvedWithinTarget,
  isLeasingContract,
  isPurchaseOrderExpiringSoon,
  isPurchaseOrderPeriodOrdered,
  isPurchaseOrderValidOn,
  isReliefOpen,
  isSelectableReliefChargeType,
  isServicePlanInEffect,
  isWorkOrderOpen,
  issueAgeHours,
  issueDetailFactory,
  issueFactory,
  issueListItemFactory,
  issueStatsFactory,
  issueTimelineEntryFactory,
  issueTimelineEntryViewFactory,
  leaseEndDate,
  leaseTermMonths,
  leasingSettingsFactory,
  liveOverrunDays,
  locationFactory,
  magicLinkFactory,
  magicLinkRequestViewFactory,
  maintenanceSettingsFactory,
  masterNumberOfAddendum,
  maxSnoozeDate,
  minSnoozeDate,
  missingIntervalThresholds,
  moduleAreaForPath,
  nextLeasingStatus,
  nextMileageMark,
  nextReliefActions,
  normalizeInvoiceNumber,
  orderFactory,
  ownDailyRate,
  parseDocumentNumber,
  parseFeeRebillSourceRef,
  parseInvoiceNumber,
  parseIssueNumber,
  pickDefaultOption,
  planReminderChanges,
  poPeriodsOverlap,
  poolTypeFactory,
  previewRequiresConfirmation,
  purchaseOrderCardMode,
  purchaseOrderWriteGate,
  queueComparedDate,
  readingAgeLevel,
  readingSpanDays,
  recomputeContractExtraFees,
  reliefDays,
  reliefFactory,
  reliefInvoiceStatus,
  reliefViewFactory,
  resolveApplicablePurchaseOrder,
  resolveBaseline,
  resolveBusinessTimeZone,
  resolveDailyRate,
  resolveLeasingStatus,
  resolveMagicLinkRecipient,
  resolveMagicLinkState,
  resolveServicePlanAssignment,
  resolveVehicleStatusAtOpening,
  restartBaselineFromDuePoint,
  restoreTargetStatus,
  roleFactory,
  roundToCents,
  roundToMinorUnit,
  sameApplicability,
  sameInstant,
  sameIntervals,
  serviceOperationFactory,
  servicePackageFactory,
  servicePlanFactory,
  servicePlanStatus,
  servicePlanYearIssues,
  serviceReminderFactory,
  spansOwnRate,
  supplierFactory,
  tablePreferenceFactory,
  tenantDefaultFactory,
  tenantFactory,
  territoryFactory,
  toCalendarDay,
  toOdometerReading,
  toVehicleAgreementSummary,
  trackingResultFactory,
  triggerThresholds,
  userFactory,
  userWithResolvedScopesFactory,
  vatPerVehicle,
  vehicleAgreementSummaryFactory,
  vehicleAllocationDetailFactory,
  vehicleAllocationFactory,
  vehicleExpenseFactory,
  vehicleExpenseListItemFactory,
  vehicleFactory,
  vehicleFleetStatsFactory,
  vehicleGroupFactory,
  vehicleHistoryEntryFactory,
  vehicleInsuranceFactory,
  vehicleListItemFactory,
  vehicleMakeFactory,
  vehicleMatchesApplicability,
  vehicleModelFactory,
  vehicleOwnershipFactory,
  vehicleTypeFactory
};
//# sourceMappingURL=index.mjs.map